import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Select } from './ui';
import type { Property, Comp, DealAnalysis, InvestorProfile, FieldProvenance, AnalysisCheck } from '../types';
import { generateInvestmentAnalysisPDF } from '../utils/pdfExport';
import { describeOutcome } from '../utils/exportFiling';
import { loadLogoImage } from '../utils/pdfBranding';
import { useAuth } from '../contexts/AuthContext';
import {
  calculateVacancyAmount,
  calculateEffectiveGrossIncome,
  calculateManagementFee,
  calculateTotalExpenses,
  calculateCapRate,
  calculatePricePerSqft,
  calculateLoanAmount,
  calculateMonthlyPayment,
  calculateAnnualDebtService,
  calculateDSCR,
  calculateClosingCosts,
  calculateTotalCashRequired,
  secondaryNoteAnnualDebtService,
  type SecondaryNote,
  calculateBeforeTaxCashFlow,
  calculateCashOnCash,
  calculateSaleProceeds,
  buildExitCapLadder,
  calculateIRR,
  calculateEquityMultiple,
  calculateAvgCashOnCash,
  calculateNPV,
  buildIRRCashFlows,
  generateProjections,
  parseNumberInput,
  formatCurrency,
  formatPercent,
  formatRatio,
  suggestExitCapRate,
  benchmarkPricePerSqft,
  solveMaxPurchasePrice,
  solveRequiredNOILift,
  solveRequiredRentPSF,
  solveCapexCeiling,
  solveTargetExitCap,
  resolveExpenseRecoveries,
  calculateAdjustedDownPayment,
  computeScenario,
  classifyInvestmentStrategy,
  sizeLoanFromEquity,
  maxSupportableLoan,
  calculateDebtYield,
  buildScenarioSet,
  buildUnleveredCashFlows,
} from '../utils/financialCalculations';
import type { InvestmentStrategy, ScenarioInputs, ScenarioDeltas } from '../utils/financialCalculations';
import {
  getDefaultPropertyTaxes,
  getDefaultInsurance,
  getDefaultUtilities,
} from '../utils/expenseDefaults';
import {
  ARCHETYPE_PRESETS,
  STRUCTURE_SUPPRESSED_METRICS,
  resolveActiveThresholds,
  resolveThresholdLabel,
} from '../utils/archetypePresets';
import { AnalysisCheckPanel } from './AnalysisCheckPanel';
import DealAdvisorChat from './analysis/DealAdvisorChat';
import DealBriefStrip from './analysis/DealBriefStrip';
import type { BriefAxes } from './analysis/DealBriefStrip';
import VerdictCard from './analysis/VerdictCard';
import DecisionScorecard from './analysis/DecisionScorecard';
import BenchmarkPanel from './analysis/BenchmarkPanel';
import { singleTenantOf } from '../utils/singleTenant';
import LoanSizingCard from './analysis/LoanSizingCard';
import { scoreDeal, isAmberVerdict } from './analysis/scoreDeal';
import { runScenario } from '../utils/scenarioLevers';
import { resolveCloseDate, isDefaultedCloseDate, DEFAULT_CLOSE_DAYS, toIsoDate } from '../utils/closeDate';
import { yearOneNoi, inPlaceNoi, noiProjectionInputs, capBasisCaption, exitNoi as resolveExitNoi, exitNoiYearLabel, finalHoldYearNoi, noiScheduleFromRent } from '../utils/scheduleAnchors';
import { reconcileInPlaceIncome } from '../utils/inPlaceIncome';
import { landlordExpenseBasis } from '../utils/landlordExpenseBasis';
import { analysisPreflight } from '../utils/analysisPreflight';
import { PROP_13_ANNUAL_CAP_PERCENT } from '../utils/expenseGrowth';
import { resolveFinancingFee } from '../utils/financingFee';
import { rebaseToInPlace } from '../utils/noiAsOf';
import { applyPreset, sourceOf, markExtractionSources } from './analysis/applyPreset';
import { expenseConsistencyWarning } from '../utils/expenseConsistency';
import { managementFeeRate } from '../utils/managementFee';
import QuickScreen from './analysis/QuickScreen';
import { seedFromStatedNoi, type StatedNoiSeed } from '../utils/statedNoiSeed';
import { buildExitCapEvidence, linkedCompsToPoolRows, type PoolExitCap } from '../utils/compEvidence';
import { buildRentSchedule, annualRentRateAsOf, leaseYearSchedule, type ScheduleTenant } from '../utils/leaseScheduleEngine';
import { diffVersions, type VersionSnapshot } from '../utils/versionDiff';
import { matchDealToProfile } from '../utils/buyBoxMatch';
import { api } from '../lib/api';

type TabType = 'summary' | 'strategy' | 'valueadd' | 'proforma' | 'exit' | 'decision' | 'optimization' | 'advisor';

interface InvestmentAnalysisProps {
  property: Property;
  comps?: Comp[];
  /** Market fallback for the exit cap when linked comps carry no cap rate. */
  poolExitCap?: PoolExitCap | null;
  initialData?: Partial<DealAnalysis> | null;
  onSave?: (data: Partial<DealAnalysis>) => void;
  saving?: boolean;
  photoUrl?: string | null;
}

// Value-add checkbox items
const VA_ITEMS = [
  { key: 'va_below_market_rents' as const, label: 'Below-Market Rents', noteKey: 'va_below_market_rents_note' as const },
  { key: 'va_vacancy_leaseup' as const, label: 'Vacancy / Lease-Up', noteKey: 'va_vacancy_leaseup_note' as const },
  { key: 'va_expense_reduction' as const, label: 'Expense Reduction', noteKey: 'va_expense_reduction_note' as const },
  { key: 'va_re_tenanting' as const, label: 'Re-Tenanting', noteKey: 'va_re_tenanting_note' as const },
  { key: 'va_physical_improvements' as const, label: 'Physical Improvements', noteKey: 'va_physical_improvements_note' as const },
];

/**
 * Which of the four analysis-checker figures (`effective_gross_income`, `total_operating_expenses`,
 * `net_operating_income`, `cap_rate`) `handleSave` may persist, and their validated values.
 *
 * These four are computed at render time and were never saved - which is why the analysis checker
 * built to catch a preset zeroing an OM's expenses found almost nothing: 0 of 33 saved analyses
 * carried any of them. This is what finally sends them, but only the ones that pass the backend's
 * own check (`backend/src/middleware/validate.ts:559-575` - `effective_gross_income` and
 * `net_operating_income` optional numbers, `total_operating_expenses` nonnegative,
 * `cap_rate` 0-100).
 *
 * ⛔ THE TRAP: that backend validator rejects the WHOLE autosave request if any ONE field fails
 * its own check - so an unguarded save does not just fail to persist a bad cap rate, it silently
 * drops every other field in that same save, including the ones saving correctly today. `capRate`
 * is a division by purchase price: a zero or missing price yields Infinity/NaN, and a real NOI
 * against a tiny price yields a rate far above the schema's 0-100 ceiling.
 *
 * So each figure is OMITTED individually when it would not validate - never sent as null, and
 * never clamped, because clamping would write a fabricated number, which is exactly the defect
 * class this checker exists to catch. Do NOT "simplify" this back into an unconditional spread of
 * all four - and it is exported so this rule is something a test can call directly, rather than
 * only read as text.
 */
export function persistableAnalysisFigures(figures: {
  egi: number;
  totalExpenses: number;
  noi: number;
  capRate: number;
}): Partial<Pick<DealAnalysis, 'effective_gross_income' | 'total_operating_expenses' | 'net_operating_income' | 'cap_rate'>> {
  const { egi, totalExpenses, noi, capRate } = figures;
  return {
    ...(Number.isFinite(egi) ? { effective_gross_income: egi } : {}),
    ...(Number.isFinite(totalExpenses) && totalExpenses >= 0 ? { total_operating_expenses: totalExpenses } : {}),
    ...(Number.isFinite(noi) ? { net_operating_income: noi } : {}),
    ...(Number.isFinite(capRate) && capRate >= 0 && capRate <= 100 ? { cap_rate: capRate } : {}),
  };
}

export default function InvestmentAnalysis({
  property,
  comps = [],
  poolExitCap = null,
  initialData,
  onSave,
  saving = false,
  photoUrl,
}: InvestmentAnalysisProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const [expanded, setExpanded] = useState(true);
  const [sensitivityExpanded, setSensitivityExpanded] = useState(false);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  /** Where the last export went - the Deal Room, the property, or why it was not filed. */
  const [exportNote, setExportNote] = useState<string | null>(null);
  /**
   * T6 - the analysis checker's report, shown before the PDF actually generates. Its own
   * `useState`, deliberately NOT wired through `markUserEdit` or `setData`: this is a read-only
   * report on the export path, and the whole feature's safety argument is that opening it never
   * writes the analysis row it exists to audit.
   */
  const [analysisCheck, setAnalysisCheck] = useState<AnalysisCheck | null>(null);
  /**
   * The export call the panel's "Continue to PDF" runs, captured at click time so the check's own
   * render pass doesn't have to re-derive the export options. Takes an OPTIONAL preamble
   * (CRITICAL 2, final whole-branch review): when the analysis check itself failed, the catch
   * block below calls this WITH one, so the one fact that mattered - that no check happened -
   * survives into `exportNote` alongside where the PDF actually went, rather than disappearing
   * the moment the export completes.
   */
  const pendingExportRef = useRef<((checkFailureNote?: string) => void) | null>(null);
  /**
   * Review finding 2: `api.checkAnalysis` reads a PDF with a language model and takes seconds.
   * Without an in-flight signal the Export PDF button looked frozen, and a second click before
   * the first call resolved fired a second check - a second model call, billed. The REF is the
   * actual guard: it is read SYNCHRONOUSLY at the very top of the click handler, before any
   * `await`, because a `useState` update is not guaranteed to have committed before a fast
   * second click. `exportChecking` only drives the button's disabled/spinner look.
   */
  const exportCheckInFlightRef = useRef(false);
  const [exportChecking, setExportChecking] = useState(false);
  const [aiInsights, setAiInsights] = useState<Array<{ content: string; created_at: string }>>([]);
  const [keyConsiderations, setKeyConsiderations] = useState<string | null>(null);
  const [keyConsiderationsLoading, setKeyConsiderationsLoading] = useState(false);
  const [keyConsiderationsError, setKeyConsiderationsError] = useState<string | null>(null);
  type HouseStyleReview = { advocacy: string[]; attribution: string[] };
  const [keyConsiderationsReview, setKeyConsiderationsReview] = useState<HouseStyleReview | null>(null);

  // ── Scenario from plain language ───────────────────────────────────────
  // Joseph types the risk; the model picks LEVERS; the engine computes the numbers. The
  // assumptions are PRINTED next to the result, because an assumption you can see is one you
  // can correct and an invisible one just quietly changes the answer.
  type ProposedScenario = {
    title: string;
    assumptions: Array<{ label: string; lever: string; value: number; year?: number }>;
    rationale: string;
    notEstablished: string[];
    advocacy: string[];
    attribution: string[];
  };
  const [riskText, setRiskText] = useState('');
  const [scenario, setScenario] = useState<ProposedScenario | null>(null);
  const [scenarioFacts, setScenarioFacts] = useState<Array<{ label: string; value: string | null; source: string }>>([]);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [scenarioError, setScenarioError] = useState<string | null>(null);



  // Pre-load white logo for PDF export

  // Load starred AI insights for PDF export
  useEffect(() => {
    if (property.id) {
      api.dealAdvisorStarredMessages(property.id)
        .then(res => setAiInsights(res.messages || []))
        .catch(() => {});
    }
  }, [property.id]);

  // Pre-load exterior photo for PDF export
  useEffect(() => {
    if (photoUrl) {
      loadLogoImage(photoUrl).then(setPhotoBase64);
    }
  }, [photoUrl]);

  // ─── Three-Axis Deal Description ───────────────────────────────
  // `fieldSources` records where each assumption came from so a preset can
  // never silently overwrite a number the user typed (see applyPreset).
  const [profiles, setProfiles] = useState<InvestorProfile[]>([]);
  // A value is either the Phase 1 bare source string or a Phase 2 provenance
  // object; read it with sourceOf rather than comparing the value directly.
  const [fieldSources, setFieldSources] = useState<Record<string, string | FieldProvenance>>(
    initialData?.field_sources ?? {}
  );

  useEffect(() => {
    api.investorProfiles().then(r => setProfiles(r.profiles)).catch(() => {});
  }, []);

  // ─── Form State ────────────────────────────────────────────────
  const [data, setData] = useState<Partial<DealAnalysis>>(() => ({
    // Income
    /**
     * ⭐⭐ F1, Joseph's ruling 2026-08-27: USE THE RENT ROLL. The OM's headline and its own rent
     * roll disagreed on 2 of 5 deals measured, in OPPOSITE directions - Guitar Center's stated
     * income was a 2/1/2028 step, Banc of California's had expired 27 days earlier. The rent roll
     * is the contractual document; the headline is marketing.
     *
     * ⛔ ONLY ON A NEW ANALYSIS. A saved analysis keeps exactly what was saved - silently
     * re-basing 25 delivered analyses is the same class of harm as editing STRATEGY_THRESHOLDS.
     * Where a saved figure disagrees the screen FLAGS it and offers a one-click adoption.
     */
    potential_gross_income: initialData?.potential_gross_income
      ?? annualRentRateAsOf({
           tenants: ((property.additional_data as Record<string, any> | undefined)?.tenants ?? []) as ScheduleTenant[],
           asOf: toIsoDate(new Date()),
           // The rent roll speaks for TODAY, so an inferred series must reproduce it today.
           baseRentAsOf: toIsoDate(new Date()),
         }).rate
      ?? property.gross_income ?? 0,
    vacancy_rate: initialData?.vacancy_rate ?? 5,
    other_income: initialData?.other_income ?? 0,
    // Lease Structure
    lease_type: initialData?.lease_type ?? 'gross',
    nnn_reimbursement_rate: initialData?.nnn_reimbursement_rate ?? 100,
    modified_gross_reimbursement: initialData?.modified_gross_reimbursement ?? 0,
    // Expenses
    property_taxes: initialData?.property_taxes ?? 0,
    insurance: initialData?.insurance ?? 0,
    utilities: initialData?.utilities ?? 0,
    management_fee_percent: initialData?.management_fee_percent ?? 3,
    repairs_maintenance: initialData?.repairs_maintenance ?? 0,
    reserves_capex: initialData?.reserves_capex ?? 0,
    other_expenses: initialData?.other_expenses ?? 0,
    // Smart expense defaults tracking
    auto_property_taxes: initialData?.auto_property_taxes ?? false,
    auto_insurance: initialData?.auto_insurance ?? false,
    auto_utilities: initialData?.auto_utilities ?? false,
    // Financing
    purchase_price: initialData?.purchase_price ?? property.price ?? 0,
    ltv_percent: initialData?.ltv_percent ?? 70,
    interest_rate: initialData?.interest_rate ?? 7,
    amortization_years: initialData?.amortization_years ?? 25,
    closing_costs_percent: initialData?.closing_costs_percent ?? 2,
    // Seller Carryback
    seller_carryback_amount: initialData?.seller_carryback_amount ?? 0,
    seller_carryback_rate: initialData?.seller_carryback_rate ?? 0,
    seller_carryback_term: initialData?.seller_carryback_term ?? 5,
    // ⭐ FALSE is the existing behaviour; an interest-only note is the deliberate choice.
    seller_carryback_interest_only: initialData?.seller_carryback_interest_only ?? false,
    /**
     * ⭐⭐⭐ THE FINANCING FEE. ⛔ NULL, not 0, on the amount and the rate: a blank field and an
     * underwritten zero are different answers, and `n()` cannot tell them apart.
     * `financing_fee_financed` defaults FALSE because paid-in-cash is the mechanic that raises
     * the buyer's cash - the conservative reading - while financing it flatters nothing.
     */
    financing_fee_amount: initialData?.financing_fee_amount ?? null,
    financing_fee_financed: initialData?.financing_fee_financed ?? false,
    financing_fee_note: initialData?.financing_fee_note ?? 'primary',
    loan_servicing_fee_rate: initialData?.loan_servicing_fee_rate ?? null,
    // Strategy
    investment_strategy: initialData?.investment_strategy ?? classifyInvestmentStrategy({
      occupancyRate: property.occupancy_rate ?? undefined,
      propertyType: property.property_type ?? undefined,
      yearBuilt: property.year_built ?? undefined,
      tenantCredit: (property.additional_data as Record<string, any>)?.tenants?.[0]?.tenant_credit ?? undefined,
    }),
    // Value-Add Attribution
    va_below_market_rents: initialData?.va_below_market_rents ?? false,
    va_below_market_rents_note: initialData?.va_below_market_rents_note ?? '',
    va_vacancy_leaseup: initialData?.va_vacancy_leaseup ?? false,
    va_vacancy_leaseup_note: initialData?.va_vacancy_leaseup_note ?? '',
    lease_up_months: initialData?.lease_up_months ?? 0,
    // ⛔ NULL, not 2. A default here would silently re-price every saved analysis.
    property_tax_growth_rate: initialData?.property_tax_growth_rate ?? null,
    other_income_growth_rate: initialData?.other_income_growth_rate ?? null,
    va_expense_reduction: initialData?.va_expense_reduction ?? false,
    va_expense_reduction_note: initialData?.va_expense_reduction_note ?? '',
    va_re_tenanting: initialData?.va_re_tenanting ?? false,
    va_re_tenanting_note: initialData?.va_re_tenanting_note ?? '',
    va_physical_improvements: initialData?.va_physical_improvements ?? false,
    va_physical_improvements_note: initialData?.va_physical_improvements_note ?? '',
    // As-Is vs Stabilized
    as_is_rent_psf: initialData?.as_is_rent_psf ?? 0,
    stabilized_rent_psf: initialData?.stabilized_rent_psf ?? 0,
    as_is_occupancy: initialData?.as_is_occupancy ?? (property.occupancy_rate ?? 0),
    stabilized_occupancy: initialData?.stabilized_occupancy ?? 95,
    as_is_other_income: initialData?.as_is_other_income ?? 0,
    stabilized_other_income: initialData?.stabilized_other_income ?? 0,
    as_is_expense_ratio: initialData?.as_is_expense_ratio ?? 0,
    stabilized_expense_ratio: initialData?.stabilized_expense_ratio ?? 0,
    // Value-Add Costs
    va_capex: initialData?.va_capex ?? 0,
    va_ti_leasing: initialData?.va_ti_leasing ?? 0,
    va_carry_costs: initialData?.va_carry_costs ?? 0,
    va_contingency: initialData?.va_contingency ?? 0,
    va_total_cost: initialData?.va_total_cost ?? 0,
    // Proforma Settings
    income_growth_rate: initialData?.income_growth_rate ?? 3,
    expense_growth_rate: initialData?.expense_growth_rate ?? 2.5,
    holding_period: initialData?.holding_period ?? 5,
    // Exit — default to going-in cap + buffer (age-dependent) or comp-based suggestion
    // Never assume cap compression on exit (especially for older assets)
    // ⛔ NULL, not 0. A stated exit value OUTRANKS the cap rate, so a defaulted 0 would have to
    // be distinguished from a real one at every consumer. `resolveExitSalePrice` treats absent,
    // null, zero and non-finite alike as 'price it as a yield'.
    exit_value: initialData?.exit_value ?? null,
    expenses_are_carry: initialData?.expenses_are_carry ?? false,
    exit_cap_rate: initialData?.exit_cap_rate ?? (suggestExitCapRate(comps) ?? (() => {
      const estNOI = property.noi ?? (property.gross_income && property.operating_expenses
        ? property.gross_income - property.operating_expenses : 0);
      const estPrice = property.price ?? 0;
      const goingInCap = estPrice > 0 && estNOI > 0 ? (estNOI / estPrice) * 100 : 0;
      // Buffer scales with building age: newer=50bps, 20-40yr=75bps, 40yr+=100bps
      const age = property.year_built ? new Date().getFullYear() - property.year_built : 30;
      const buffer = age >= 40 ? 1.0 : age >= 20 ? 0.75 : 0.5;
      return parseFloat((Math.max(goingInCap, 6) + buffer).toFixed(2));
    })()),
    selling_costs_percent: initialData?.selling_costs_percent ?? 2,
    // Three-axis deal description + lender covenants
    archetype: initialData?.archetype ?? null,
    capital_structure: initialData?.capital_structure ?? null,
    investor_profile_id: initialData?.investor_profile_id ?? null,
    down_payment_target: initialData?.down_payment_target ?? null,
    sizing_mode: initialData?.sizing_mode ?? null,
    max_ltv_percent: initialData?.max_ltv_percent ?? null,
    min_dscr_covenant: initialData?.min_dscr_covenant ?? null,
    min_debt_yield: initialData?.min_debt_yield ?? null,
    is_1031: initialData?.is_1031 ?? false,
    /**
     * ⚠️ SEEDED AND HYDRATED, BOTH. It shipped in NEITHER list, so a saved close date wrote to
     * the database and never came back - and the panel then stated the 90-day default as fact
     * over a date the user had already chosen. `analysisFieldRoundTrip.test.ts` guards it.
     */
    close_date: initialData?.close_date ?? null,
    noi_overrides: initialData?.noi_overrides ?? null,
    rollover_assumptions: initialData?.rollover_assumptions ?? null,
    scenario_overrides: initialData?.scenario_overrides ?? null,
    // Notes
    notes: initialData?.notes ?? '',
  }));

  // Only sync initialData on first load (not after save responses, which would cause a loop)
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    if (initialData && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      setData(prev => ({
        ...prev,
        potential_gross_income: initialData.potential_gross_income ?? prev.potential_gross_income,
        vacancy_rate: initialData.vacancy_rate ?? prev.vacancy_rate,
        other_income: initialData.other_income ?? prev.other_income,
        lease_type: initialData.lease_type ?? prev.lease_type,
        nnn_reimbursement_rate: initialData.nnn_reimbursement_rate ?? prev.nnn_reimbursement_rate,
        modified_gross_reimbursement: initialData.modified_gross_reimbursement ?? prev.modified_gross_reimbursement,
        property_taxes: initialData.property_taxes ?? prev.property_taxes,
        insurance: initialData.insurance ?? prev.insurance,
        utilities: initialData.utilities ?? prev.utilities,
        management_fee_percent: initialData.management_fee_percent ?? prev.management_fee_percent,
        repairs_maintenance: initialData.repairs_maintenance ?? prev.repairs_maintenance,
        reserves_capex: initialData.reserves_capex ?? prev.reserves_capex,
        other_expenses: initialData.other_expenses ?? prev.other_expenses,
        auto_property_taxes: initialData.auto_property_taxes ?? prev.auto_property_taxes,
        auto_insurance: initialData.auto_insurance ?? prev.auto_insurance,
        auto_utilities: initialData.auto_utilities ?? prev.auto_utilities,
        purchase_price: initialData.purchase_price ?? prev.purchase_price,
        ltv_percent: initialData.ltv_percent ?? prev.ltv_percent,
        interest_rate: initialData.interest_rate ?? prev.interest_rate,
        amortization_years: initialData.amortization_years ?? prev.amortization_years,
        closing_costs_percent: initialData.closing_costs_percent ?? prev.closing_costs_percent,
        seller_carryback_amount: initialData.seller_carryback_amount ?? prev.seller_carryback_amount,
        seller_carryback_rate: initialData.seller_carryback_rate ?? prev.seller_carryback_rate,
        seller_carryback_term: initialData.seller_carryback_term ?? prev.seller_carryback_term,
        seller_carryback_interest_only: initialData.seller_carryback_interest_only ?? prev.seller_carryback_interest_only,
        // ⛔ A field the screen can EDIT and cannot LOAD is the `close_date` defect exactly, and
        // a financing fee that vanishes on reopen would silently flatter the deal by its own size.
        financing_fee_amount: initialData.financing_fee_amount ?? prev.financing_fee_amount,
        financing_fee_financed: initialData.financing_fee_financed ?? prev.financing_fee_financed,
        financing_fee_note: initialData.financing_fee_note ?? prev.financing_fee_note,
        loan_servicing_fee_rate: initialData.loan_servicing_fee_rate ?? prev.loan_servicing_fee_rate,
        investment_strategy: initialData.investment_strategy ?? prev.investment_strategy,
        va_below_market_rents: initialData.va_below_market_rents ?? prev.va_below_market_rents,
        va_below_market_rents_note: initialData.va_below_market_rents_note ?? prev.va_below_market_rents_note,
        va_vacancy_leaseup: initialData.va_vacancy_leaseup ?? prev.va_vacancy_leaseup,
        va_vacancy_leaseup_note: initialData.va_vacancy_leaseup_note ?? prev.va_vacancy_leaseup_note,
        // ⛔ A field the screen can EDIT and cannot LOAD is the `close_date` defect exactly.
        lease_up_months: initialData.lease_up_months ?? prev.lease_up_months,
        property_tax_growth_rate: initialData.property_tax_growth_rate ?? prev.property_tax_growth_rate,
        other_income_growth_rate: initialData.other_income_growth_rate ?? prev.other_income_growth_rate,
        va_expense_reduction: initialData.va_expense_reduction ?? prev.va_expense_reduction,
        va_expense_reduction_note: initialData.va_expense_reduction_note ?? prev.va_expense_reduction_note,
        va_re_tenanting: initialData.va_re_tenanting ?? prev.va_re_tenanting,
        va_re_tenanting_note: initialData.va_re_tenanting_note ?? prev.va_re_tenanting_note,
        va_physical_improvements: initialData.va_physical_improvements ?? prev.va_physical_improvements,
        va_physical_improvements_note: initialData.va_physical_improvements_note ?? prev.va_physical_improvements_note,
        as_is_rent_psf: initialData.as_is_rent_psf ?? prev.as_is_rent_psf,
        stabilized_rent_psf: initialData.stabilized_rent_psf ?? prev.stabilized_rent_psf,
        as_is_occupancy: initialData.as_is_occupancy ?? prev.as_is_occupancy,
        stabilized_occupancy: initialData.stabilized_occupancy ?? prev.stabilized_occupancy,
        as_is_other_income: initialData.as_is_other_income ?? prev.as_is_other_income,
        stabilized_other_income: initialData.stabilized_other_income ?? prev.stabilized_other_income,
        as_is_expense_ratio: initialData.as_is_expense_ratio ?? prev.as_is_expense_ratio,
        stabilized_expense_ratio: initialData.stabilized_expense_ratio ?? prev.stabilized_expense_ratio,
        va_capex: initialData.va_capex ?? prev.va_capex,
        va_ti_leasing: initialData.va_ti_leasing ?? prev.va_ti_leasing,
        va_carry_costs: initialData.va_carry_costs ?? prev.va_carry_costs,
        va_contingency: initialData.va_contingency ?? prev.va_contingency,
        va_total_cost: initialData.va_total_cost ?? prev.va_total_cost,
        income_growth_rate: initialData.income_growth_rate ?? prev.income_growth_rate,
        expense_growth_rate: initialData.expense_growth_rate ?? prev.expense_growth_rate,
        holding_period: initialData.holding_period ?? prev.holding_period,
        exit_cap_rate: initialData.exit_cap_rate ?? prev.exit_cap_rate,
        exit_value: initialData.exit_value ?? prev.exit_value,
        expenses_are_carry: initialData.expenses_are_carry ?? prev.expenses_are_carry,
        selling_costs_percent: initialData.selling_costs_percent ?? prev.selling_costs_percent,
        archetype: initialData.archetype ?? prev.archetype,
        capital_structure: initialData.capital_structure ?? prev.capital_structure,
        investor_profile_id: initialData.investor_profile_id ?? prev.investor_profile_id,
        down_payment_target: initialData.down_payment_target ?? prev.down_payment_target,
        sizing_mode: initialData.sizing_mode ?? prev.sizing_mode,
        max_ltv_percent: initialData.max_ltv_percent ?? prev.max_ltv_percent,
        min_dscr_covenant: initialData.min_dscr_covenant ?? prev.min_dscr_covenant,
        min_debt_yield: initialData.min_debt_yield ?? prev.min_debt_yield,
        is_1031: initialData.is_1031 ?? prev.is_1031,
        close_date: initialData.close_date ?? prev.close_date,
        noi_overrides: initialData.noi_overrides ?? prev.noi_overrides,
        rollover_assumptions: initialData.rollover_assumptions ?? prev.rollover_assumptions,
        scenario_overrides: initialData.scenario_overrides ?? prev.scenario_overrides,
        notes: initialData.notes ?? prev.notes,
      }));
    }
  }, [initialData]);

  // ─── Smart Expense Defaults ──────────────────────────────────
  const smartDefaultsApplied = useRef(false);
  useEffect(() => {
    // Only auto-fill on first mount when there is no saved analysis data and expense fields are 0
    if (initialData || smartDefaultsApplied.current) return;
    smartDefaultsApplied.current = true;

    const pp = property.price ?? 0;
    const sf = property.building_size ?? 0;
    const pt = property.property_type ?? 'industrial';

    const updates: Partial<DealAnalysis> = {};

    if (pp > 0) {
      const defaultTaxes = getDefaultPropertyTaxes(pp, property.city);
      if (defaultTaxes > 0) {
        updates.property_taxes = defaultTaxes;
        updates.auto_property_taxes = true;
      }
    }
    if (sf > 0) {
      const defaultIns = getDefaultInsurance(sf, pt);
      if (defaultIns > 0) {
        updates.insurance = defaultIns;
        updates.auto_insurance = true;
      }
      const defaultUtil = getDefaultUtilities(sf, pt);
      if (defaultUtil > 0) {
        updates.utilities = defaultUtil;
        updates.auto_utilities = true;
      }
    }

    if (Object.keys(updates).length > 0) {
      setData(prev => ({ ...prev, ...updates }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-populate from enriched extraction data (pro forma, growth, reimbursements) ──
  // Runs on first load OR when there's no saved analysis yet. If a saved analysis exists,
  // we still apply enriched defaults for fields the saved analysis doesn't have values for.
  /** ⭐ What the stated-NOI seed did, so the SCREEN can say it. A silent seed is a guess. */
  const [statedNoiSeed, setStatedNoiSeed] = useState<StatedNoiSeed | null>(null);
  const enrichedDataApplied = useRef(false);
  useEffect(() => {
    if (enrichedDataApplied.current) return;
    enrichedDataApplied.current = true;

    const additional = property.additional_data as Record<string, any> | undefined;
    if (!additional) return;

    const hasSavedAnalysis = !!initialData;
    const updates: Partial<DealAnalysis> = {};

    // Helper: only set if no saved analysis or the saved value is 0/empty
    const setIfEmpty = (key: keyof DealAnalysis, value: any) => {
      if (!hasSavedAnalysis || !initialData?.[key]) {
        updates[key] = value;
      }
    };

    // Pro forma financials from OM extraction
    const pf = additional.pro_forma_financials;
    if (pf) {
      // PGI should be CURRENT in-place income, not pro forma — pro forma is the
      // stabilized target. Using pro forma as PGI inflates the cap rate (e.g. 6%
      // instead of the actual in-place ~3% for a 46% occupied property).
      // Pro forma income feeds the value-add section (stabilized rent PSF, stabilized occupancy).
      if (pf.occupancy_rate) {
        setIfEmpty('stabilized_occupancy', pf.occupancy_rate);
      }
      // Seed as-is values from current extraction
      if (property.occupancy_rate) {
        setIfEmpty('as_is_occupancy', property.occupancy_rate);
      }
    }

    // Growth assumptions from OM projections
    const ga = additional.growth_assumptions;
    if (ga) {
      if (ga.income_growth_rate) setIfEmpty('income_growth_rate', ga.income_growth_rate);
      if (ga.expense_growth_rate) setIfEmpty('expense_growth_rate', ga.expense_growth_rate);
      if (ga.analysis_period_years) setIfEmpty('holding_period', ga.analysis_period_years);
      if (ga.exit_cap_rate) setIfEmpty('exit_cap_rate', ga.exit_cap_rate);
    }

    // ── Detect NNN lease structure from tenant data ──
    const tenants = additional.tenants as Array<{
      tenant_name?: string; lease_type?: string; leased_sf?: number; tenant_credit?: string;
    }> | undefined;
    const occupiedTenants = tenants?.filter(t =>
      t.tenant_name && !t.tenant_name.toLowerCase().includes('vacant')
    ) ?? [];
    const nnnTenants = occupiedTenants.filter(t =>
      t.lease_type && t.lease_type.toLowerCase().includes('nnn')
    );
    const totalOccupiedSF = occupiedTenants.reduce((s, t) => s + (t.leased_sf ?? 0), 0);
    const nnnSF = nnnTenants.reduce((s, t) => s + (t.leased_sf ?? 0), 0);
    const isFullNNN = occupiedTenants.length > 0 && nnnSF >= totalOccupiedSF * 0.9;

    // ── Income & lease type handling ──
    // Priority order:
    // 1. income_includes_reimbursements = true → income is EGI, reimbursements are baked in,
    //    landlord HAS expenses (they're just recovered via NNN). Don't add reimbursements.
    // 2. isFullNNN with NO extracted expenses → true NNN, $0 landlord expenses
    // 3. expense_reimbursements > 0 → traditional OM, add reimbursements to PGI
    const incomeIncludesReimbursements = additional.income_includes_reimbursements === true
      || additional.income_includes_reimbursements === 'true';

    /**
     * ⭐⭐ FIELDS WRITTEN FROM THE DOCUMENT, so an archetype preset cannot clear them.
     *
     * 🚨 GENERALISED 2026-08-30 AFTER STAGECOACH, and the narrowness of the first version is the
     * lesson. Yesterday this protected exactly one field - `other_expenses` - because that was the
     * one Bakersfield exposed. Stagecoach then hit the identical defect on `lease_type` and
     * `nnn_reimbursement_rate`: its extraction says `income_includes_reimbursements: true`, the
     * seeding correctly set modified-gross with $21,493 of recoveries, and picking the
     * Multi-tenant NNN archetype overwrote it with `nnn` at 100% recovery - **inventing $119,756
     * of reimbursement income**, six times the document's figure, and taking the cap from 6.25%
     * to 7.74%.
     * ⭐ Fixing one field and not the class is how the same defect ships twice in two days.
     */
    const extractionSourced: string[] = [];

    const hasExtractedExpenses = (additional.expenses as Array<any> | undefined)?.length ?? 0 > 0;

    if (incomeIncludesReimbursements) {
      // Source data reports EGI — reimbursements are ALREADY in gross_income.
      // This takes priority over NNN detection because income_includes_reimbursements
      // means there ARE real expenses being recovered — not a zero-expense NNN.
      // FORCE these values to prevent double-counting from any stale saved data.
      updates.vacancy_rate = 0;
      updates.lease_type = 'modified_gross';
      updates.modified_gross_reimbursement = 0;
      // ⛔ The DOCUMENT says its income already carries recoveries. A preset must not re-decide it.
      extractionSourced.push('lease_type', 'modified_gross_reimbursement', 'nnn_reimbursement_rate', 'vacancy_rate');

      const occ = property.occupancy_rate ?? 100;
      const vacancyNote = `Note: Vacancy of ${100 - occ}% is already reflected in the Effective Gross Income figure from the source data.`;
      const existingNotes = updates.notes || data.notes || '';
      if (occ < 100 && !existingNotes.includes('already reflected in the Effective Gross Income')) {
        updates.notes = existingNotes + (existingNotes ? '\n' : '') + vacancyNote;
      }
    } else if (isFullNNN && !hasExtractedExpenses) {
      // Pure NNN property with NO landlord expenses in the extraction
      // (e.g. AutoZone + Dollar Tree where tenants pay everything directly)
      setIfEmpty('lease_type', 'nnn');
      setIfEmpty('nnn_reimbursement_rate', 100);
      extractionSourced.push('lease_type', 'nnn_reimbursement_rate');
      updates.property_taxes = 0;
      updates.insurance = 0;
      updates.utilities = 0;
      updates.repairs_maintenance = 0;
      updates.reserves_capex = 0;
      updates.other_expenses = 0;
      updates.management_fee_percent = 0;
      updates.auto_property_taxes = false;
      updates.auto_insurance = false;
      updates.auto_utilities = false;
      // Force reimbursement to 0 so it doesn't get set by the else branch
      updates.modified_gross_reimbursement = 0;

      // 100% occupied with national credit tenants = 0% vacancy
      const occ = property.occupancy_rate ?? 100;
      const hasNationalTenants = occupiedTenants.some(t => t.tenant_credit === 'national');
      if (occ >= 95 && hasNationalTenants) {
        setIfEmpty('vacancy_rate', 0);
      }

      // NNN income growth from pro forma CAGR
      const holdPeriod = data.holding_period ?? 5;
      const currentIncome = property.gross_income ?? 0;
      const pfIncome = pf?.gross_income ?? pf?.noi ?? 0;
      if (pfIncome > currentIncome && currentIncome > 0) {
        const effectiveCAGR = (Math.pow(pfIncome / currentIncome, 1 / holdPeriod) - 1) * 100;
        setIfEmpty('income_growth_rate', parseFloat(effectiveCAGR.toFixed(2)));
      } else {
        setIfEmpty('income_growth_rate', 0);
      }
      setIfEmpty('expense_growth_rate', 0);
    } else if (additional.expense_reimbursements && additional.expense_reimbursements > 0) {
      // Traditional OM: gross_income is PGI (base rent only), reimbursements are separate
      setIfEmpty('lease_type', 'modified_gross');
      setIfEmpty('modified_gross_reimbursement', additional.expense_reimbursements);
      // ⛔ The OM states the recovery figure - that is evidence, not a category default.
      extractionSourced.push('lease_type', 'modified_gross_reimbursement', 'nnn_reimbursement_rate');

      const currentOcc = property.occupancy_rate ?? 100;
      if (currentOcc < 85) {
        setIfEmpty('vacancy_rate', 0);
      }
    }

    // Seed expense fields from extracted expense breakdown (overrides smart defaults)
    // Only skip when we're in the pure NNN path (no extracted expenses, zeroed above)
    const extractedExpenses = additional.expenses as Array<{ category?: string; description?: string; annual_amount?: number }> | undefined;
    /**
     * ⭐⭐ "Pure NNN" is a statement about what the LANDLORD PAYS, not about the lease label.
     * 🚨 This used to read `!hasExtractedExpenses`, so an OM that itemises nothing was treated as
     * absolute-net even when the extractor had DERIVED a landlord cost from the document's own
     * figures. Bakersfield's $10,075 of property insurance vanished and the cap read 6.94%
     * against a delivered 6.75%. ▶ `landlordExpenseBasis`.
     */
    const expenseBasis = landlordExpenseBasis({
      isFullNNN, incomeIncludesReimbursements,
      lineItemCount: extractedExpenses?.length ?? 0,
      statedTotal: property.operating_expenses,
    });
    const pureNNNApplied = expenseBasis.pureNNN;
    /**
     * ⛔ A STATED TOTAL WITH NO LINE ITEMS STILL HAS TO REACH THE MODEL. The per-category block
     * below can only walk an array; this is the path for a document that prints one number.
     */
    if (expenseBasis.otherPlug !== null) {
      updates.other_expenses = expenseBasis.otherPlug;
      updates.utilities = 0; updates.auto_utilities = false;
      updates.auto_property_taxes = false; updates.auto_insurance = false;
      /**
       * ⛔ MARKED AS EVIDENCE, or the archetype preset zeroes it on the next click. Every
       * archetype carries `other_expenses: 0` in its defaults, and `applyPreset` only protected
       * hand-typed work - so a figure read out of the document was overwritten by a guess about
       * a category of deal. ▶ `PROTECTED_SOURCES`.
       */
      extractionSourced.push('other_expenses');
    }
    if (!pureNNNApplied && extractedExpenses && extractedExpenses.length > 0) {
      let seededTaxes = 0;
      let seededInsurance = 0;
      let seededUtilities = 0;
      let seededRepairs = 0;
      let seededReserves = 0;
      let seededManagement = 0;
      let seededOther = 0;
      let hasExtractedManagement = false;

      for (const exp of extractedExpenses) {
        const cat = (exp.category || '').toLowerCase();
        const desc = (exp.description || '').toLowerCase();
        const amt = exp.annual_amount || 0;

        if (cat.includes('tax')) {
          seededTaxes += amt;
        } else if (cat.includes('insurance')) {
          seededInsurance += amt;
        } else if (cat.includes('utilit') || cat.includes('electric') || cat.includes('water') || cat.includes('gas')) {
          seededUtilities += amt;
        } else if (cat.includes('maintenance') || cat.includes('repair') || cat.includes('janitorial') || cat.includes('landscap')) {
          seededRepairs += amt;
        } else if (cat.includes('management') || desc.includes('management')) {
          seededManagement += amt;
          hasExtractedManagement = true;
        } else if (cat.includes('reserve') || cat.includes('capex') || cat.includes('capital')) {
          seededReserves += amt;
        } else {
          seededOther += amt;
        }
      }

      // ⛔ Every line below is a figure the OM printed. A preset's zero is a default, not evidence.
      extractionSourced.push('property_taxes', 'insurance', 'utilities', 'repairs_maintenance', 'reserves_capex', 'other_expenses');
      if (seededTaxes > 0) { updates.property_taxes = seededTaxes; updates.auto_property_taxes = false; }
      if (seededInsurance > 0) { updates.insurance = seededInsurance; updates.auto_insurance = false; }
      if (seededUtilities > 0) { updates.utilities = seededUtilities; updates.auto_utilities = false; }
      else { updates.utilities = 0; updates.auto_utilities = false; }
      if (seededRepairs > 0) { updates.repairs_maintenance = seededRepairs; }
      if (seededReserves > 0) { updates.reserves_capex = seededReserves; }
      if (seededOther > 0) { updates.other_expenses = seededOther; }

      // If extraction included management fee, back-calculate the percentage
      // so it displays as a proper labeled line item (e.g. "Management Fee (3.5%)")
      // instead of being hidden inside "Other Expenses"
      //
      // 🚨 THE PRECISION IS THE POINT. This rounded to ONE decimal, and on 13368 Main Street the
      // OM's stated $8,157 became 2.1% x $383,024 = $8,044 - **$113 short on the one line a buyer
      // reconciles against the document.** ▶ `managementFeeRate`, which keeps enough decimals to
      // reproduce the stated dollars, while the LABEL still prints "2.13%".
      if (hasExtractedManagement && seededManagement > 0) {
        const egi = property.gross_income ?? 0;
        const mgmtPercent = managementFeeRate(seededManagement, egi);
        if (mgmtPercent !== null) {
          updates.management_fee_percent = mgmtPercent;
        } else {
          // Fallback: can't calculate percent, put in other
          updates.other_expenses = (updates.other_expenses ?? 0) + seededManagement;
          updates.management_fee_percent = 0;
        }
      }

      // When a stated operating_expenses total exists (from CMBS/CoStar), it's more
      // authoritative than summing per-SF line items (which have rounding errors).
      // Adjust "other_expenses" as the plug to make the total match the stated figure.
      const statedTotal = property.operating_expenses;
      if (statedTotal && statedTotal > 0) {
        const knownExpenses = (updates.property_taxes ?? 0)
          + (updates.insurance ?? 0)
          + (updates.utilities ?? 0)
          + (updates.repairs_maintenance ?? 0)
          + (updates.reserves_capex ?? 0)
          + (hasExtractedManagement ? seededManagement : 0);
        const otherPlug = statedTotal - knownExpenses;
        if (otherPlug >= 0) {
          updates.other_expenses = otherPlug;
        }
      }
    }

    // ── Stabilization / Capital costs from OM ──
    const stabCosts = additional.stabilization_costs as {
      capex?: number; tenant_improvements?: number; leasing_commissions?: number; total?: number;
    } | undefined;
    if (stabCosts) {
      if (stabCosts.capex && stabCosts.capex > 0) {
        setIfEmpty('va_capex', stabCosts.capex);
      }
      if ((stabCosts.tenant_improvements ?? 0) > 0 || (stabCosts.leasing_commissions ?? 0) > 0) {
        setIfEmpty('va_ti_leasing', (stabCosts.tenant_improvements ?? 0) + (stabCosts.leasing_commissions ?? 0));
      }
    }

    // Calculate as-is and stabilized rent PSF from tenant data
    const sqft = property.building_size || 0;
    if (sqft > 0 && property.gross_income) {
      const currentOccupancy = property.occupancy_rate ?? 100;
      const occupiedSF = sqft * (currentOccupancy / 100);
      if (occupiedSF > 0) {
        setIfEmpty('as_is_rent_psf', parseFloat((property.gross_income / occupiedSF).toFixed(2)));
      }
      if (pf?.gross_income) {
        const stabOcc = (pf.occupancy_rate ?? 95) / 100;
        const stabSF = sqft * stabOcc;
        if (stabSF > 0) {
          setIfEmpty('stabilized_rent_psf', parseFloat((pf.gross_income / stabSF).toFixed(2)));
        }
      }
    }

    /**
     * ⭐⭐⭐ SEED THE INCOME FROM A STATED NOI. Joseph's ruling, 2026-08-31.
     *
     * Runs LAST in this effect, because it needs what the expense seeding above decided. A stated
     * NOI is already NET, so seeding it as income without settling the expense side nets it twice -
     * which is why this writes BOTH sides together or refuses outright.
     * ⛔ New analyses only: re-basing a saved one is the same class of harm as editing
     * STRATEGY_THRESHOLDS. ⛔ And it refuses whenever a rent roll or a stated gross income exists,
     * so every deal that already reconciles is untouched by construction.
     */
    if (!hasSavedAnalysis) {
      const seededExpenseTotal = (updates.property_taxes ?? 0) + (updates.insurance ?? 0)
        + (updates.utilities ?? 0) + (updates.repairs_maintenance ?? 0)
        + (updates.reserves_capex ?? 0) + (updates.other_expenses ?? 0);
      const noiSeed = seedFromStatedNoi({
        statedNoi: property.noi ?? null,
        /**
         * ⛔ THE RATE THE ENGINE RESOLVES, not a row count - the same call the PGI seed makes at
         * the top of this component, so the two cannot disagree. Murrieta carries one tenant row
         * with no rent at all ("Tenant-level rent amount not disclosed"); a row count called that
         * a rent roll and the screen kept reading cap 0.00%.
         */
        rentRollRate: annualRentRateAsOf({
          tenants: ((property.additional_data as Record<string, any> | undefined)?.tenants ?? []) as ScheduleTenant[],
          asOf: toIsoDate(new Date()),
          baseRentAsOf: toIsoDate(new Date()),
        }).rate,
        statedGrossIncome: property.gross_income ?? null,
        statedExpenses: property.operating_expenses ?? seededExpenseTotal,
      });
      if (noiSeed) {
        updates.potential_gross_income = noiSeed.potentialGrossIncome;
        updates.vacancy_rate = noiSeed.vacancyRate;
        extractionSourced.push('potential_gross_income', 'vacancy_rate');
        if (noiSeed.zeroExpenses) {
          // ⛔ Both sides, together. The AUTOMATIC expense defaults would otherwise net a figure
          // that is already net - which is exactly how Victorville reached a NEGATIVE NOI, and
          // from there a negative exit value and an IRR of -100.00%.
          updates.property_taxes = 0; updates.auto_property_taxes = false;
          updates.insurance = 0; updates.auto_insurance = false;
          updates.utilities = 0; updates.auto_utilities = false;
          updates.repairs_maintenance = 0;
          updates.reserves_capex = 0;
          updates.other_expenses = 0;
          updates.management_fee_percent = 0;
          extractionSourced.push('property_taxes', 'insurance', 'utilities',
            'repairs_maintenance', 'reserves_capex', 'other_expenses', 'management_fee_percent');
        }
        setStatedNoiSeed(noiSeed);
      }
    }

    if (Object.keys(updates).length > 0) {
      setData(prev => ({ ...prev, ...updates }));
    }
    /**
     * ⛔ Record provenance for the fields taken from the DOCUMENT, so `applyPreset` leaves them.
     *
     * 🚨🚨 THIS USED TO READ `if (!next[k]) next[k] = 'extraction'` - A LATCH, AND IT COST A REAL
     * DEAL. 13368 Main Street: the OM stated CAM $54,022, taxes $46,523 and insurance $5,068, the
     * extractor read all three correctly and they reached the property record. Picking the
     * Single-tenant NNN archetype - the one preset that carries all six expense fields at 0 -
     * stamped them `archetype_preset`, and from that moment the latch refused to let the DOCUMENT
     * claim them back. Switching to Multi-tenant retail left them at zero, because a field already
     * stamped by a preset is not protected FROM a preset.
     *
     * ⭐⭐⭐ The result was income WITHOUT the expenses it recovers: NOI $349,318 against the OM's
     * $269,254, a 7.46% cap against a stated 5.75%, and a GO where the deal reads CAUTION.
     * **First writer wins is the wrong rule when one of the writers is the document.**
     *
     * ⭐ 'user' still outranks everything - hand-typed work is never overwritten. Only a PRESET's
     * guess loses to the document, which is exactly what `PROTECTED_SOURCES` says it should do.
     */
    if (extractionSourced.length > 0) {
      setFieldSources(prev => markExtractionSources(prev, extractionSourced));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Derived Calculations ──────────────────────────────────────

  const n = (v: number | null | undefined) => v ?? 0;
  /**
   * ⭐⭐ AN OPTIONAL RATE, WHERE "NOT SET" AND "ZERO" ARE DIFFERENT ANSWERS.
   *
   * 🚨 `handleChange` stores what the input gives it, which is a STRING. `property_tax_growth_rate`
   * was read with `typeof v === 'number'` at both call sites, so a field showing "2" on screen
   * arrived as `"2"`, failed the check, and BOTH the projection and the NOI schedule silently fell
   * back to a single growth rate. The screen said 2%, the maths used 3%, and nothing said so.
   * ⛔ `n()` cannot do this job: it maps an empty field to 0, and 0% tax growth is a real
   * assumption, not the absence of one.
   */
  const optionalRate = (v: number | string | null | undefined): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const parsed = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const strategy = (data.investment_strategy ?? 'value_add') as InvestmentStrategy;
  // The selected investor profile outranks the saved investment_strategy. This
  // one binding feeds the scorecard, the header strip, the scenario table, the
  // goal-seek solver and the PDF, so every surface scores the deal the same way.
  const activeProfile = profiles.find(p => p.id === data.investor_profile_id);
  const thresholds = resolveActiveThresholds(activeProfile, strategy);
  /**
   * What to call that bar. Resolved from the SAME two inputs as `thresholds`, through the shared
   * resolver, so the words and the numbers cannot name different deals. Three call sites below
   * used to print a hardcoded literal instead, which meant every freshly extracted deal - none of
   * which has an investor profile - said "Core-Plus" on the verdict card and in the PDF while
   * being graded on another strategy entirely. Found 2026-08-31 by opening a fresh deal.
   */
  const thresholdLabel = resolveThresholdLabel(activeProfile, strategy);

  // Lease type & NNN reimbursements
  const leaseType = data.lease_type ?? 'gross';
  /**
   * ⛔ A CARRY HAS NOBODY TO REIMBURSE IT. One resolver, so the screen and the document cannot
   * disagree about whether a recovery applies. ▶ `resolveExpenseRecoveries`.
   */
  const recoveries = resolveExpenseRecoveries({
    leaseType,
    propertyTaxes: n(data.property_taxes),
    insurance: n(data.insurance),
    repairsMaintenance: n(data.repairs_maintenance),
    nnnRecoveryRate: n(data.nnn_reimbursement_rate),
    modifiedGrossReimbursement: n(data.modified_gross_reimbursement),
    expensesAreCarry: data.expenses_are_carry === true,
  });
  const nnnReimbursements = recoveries.recoveries;

  // ⚠️ Frozen once per mount. Reading the clock inside the memo would make the same analysis
  // produce a different schedule on a re-render, which is the kind of drift nobody notices.
  const todayForClose = useMemo(() => new Date(), []);

  // Income / Expense
  const pgi = n(data.potential_gross_income);

  /**
   * ⭐⭐ F1 - THE HEADLINE AND THE RENT ROLL, RECONCILED AND STATED.
   *
   * Two independent statements of what the property earns live in the same record, and nothing
   * compared them. Measured 2026-08-27 across the house-style six: Guitar Center's stated income
   * was the 2/1/2028 step (cap 6.05% against a delivered 5.75%), Banc of California's expired on
   * 8/1/2026 (6.01% against 6.19%), and Mojave's $2,427 gap was REAL other income.
   * ⛔ The three are indistinguishable to the app, so the flag says what to DO, and the figure
   * currently in use is never changed underneath a saved analysis.
   */
  const inPlaceIncome = useMemo(() => reconcileInPlaceIncome({
    rentRollRate: annualRentRateAsOf({
      tenants: ((property.additional_data as Record<string, any> | undefined)?.tenants ?? []) as ScheduleTenant[],
      asOf: toIsoDate(todayForClose),
      /**
       * ⭐ The same day on BOTH sides of the comparison. `statedIncome` is what the document says
       * it earns today, so the rate it is compared against must be the one the document states
       * for today - not one a derived anniversary has already stepped past. Without this the flag
       * fires on a difference the app invented, and the invented figure wins.
       */
      baseRentAsOf: toIsoDate(todayForClose),
    }).rate,
    /**
     * ⛔ ALWAYS THE OM'S HEADLINE, NEVER THE CURRENT BASIS. Comparing against `pgi` made the flag
     * VANISH the moment the seed adopted the rent roll - on the very deal whose headline is a
     * 2028 figure. A flag that disappears when it is acted on is not a flag, and the property
     * header still reads $181,440 and 6.05% for anyone looking at it.
     */
    statedIncome: property.gross_income ?? null,
    asOf: toIsoDate(todayForClose),
  }), [property.additional_data, property.gross_income, todayForClose]);
  const vacancyAmount = calculateVacancyAmount(pgi, n(data.vacancy_rate));
  const egi = calculateEffectiveGrossIncome(pgi, vacancyAmount, n(data.other_income) + nnnReimbursements);
  // Management fee is on rental income only (EGI before pass-throughs),
  // not on NNN reimbursements which are direct pass-throughs to tenants
  const egiBeforeReimbursements = pgi - vacancyAmount + n(data.other_income);
  const managementFee = calculateManagementFee(egiBeforeReimbursements, n(data.management_fee_percent));
  const totalExpenses = calculateTotalExpenses({
    propertyTaxes: n(data.property_taxes),
    insurance: n(data.insurance),
    utilities: n(data.utilities),
    managementFee,
    repairsMaintenance: n(data.repairs_maintenance),
    reservesCapex: n(data.reserves_capex),
    otherExpenses: n(data.other_expenses),
  });
  /**
   * ⭐⭐⭐ ON A NON-INCOME ASSET THE OPERATING EXPENSES ARE A CARRY. It reduces cash flow and never
   * NOI, so a property that earns nothing shows NOI $0 rather than a negative - which is what stops
   * the exit going negative and `calculateIRR` printing its -100 sentinel as a measurement.
   */
  const expensesAreCarry = data.expenses_are_carry === true;
  /**
   * ⭐⭐ JJ2. THE IN-PLACE NOI IS STRUCK AFTER THE CARRY IS READ, through the shared rule. It used
   * to be struck as `egi - totalExpenses` seven lines ABOVE the flag, so the cap, DSCR, debt
   * yield, the Year 1 card, the lender sizing and the exit fallback all read -$100,443 on the
   * untouched Victorville deal while the proforma beside them read $0 with the carry on. Measured
   * 2026-09-02: the flag moved one table and none of the seven headline numbers. ▶ `inPlaceNoi`.
   */
  const noi = inPlaceNoi({ egi, totalExpenses, expensesAreCarry });
  /**
   * ⭐⭐⭐ ONE SET OF NOI INPUTS FOR EVERY SURFACE. Built once, SPREAD into the projection below and
   * read by `yearOneNoi`, so year 1 on the strip IS year 1 in the table - and the PDF, which once
   * assembled these by hand and left the lease-up and Prop 13 out, cannot omit a field.
   */
  const noiInputs = useMemo(() => noiProjectionInputs({ data, egi, totalExpenses }), [data, egi, totalExpenses]);
  /** ⭐ KK3: what the cap is struck on, in words, derived from the lease-up - see `capBasisCaption`. */
  const capCaption = capBasisCaption(noiInputs.leaseUpMonths);

  // Financing
  const purchasePrice = n(data.purchase_price);

  // Seller carryback
  const sellerCarrybackAmt = n(data.seller_carryback_amount);
  const sellerCarrybackRate = n(data.seller_carryback_rate);
  const sellerCarrybackTerm = n(data.seller_carryback_term) || 5;
  /**
   * ⭐⭐⭐ A COST THAT ATTACHES TO THE FINANCING - resolved ONCE, here, and handed to every pricing
   * path as one object. Closing costs attach to the PURCHASE and operating expenses to the
   * PROPERTY; this is the third category, and until now $67,000 of SBA 504 fees, a $138,125 7(a)
   * guaranty and Long Beach's $40,990 assumption fee each had to be mis-filed or dropped.
   * ⛔ Being light on cash FLATTERS every return metric, which is why it cannot simply be ignored.
   * ▶ `financingFee.ts` carries the identity and the evidence.
   */
  const financingFee = resolveFinancingFee({
    amount: data.financing_fee_amount,
    financed: data.financing_fee_financed,
    note: data.financing_fee_note,
    servicingFeeRate: data.loan_servicing_fee_rate,
    hasPrimaryNote: data.sizing_mode !== 'all_cash',
    hasSecondaryNote: sellerCarrybackAmt > 0,
  });

  /** The second note AS FINANCED - the 504's debenture carries its own fees, and the bank's does not. */
  const effectiveCarrybackAmt = sellerCarrybackAmt + financingFee.secondaryUplift;
  /**
   * ⛔ THE PAYMENT DERIVES FROM THE SAME AMOUNT THE MODEL SERVICES AND RETIRES. Leaving it on the
   * raw carryback would print a monthly payment on the screen that the projection does not use -
   * the same two-figures-one-note shape that made the exit pay off one note of two.
   */
  /**
   * ⛔ ONE OBJECT, BOTH OUTPUTS. The note is built FIRST and the payment derives from it, so an
   * interest-only note cannot be serviced as an amortizing one and retired as an interest-only
   * one. Building the payment separately is exactly how the carryback came apart before.
   */
  const carrybackInterestOnly = data.seller_carryback_interest_only === true;
  const secondNote: SecondaryNote | undefined = effectiveCarrybackAmt > 0
    ? {
        amount: effectiveCarrybackAmt,
        interestRate: sellerCarrybackRate,
        amortizationYears: sellerCarrybackTerm,
        interestOnly: carrybackInterestOnly,
      }
    : undefined;
  const sellerAnnualDS = secondNote ? secondaryNoteAnnualDebtService(secondNote) : 0;
  const sellerMonthlyPmt = sellerAnnualDS / 12;
  // The capital structure decides which of the loan and the equity is the input.
  // Equity-first states the cash and solves the loan (LTV becomes an output);
  // ltv_driven keeps the historical path so every saved analysis reads the same.
  const { loanAmount, ltvPercent, downPayment } = (() => {
    if (data.sizing_mode === 'all_cash') {
      return { loanAmount: 0, ltvPercent: 0, downPayment: purchasePrice };
    }
    if (data.sizing_mode === 'equity_first' && (data.down_payment_target ?? 0) > 0) {
      const sized = sizeLoanFromEquity(purchasePrice, data.down_payment_target!, sellerCarrybackAmt);
      return {
        loanAmount: sized.loanAmount + financingFee.primaryUplift,
        ltvPercent: sized.ltvPercent,
        downPayment: data.down_payment_target!,
      };
    }
    const la = calculateLoanAmount(purchasePrice, data.ltv_percent ?? 70);
    return {
      loanAmount: la + financingFee.primaryUplift,
      ltvPercent: data.ltv_percent ?? 70,
      /**
       * ⛔ THE DOWN PAYMENT IS STRUCK ON THE NOTES **NET** OF A FINANCED FEE, which is to say it
       * does not move at all. A financed fee raises the note, but the extra proceeds pay the fee
       * rather than the seller, so the equity toward the PURCHASE is unchanged. On Joseph's SBA
       * 504 that reads $5,750,000 - $2,875,000 - $2,300,000 = $575,000, which is his equity
       * exactly; the app read $508,000 while the $67,000 had nowhere to go.
       */
      downPayment: calculateAdjustedDownPayment(purchasePrice, la, sellerCarrybackAmt),
    };
  })();

  const monthlyPmt = calculateMonthlyPayment(loanAmount, n(data.interest_rate), n(data.amortization_years));
  const bankAnnualDS = calculateAnnualDebtService(monthlyPmt);
  const annualDS = bankAnnualDS + sellerAnnualDS;
  const closingCosts = calculateClosingCosts(purchasePrice, n(data.closing_costs_percent));
  /**
   * ⛔ SOURCES = USES. `financingFee.cashCost` is the whole fee when it is paid at closing and
   * ZERO when it is financed, because then a note is the matching source. Joseph's 7(a) reads
   * $750,000 + $115,000 + $138,125 = $1,003,125 and his 504 $575,000 + $115,000 + $0 = $690,000,
   * both to the dollar. ▶ `calculateTotalCashRequired`.
   */
  const totalCashRequired = calculateTotalCashRequired(downPayment, closingCosts, financingFee.cashCost);
  /**
   * ⭐⭐ YEAR-1 METRICS READ YEAR 1 OF THE APPLIED SCHEDULE, not the in-place NOI.
   *
   * 🚨 Once a contractual schedule was applied the proforma ran on it while DSCR and year-1 cash
   * flow kept reading the in-place figure - two different year-1 NOIs on one screen. Measured
   * against Joseph's delivered STIIIZY analysis: DSCR read 3.52x (386,355/109,650) where he
   * delivered 3.62x (396,980/109,650), and his year-1 cash flow of $287,330 is exactly
   * 396,980 - 109,650.
   *
   * ⛔ THE GOING-IN CAP DELIBERATELY STAYS ON THE IN-PLACE NOI. It is struck on what the
   * property earns TODAY - his own table is headed "Going-In Cap (In-Place)" and 386,355 /
   * 4,292,840 is exactly the 9.00% he prints. Moving it would break a number that is already
   * right, which is the mistake I nearly made with the exit cap.
   *
   * ⭐⭐ JJ2: year 1 is the PROJECTION'S year 1, so it also honours the lease-up (Victorville: NOI
   * $0 and DSCR 0.00x - his scorecard) and the carry (NOI $0, the carry deducted from cash flow
   * below exactly as the proforma's year 1 deducts it).
   */
  const yearOne = yearOneNoi(noiInputs);
  const yearOneNOI = yearOne.noi;
  const beforeTaxCF = calculateBeforeTaxCashFlow(yearOneNOI, annualDS) - yearOne.carryCosts;
  /**
   * ⭐⭐ THE UNDERWRITER'S CAP, AND THE MARKETED ONE, BOTH SHOWN.
   *
   * An OM may date its NOI ahead of today - Bank of the Sierra prints "$150,408 (as of 1/1/2027)",
   * one 2.25% escalation out, so its marketed 6.00% is a FORWARD cap against an in-place 5.87%.
   * A lender sizes on the rent in force at closing, so the going-in cap is struck on the re-based
   * figure and the marketed one is printed beside it rather than discarded.
   * ⛔ It refuses far more often than it acts - no stated date, a past date, several leases, or no
   * recurring rate all leave the figure untouched and say why.
   */
  const noiRebase = useMemo(() => rebaseToInPlace({
    statedNoi: noi,
    noiAsOf: (property.additional_data as Record<string, any> | undefined)?.noi_as_of ?? null,
    asOf: toIsoDate(todayForClose),
    tenants: ((property.additional_data as Record<string, any> | undefined)?.tenants ?? []) as ScheduleTenant[],
  }), [noi, property.additional_data, todayForClose]);

  const capRate = calculateCapRate(noiRebase.inPlaceNoi, purchasePrice);
  /** NB1 (the 122nd): the deal's one tenant - the Benchmark panel shows only on a single-tenant deal (brief section 6). */
  const benchmarkTenant = useMemo(() => singleTenantOf(property.additional_data), [property.additional_data]);
  /** What the OM markets, when it differs. Printed, never silently dropped. */
  const marketedCapRate = noiRebase.stepsRemoved > 0
    ? calculateCapRate(noiRebase.statedNoi, purchasePrice)
    : null;
  const pricePerSqft = calculatePricePerSqft(purchasePrice, property.building_size || 0);
  const dscr = calculateDSCR(yearOneNOI, annualDS);

  // Value-Add totals
  const vaTotalCost = n(data.va_capex) + n(data.va_ti_leasing) + n(data.va_carry_costs) + n(data.va_contingency);
  /**
   * ⭐⭐ THE FINANCING FEE IS PART OF PROJECT COST, whichever way it is paid. His own SBA 504
   * document states it: total project cost $5,817,000 against a $5,750,000 price, because $67,000
   * of fees rode into the debenture - and that is exactly why the CDC note is 40.7% of project
   * cost rather than 40.0% of price. Leaving it out printed the same project cost for two deals
   * that differ by the whole fee. ▶ `financingFee.ts`.
   */
  const totalProjectCost = purchasePrice + closingCosts + vaTotalCost + financingFee.fee;

  // Projections
  const holdingPeriod = n(data.holding_period) || 5;
  const exitCapRate = n(data.exit_cap_rate) || 6;
  const sellingCostsPercent = n(data.selling_costs_percent) || 2;

  /**
   * Phase 3A: the contractual rent schedule from the actual lease terms.
   *
   * `buildRentSchedule` returns RENT, deliberately - deriving expenses inside it would
   * be a second expense model. NOI is reached here by anchoring on the year-1 NOI this
   * screen already computes and layering the contractual rent growth on top, net of
   * modelled expense growth:
   *
   *   NOI(y) = NOI(1) + (rent(y) - rent(1)) - (expenses(y) - expenses(1))
   *
   * Anchoring on NOI(1) rather than using rent(y) directly matters: this screen's NOI
   * already accounts for vacancy, reimbursements and expenses, and Bear Valley's saved
   * analysis carries a 7% vacancy and $55,000 of taxes, so its NOI is NOT the OM's
   * headline rent. For a true NNN deal with zero landlord expenses the formula reduces
   * to NOI(y) = rent(y), which is what the reference guide's schedule shows.
   *
   * ⭐⭐ HOLD YEAR 1 RUNS FROM THE CLOSE, not from 1 January - Joseph's ruling 2026-08-27. The
   * engine was always built for this ("honours the close date, so the same bump blends
   * differently"); it just never received one, and the old hardcode of 1 January of the CURRENT
   * year anchored every deal on a year the buyer would barely own. Measured on STIIIZY: that one
   * wrong starting year moved IRR from a delivered 10.35% to 7.41%.
   * ⛔ The default is 90 days and the panel PRINTS the date it used, so it is never silent.
   */

  /**
   * ⭐ What the extraction guard flagged about SCOPE and reconciliation. Written by
   * `financialReconciliation.ts` into `additional_data.reconciliation.warnings` and, until
   * 2026-08-31, read by nothing on any screen.
   */
  const extractionWarnings = useMemo(() => {
    const add = property.additional_data as Record<string, any> | undefined;
    const rec = add?.reconciliation;
    const list = (rec?.warnings ?? []) as Array<{ field: string; message: string; severity: string }>;
    const fromExtraction = list.filter(w => w && typeof w.message === 'string' && w.message.length > 0);

    /**
     * ⭐⭐⭐ A LIVE CHECK ON THE MODEL, not a record of what the extractor thought. The others are
     * written once at extraction time and cannot see a preset that zeroes the expenses ten minutes
     * later - which is exactly how 13368 Main Street shipped a 7.46% cap against a stated 5.75%.
     * This one reads the CURRENT form every render, so it fires whatever path produced the state.
     */
    const inconsistent = expenseConsistencyWarning({
      incomeIncludesReimbursements:
        add?.income_includes_reimbursements === true || add?.income_includes_reimbursements === 'true',
      expenseReimbursements: add?.expense_reimbursements,
      statedOperatingExpenses: add?.operating_expenses ?? property.operating_expenses,
      // ⛔ The DOLLARS the document itemised, not how many lines it printed. 1345 N Grand lists
      // four expense lines at $0 each, "paid by tenant (absolute NNN)" - a correct absolute-net
      // deal that counting lines would have flagged.
      itemisedExpenseTotal: Array.isArray(add?.expenses)
        ? (add!.expenses as Array<{ annual_amount?: number }>)
            .reduce((t, e) => t + (Number(e?.annual_amount) || 0), 0)
        : 0,
      landlordExpenses: {
        property_taxes: data.property_taxes,
        insurance: data.insurance,
        utilities: data.utilities,
        repairs_maintenance: data.repairs_maintenance,
        reserves_capex: data.reserves_capex,
        other_expenses: data.other_expenses,
      },
    });

    return inconsistent ? [inconsistent, ...fromExtraction] : fromExtraction;
  }, [
    property.additional_data, property.operating_expenses,
    data.property_taxes, data.insurance, data.utilities,
    data.repairs_maintenance, data.reserves_capex, data.other_expenses,
  ]);

  const leaseSchedule = useMemo(() => {
    const tenants = ((property.additional_data as Record<string, any> | undefined)?.tenants ?? []) as ScheduleTenant[];
    const startDate = resolveCloseDate(data.close_date, todayForClose);
    // Rollover is opt-in. Passing undefined keeps the engine's refusal to guess past a
    // lease expiry, which is the shipped behaviour for every deal that has not set it.
    const built = buildRentSchedule({
      tenants, holdYears: holdingPeriod, startDate,
      // ⛔ TODAY, not the close. The base rent is current as of the day the roll was read; the
      // close is where hold year 1 begins and says nothing about when the rent was stated.
      baseRentAsOf: toIsoDate(todayForClose),
      rollover: data.rollover_assumptions
        ? {
            renewalProbability: n(data.rollover_assumptions.renewalProbability),
            downtimeMonths: n(data.rollover_assumptions.downtimeMonths),
            marketRentPerSf: n(data.rollover_assumptions.marketRentPerSf),
            freeRentMonths: n(data.rollover_assumptions.freeRentMonths),
            renewalRentBasis: data.rollover_assumptions.renewalRentBasis,
            tiNewPerSf: n(data.rollover_assumptions.tiNewPerSf),
            tiRenewalPerSf: n(data.rollover_assumptions.tiRenewalPerSf),
            lcNewPercent: n(data.rollover_assumptions.lcNewPercent),
            lcRenewalPercent: n(data.rollover_assumptions.lcRenewalPercent),
          }
        : undefined,
    });

    /**
     * ⭐⭐ THE ANCHOR IS THE RENT RATE IN FORCE TODAY - backlog A1, fixed 2026-08-27.
     *
     * `noi` is what the property earns TODAY, so the rent it must be matched against is the
     * annual rate in force TODAY. The old code anchored on HOLD YEAR 1, which is the same year
     * only when the close is this year - so once `close_date` moved year 1 forward the whole
     * schedule sat ONE ESCALATION LOW. Measured on STIIIZY: it opened at $386,355 against a
     * delivered $396,980, taking DSCR to 3.52x and IRR to 9.45% against 3.62x and 10.35%.
     *
     * ⛔ AND IT IS A RATE, NOT A YEAR. Deriving it from a one-year schedule starting today
     * BLENDS across the next step - $392,553 instead of $386,355 on this deal. That was tried
     * and reverted: it is closer, still wrong, and worse than nothing because it looks right.
     * `annualRentRateAsOf` reads one day and never blends.
     *
     * ⛔ When the rate cannot be derived the schedule REFUSES rather than falling back to year 1,
     * because falling back is the defect and it is invisible.
     */
    const anchor = annualRentRateAsOf({
      tenants, asOf: toIsoDate(todayForClose), baseRentAsOf: toIsoDate(todayForClose),
    });
    const { noiSchedule, warnings: anchorWarnings } = noiScheduleFromRent({
      annualRent: built.annualRent,
      inPlaceNoi: noi,
      inPlaceRentRate: anchor.rate,
      totalExpenses,
      expenseGrowthRate: n(data.expense_growth_rate) / 100,
      propertyTaxes: n(data.property_taxes),
      taxGrowthRate: (r => (r === null ? null : r / 100))(optionalRate(data.property_tax_growth_rate)),
      // ⭐ Recoveries reimburse expenses; frozen while expenses grow, they understate NOI.
      otherIncome: n(data.other_income),
      otherIncomeGrowthRate: (r => (r === null ? null : r / 100))(optionalRate(data.other_income_growth_rate)),
    });

    /**
     * ⭐⭐ THE SECOND TABLE. The proforma above BLENDS - Joseph's Pitfall 3 and his 2026-08-01
     * ruling - which is right for "what do I collect" and wrong for checking against a lease.
     * This is the contract as written: the rent in force from each effective date, the table an
     * OM prints. Neither corrects the other; showing only the blend is why a reader cannot
     * reconcile the proforma against the document in front of them.
     */
    const through = new Date(startDate + 'T12:00:00Z');
    through.setUTCFullYear(through.getUTCFullYear() + holdingPeriod);
    const leaseYears = leaseYearSchedule({
      tenants, from: startDate, through: toIsoDate(through), baseRentAsOf: toIsoDate(todayForClose),
    });

    return {
      ...built,
      warnings: [...built.warnings, ...anchor.warnings, ...anchorWarnings],
      noiSchedule,
      startDate,
      leaseYears,
      anchorRate: anchor.rate,
      anchorDate: toIsoDate(todayForClose),
      tenantCount: tenants.length,
    };
  }, [property.additional_data, holdingPeriod, noi, totalExpenses, data.expense_growth_rate, data.rollover_assumptions, data.close_date, todayForClose]);

  const scheduleIsApplied = useMemo(() => {
    const applied = data.noi_overrides;
    if (!Array.isArray(applied) || applied.length !== leaseSchedule.noiSchedule.length) return false;
    return leaseSchedule.noiSchedule.every((v, i) =>
      v === null ? applied[i] == null : typeof applied[i] === 'number' && Math.abs((applied[i] as number) - v) < 1);
  }, [data.noi_overrides, leaseSchedule.noiSchedule]);

  /**
   * Leasing capital applies only once the schedule is applied. Tying it to the applied
   * schedule keeps the two halves of the same model together: the rollover rent and the
   * TI/LC that buys it either both price the deal or neither does.
   */
  const appliedCapitalCosts = useMemo(
    () => (scheduleIsApplied && leaseSchedule.capitalCosts.some(c => c > 0)
      ? leaseSchedule.capitalCosts
      : undefined),
    [scheduleIsApplied, leaseSchedule.capitalCosts]
  );

  const projections = useMemo(() => generateProjections({
    // ⭐⭐ THE NOI INPUTS, from the one builder `yearOneNoi` also reads - JJ2.
    ...noiInputs,
    purchasePrice,
    loanAmount,
    interestRate: n(data.interest_rate),
    amortizationYears: n(data.amortization_years),
    holdingPeriod,
    exitCapRate,
    sellingCosts: sellingCostsPercent,
    // ⛔ The note itself, not a pre-computed payment: it is serviced AND retired at exit.
    secondaryLoan: secondNote,
    // The contractual rent schedule has to drive the headline projection too.
    // The verdict card, the scenario cards and the exported PDF all price off
    // it; omitting it here is what made the on-screen IRR disagree with all
    // three on any deal that has a schedule set.
    // Leasing capital reaches the headline projection for the same reason the schedule
    // does: the verdict card, the scenarios, the solver and the PDF all price off this.
    capitalCostsByYear: appliedCapitalCosts,
    // ⭐⭐ The ONGOING financing fee. It reduces CASH FLOW and neither NOI nor debt service, so the
    // exit price and DSCR are untouched - Joseph's 7(a) prints 0.66x on debt service alone while
    // its cash flow carries the fee. NULL is exactly the previous arithmetic.
    servicingFeeRate: financingFee.servicingFeeRate,
  }), [appliedCapitalCosts, noiInputs, purchasePrice, loanAmount, data.interest_rate, data.amortization_years, holdingPeriod, exitCapRate, sellingCostsPercent, sellerAnnualDS, secondNote, financingFee]);

  /** The proforma grows a Svc Fee column only on a deal that actually pays one. */
  const showServicingFeeColumn = projections.some(p => p.servicingFee > 0);
  /** And a Carry column only on an asset whose costs are carried rather than operated. */
  const showCarryColumn = projections.some(p => p.carryCosts > 0);

  // Exit & Returns
  // Use forward-year NOI (Year N+1) for exit valuation — the buyer at exit
  // is purchasing the next year's income stream, not the current year's
  const lastYear = projections.length > 0 ? projections[projections.length - 1] : null;
  // For NNN properties with pro forma data, use the pro forma NOI as a floor
  // for exit NOI — all contractual lease bumps will have kicked in by exit.
  const additionalData = property.additional_data as Record<string, any> | undefined;
  const proFormaNOI = additionalData?.pro_forma_financials?.noi as number | undefined;
  /**
   * ⭐⭐ THE EXIT PRICES ON THE FINAL HOLD YEAR when a contractual schedule is applied.
   *
   * 🚨 `projectedExitNOI` grows the last year by the GENERIC income growth rate. On a lease with
   * contractual steps that is the error the NNN rule already forbids - "don't use generic 3%
   * growth" - and it errs toward FLATTERING the exit, the worst direction for this tool. Joseph's
   * delivered analyses all price on the last year they own it: STIIIZY reads "Exit Year NOI (Y5)
   * $442,483", and the Bear Valley golden uses the last element of its own schedule.
   */
  const exitNOIResolved = resolveExitNoi({
    applied: data.noi_overrides,
    // ⭐ The last year you OWN it, not a year past it. His ruling; six delivered analyses.
    // ⛔ Through the shared rule, so the PDF cannot derive a different one.
    finalHoldYearNoi: finalHoldYearNoi(projections, noi),
    proFormaNoi: proFormaNOI,
    leaseType,
  });
  const exitNOI = exitNOIResolved.value;

  /**
   * ⭐⭐⭐ THE RECEIPT - what was checked before this goes to a client, and what was not.
   *
   * Joseph, 2026-08-30. ⛔ It reads state this screen ALREADY computed rather than re-deriving
   * anything: a second opinion computed a second way is a second model of the deal, which is the
   * defect class this build produces most. Every input below is the figure the screen is showing.
   */
  const preflight = useMemo(() => analysisPreflight({
    tenants: ((property.additional_data as Record<string, any> | undefined)?.tenants ?? []) as ScheduleTenant[],
    inPlaceDifference: inPlaceIncome.difference,
    inPlaceStated: property.gross_income ?? null,
    inPlaceRentRoll: typeof inPlaceIncome.basis === 'number' && inPlaceIncome.source === 'rent_roll'
      ? inPlaceIncome.basis : null,
    noiSchedule: leaseSchedule.noiSchedule,
    // ⛔ The rent the LEASES state - not the NOI schedule, which carries modelled years too.
    contractualRent: leaseSchedule.annualRent,
    scheduleIsApplied,
    exitNoiSource: exitNOIResolved.source,
    holdingPeriod,
    closeDate: leaseSchedule.startDate,
    closeDateWasChosen: Boolean(data.close_date),
    hasRolloverAssumptions: Boolean(data.rollover_assumptions),
    propertyOperatingExpenses: property.operating_expenses ?? null,
    modelledExpenses: totalExpenses,
    goingInCapRate: capRate,
    exitCapRate,
    leaseUpMonths: n(data.lease_up_months),
    carryCosts: n(data.va_carry_costs),
  }), [data.lease_up_months, data.va_carry_costs, property.additional_data, property.gross_income, property.operating_expenses, inPlaceIncome,
       leaseSchedule.noiSchedule, leaseSchedule.annualRent, leaseSchedule.startDate, scheduleIsApplied, exitNOIResolved.source,
       holdingPeriod, data.close_date, data.rollover_assumptions, totalExpenses, capRate, exitCapRate]);

  /** ⭐ The caption is DERIVED from what `exitNoi` actually used - see `exitNoiYearLabel`. */
  const exitNOIYearLabel = exitNoiYearLabel(exitNOIResolved.source, holdingPeriod);
  /**
   * ⭐⭐⭐ A STATED EXIT VALUE OUTRANKS THE CAP RATE. Land has no NOI to divide, so without this the
   * exit prices at ZERO and `calculateIRR` returns its -100 sentinel for the screen to print as a
   * measurement. `optionalRate` is reused because `n()` maps an empty field to 0 and a stated 0 is
   * not a value - the same NULL-versus-zero trap the tax growth rate needed.
   */
  const statedExitValue = optionalRate(data.exit_value);
  const saleProceeds = calculateSaleProceeds(
    exitNOI, exitCapRate, lastYear?.loanBalance ?? loanAmount, sellingCostsPercent, statedExitValue);
  const exitIsStatedValue = saleProceeds.exitPriceBasis === 'stated_value';
  /** The two notes at exit, taken from the projection rather than re-derived by the waterfall. */
  const exitBankBalance = lastYear?.bankLoanBalance ?? loanAmount;
  const exitSecondBalance = lastYear?.secondaryLoanBalance ?? 0;

  // The same sale priced across a range of exit caps, for the Exit tab's value table. Built
  // from the identical inputs the waterfall above uses, so the current-cap column reproduces
  // it by construction. Lives in financialCalculations.ts because nothing can test logic left
  // in this file - see buildExitCapLadder's own comment, and backlog B14.
  const exitCapLadder = buildExitCapLadder({
    exitNOI,
    exitCapRate,
    loanBalance: lastYear?.loanBalance ?? loanAmount,
    sellingCostsPercent,
    purchasePrice,
  });
  const totalCashInvested = totalCashRequired + vaTotalCost;
  const irrCashFlows = buildIRRCashFlows(totalCashInvested, projections, saleProceeds.netToSeller);
  const irrValue = calculateIRR(irrCashFlows);
  const totalCashFlows = projections.reduce((s, p) => s + p.cashFlow, 0);
  const equityMultiple = calculateEquityMultiple(totalCashInvested, totalCashFlows, saleProceeds.netToSeller);
  const avgCashOnCash = calculateAvgCashOnCash(projections, totalCashInvested);

  // What the asset returns with no debt anywhere — the honest read on whether
  // the return is coming from the real estate or from the leverage.
  const unleveredIrr = calculateIRR(buildUnleveredCashFlows({
    purchasePrice,
    closingCosts,
    capex: vaTotalCost,
    noiByYear: projections.map(p => p.noi),
    exitGrossSale: saleProceeds.salePrice,
    sellingCostsPercent,
  }));

  // ─── Lender Sizing & Three-Axis Scoring ────────────────────────

  const sizing = maxSupportableLoan({
    purchasePrice,
    noi,
    maxLtvPercent: data.max_ltv_percent ?? 70,
    minDscr: data.min_dscr_covenant ?? 1.2,
    minDebtYield: data.min_debt_yield ?? 9,
    interestRate: data.interest_rate ?? 7,
    amortizationYears: data.amortization_years ?? 25,
  });
  const debtYield = calculateDebtYield(noi, loanAmount);

  // Was a second, parallel threshold binding. Collapsed 2026-07-31: the verdict
  // card scored on the profile while the decision tab, solver and PDF scored on
  // investment_strategy, so Bear Valley rendered NO-GO and GO at once. `thresholds`
  // above is now the single source and already resolves the profile first.
  const profileThresholds = thresholds;

  const suppressed = [
    ...(data.archetype ? ARCHETYPE_PRESETS[data.archetype].suppressedMetrics : []),
    ...(data.capital_structure ? STRUCTURE_SUPPRESSED_METRICS[data.capital_structure] : []),
  ];

  const score = scoreDeal(
    {
      cap_rate: capRate,
      cash_on_cash: avgCashOnCash,
      irr: irrValue,
      equity_multiple: equityMultiple,
      dscr,
    },
    profileThresholds,
    suppressed,
    data.open_items ?? [],
    { leaseUpMonths: noiInputs.leaseUpMonths }
  );

  /**
   * Phase 4A: does this deal fit the selected investor's buy box?
   *
   * Deliberately fed the SAME metric values the scorecard scores on - capRate,
   * avgCashOnCash, irrValue, equityMultiple, dscr, totalCashInvested - rather than
   * recomputing anything. A fit panel that disagreed with the verdict card two
   * inches above it would be occurrence #9 of this build's dominant defect class.
   *
   * Null when no investor profile is selected: a buy box needs a buyer, and scoring
   * against a strategy default would be presenting a preference nobody expressed.
   */
  const buyBoxFit = useMemo(() => {
    if (!activeProfile) return null;
    return matchDealToProfile(
      {
        id: property.id,
        label: property.address || 'This deal',
        cap_rate: capRate,
        avg_cash_on_cash: avgCashOnCash,
        irr: irrValue,
        equity_multiple: equityMultiple,
        dscr,
        total_cash_invested: totalCashInvested,
        archetype: data.archetype ?? null,
        market: property.market ?? property.submarket ?? null,
      },
      activeProfile
    );
  }, [activeProfile, property.id, property.address, property.market, property.submarket,
      capRate, avgCashOnCash, irrValue, equityMultiple, dscr, totalCashInvested, data.archetype]);


  // Stabilized NOI
  const sqft = property.building_size || 0;
  const stabilizedIncome = sqft * n(data.stabilized_rent_psf) * (n(data.stabilized_occupancy) / 100) + n(data.stabilized_other_income);
  const asIsIncome = sqft * n(data.as_is_rent_psf) * (n(data.as_is_occupancy) / 100) + n(data.as_is_other_income);
  const stabilizedExpenses = stabilizedIncome * (n(data.stabilized_expense_ratio) / 100);
  const asIsExpenses = asIsIncome * (n(data.as_is_expense_ratio) / 100);
  const stabilizedNOI = stabilizedIncome - stabilizedExpenses;
  const asIsNOI = asIsIncome - asIsExpenses;
  const noiLift = stabilizedNOI - asIsNOI;

  // ─── Client-facing summary lines ───────────────────────────────────────
  // Hoisted out of the VerdictCard JSX so the PDF prints the exact strings the
  // client just read on screen rather than the export's own paraphrase.
  const structureLine = `${formatCurrency(downPayment)} down @ ${formatCurrency(purchasePrice)} (${ltvPercent.toFixed(1)}% LTV)`;
  const caveat = score.rows.some(r => !r.pass) && score.verdict === 'GO'
    ? `Caveat: ${score.rows.filter(r => !r.pass).map(r => r.label).join(', ')} below threshold.`
    : null;

  // The corpus's one-line asset summary, e.g.
  // "SINGLE-TENANT NNN STNL | 19,300 SF | 100% Leased".
  const descriptor = data.archetype
    ? ARCHETYPE_PRESETS[data.archetype].descriptorTemplate
        .replace('{sf}', sqft > 0 ? sqft.toLocaleString() : 'N/A')
        .replace('{occupancy}', property.occupancy_rate != null ? `${property.occupancy_rate}%` : 'N/A')
        .replace('{units}', property.units != null ? `${property.units}` : 'N/A')
        .replace('{lotSize}', property.lot_size ? `${property.lot_size.toLocaleString()} SF Lot` : 'N/A')
    : [
        property.property_type?.replace(/_/g, ' ').toUpperCase(),
        sqft > 0 ? `${sqft.toLocaleString()} SF` : null,
      ].filter(Boolean).join(' | ');

  // Value Creation derived values
  const stabilizedValue = stabilizedNOI > 0 && exitCapRate > 0 ? stabilizedNOI / (exitCapRate / 100) : 0;
  const equityCreated = stabilizedValue - totalProjectCost;
  const profitOnSale = saleProceeds.netToSeller - totalCashInvested;
  const costToValue = stabilizedValue > 0 ? (totalProjectCost / stabilizedValue) * 100 : 0;

  // Comp suggestions
  // Same number, plus the comps behind it. linkedCompsToPoolRows is required:
  // buildExitCapEvidence reads cap_rate/price_per_sf, a Comp carries
  // comp_cap_rate/comp_price_per_sqft, and every field being optional means
  // tsc would not flag passing `comps` straight in - it would just return null.
  const linkedEvidence = buildExitCapEvidence(linkedCompsToPoolRows(comps));

  /**
   * Broker-curated comps win; the market pool is the fallback. Measured 2026-08-01:
   * of 34 properties, 11 have linked comps and NOT ONE linked comp anywhere carries
   * a cap rate, so today the pool is what actually backs this number.
   *
   * Both branches run through buildExitCapEvidence so the two sources cannot compute
   * the number differently. The pool rows arrive already filtered by the backend to
   * closed sales only, caps 3-12%, matching type, within 25 miles and 48 months, and
   * only when at least 5 of them exist.
   */
  const usingPool = linkedEvidence.supporting.length === 0 && !!poolExitCap?.capRate;
  const poolEvidence = buildExitCapEvidence(poolExitCap?.supporting ?? []);
  const exitCapEvidence = usingPool ? poolEvidence : linkedEvidence;
  const suggestedExitCap = usingPool ? poolEvidence.suggestedCap : suggestExitCapRate(comps);
  const psfBenchmark = benchmarkPricePerSqft(comps);

  // ─── Decision Logic ────────────────────────────────────────────

  // The metric list lives in scoreDeal. A second one here is what produced
  // defect #8, so it is deliberately NOT reintroduced.

  /**
   * Defect #8, fixed 2026-08-01. This used to be a SECOND verdict rule computed
   * over `decisionMetrics` (all-pass / >=3 / else) while `scoreDeal` returned GO
   * on a bare majority over its OWN filtered metric set. The two disagreed on
   * both the rule and the denominator, so Bear Valley at the $4.6M counter
   * rendered GO on the verdict card and REVIEW on the decision card at once.
   *
   * `score` is now the only scorer. The denominator comes from it too, because
   * suppressed metrics and null thresholds shrink the set - an all-cash deal
   * should read "4 of 4", never "4 of 5".
   */
  const verdict = score.verdict;
  const passCount = score.passed;
  const scoredTotal = score.total;

  // ─── Goal-Seek (Optimization) ─────────────────────────────────

  // The solved ltvPercent, sizing mode, contractual schedule and carryback all
  // have to reach the solvers, or the optimization table strikes its ceilings
  // against a different deal than the one the verdict card just scored. This is
  // the same override buildScenarioSet already gets below.
  const goalSeekInputs = useMemo(() => ({
    purchasePrice,
    initialNOI: noi,
    // ⭐⭐ JJ2: the NOI inputs from the one builder - the solver used to omit the lease-up and Prop 13.
    ...noiInputs,
    loanAmount,
    interestRate: n(data.interest_rate),
    amortizationYears: n(data.amortization_years),
    holdingPeriod,
    exitCapRate,
    sellingCostsPercent,
    totalCashInvested,
    closingCostsPercent: n(data.closing_costs_percent),
    ltvPercent,
    buildingSqft: sqft,
    expenseRatio: totalExpenses / (egi || 1) * 100,
    capitalCostsByYear: appliedCapitalCosts,
    exitNOIOverride: exitNOI,
    sizingMode: data.sizing_mode,
    downPaymentTarget: data.down_payment_target,
    secondaryLoan: secondNote,
    // ⛔ The solver strikes its ceilings against the deal the verdict card just scored, and a fee
    // that raises cash by $40,990 moves every one of them.
    financingFee,
    statedExitValue,
  }), [purchasePrice, noi, noiInputs, egi, totalExpenses, loanAmount, data.interest_rate, data.amortization_years, holdingPeriod, exitCapRate, sellingCostsPercent, totalCashInvested, data.closing_costs_percent, ltvPercent, sqft, exitNOI, data.sizing_mode, data.down_payment_target, sellerAnnualDS, secondNote, financingFee, statedExitValue]);

  const optimization = useMemo(() => {
    const targetIRR = thresholds.irr;
    if (purchasePrice <= 0 || egi <= 0) return null;
    const reqNOI = solveRequiredNOILift(targetIRR, goalSeekInputs);
    return {
      maxPurchasePrice: solveMaxPurchasePrice(targetIRR, goalSeekInputs),
      requiredNOI: reqNOI,
      // Use the required NOI (not current NOI) to compute the rent PSF needed
      requiredRentPSF: reqNOI > 0
        ? solveRequiredRentPSF(reqNOI, sqft, totalExpenses / (egi || 1) * 100)
        : 0,
      capexCeiling: solveCapexCeiling(targetIRR, goalSeekInputs),
      targetExitCap: solveTargetExitCap(targetIRR, goalSeekInputs),
    };
  }, [goalSeekInputs, thresholds.irr, purchasePrice, egi, noi, sqft, totalExpenses]);

  // ─── Sensitivity Analysis ──────────────────────────────────────

  const scenarioBase: ScenarioInputs = useMemo(() => ({
    purchasePrice,
    pgi,
    vacancyRate: n(data.vacancy_rate),
    otherIncome: n(data.other_income),
    nnnReimbursements,
    totalExpenses,
    ltvPercent: n(data.ltv_percent),
    interestRate: n(data.interest_rate),
    amortizationYears: n(data.amortization_years),
    closingCostsPercent: n(data.closing_costs_percent),
    vaTotalCost,
    // ⭐⭐ JJ2: the NOI inputs from the one builder - lease-up, Prop 13, carry and schedule. The
    // scenario cards used to omit the first two and drift from the headline on a lease-up deal.
    ...noiInputs,
    holdingPeriod,
    exitCapRate,
    sellingCostsPercent,
    secondaryLoan: secondNote,
    // ⛔ Without it the scenario cards price a deal whose cash requirement differs from the
    // verdict card's by the whole fee, and report a different IRR for it on the same screen.
    financingFee,
    statedExitValue,
  }), [purchasePrice, pgi, data.vacancy_rate, data.other_income, nnnReimbursements, totalExpenses, data.ltv_percent, data.interest_rate, data.amortization_years, data.closing_costs_percent, vaTotalCost, noiInputs, holdingPeriod, exitCapRate, sellingCostsPercent, sellerAnnualDS, secondNote, financingFee, statedExitValue]);

  // Three cases side by side. `scenarioBase.ltvPercent` reads data.ltv_percent,
  // which is NOT the equity-first LTV solved above — without this override an
  // equity-first deal would model different leverage here than in the verdict card.


  /**
   * ⛔⛔ THE BASE EVERY SCENARIO CONSUMER MUST USE. `scenarioBase` above is RAW - it carries
   * `data.ltv_percent` rather than the sized LTV, and no exit-NOI override, NOI schedule or
   * capital costs. `exitNOI` is the figure the verdict card prices its exit on (the OM's
   * contractual stabilized NOI on an NNN deal); without it the base leg re-derives a forward NOI
   * from growth and reports a materially different IRR for the same deal.
   *
   * 🚨 THAT IS NOT HYPOTHETICAL. The scenario builder was wired to the raw `scenarioBase` and
   * printed a base IRR of **-1.15%** on screen while the app's own Base card, two panels away,
   * read **6.92%** for the same deal. Found by opening the app; every unit test passed, because
   * they compared this path against itself. Resolving it ONCE is what stops it recurring.
   */
  const scenarioBaseResolved: ScenarioInputs = useMemo(() => ({
    ...scenarioBase,
    ltvPercent,
    capitalCostsByYear: appliedCapitalCosts,
    // The NOI schedule already rides in through `noiInputs` on the base - one source, not two.
    exitNOIOverride: exitNOI,
  }), [scenarioBase, ltvPercent, appliedCapitalCosts, exitNOI]);

  /**
   * The proposed levers, run through the SAME engine and the SAME base the sensitivity tables
   * use. ⛔ Recomputed whenever the deal moves, because a scenario cached against edited inputs
   * would print figures from a deal that no longer exists.
   */
  const scenarioOutcome = useMemo(
    () => (scenario && scenario.assumptions.length > 0 ? runScenario(scenarioBaseResolved, scenario.assumptions) : null),
    [scenario, scenarioBaseResolved]
  );

  const scenarios = useMemo(() => buildScenarioSet(
    scenarioBaseResolved,
    (data.scenario_overrides ?? {}) as Partial<ScenarioDeltas>
  ), [scenarioBaseResolved, data.scenario_overrides]);

  const sensitivityScenarios = useMemo(() => {
    if (purchasePrice <= 0 || pgi <= 0) return [];
    const defs = [
      { label: 'Price -10%', priceMod: -10, pgiMod: 0 },
      { label: 'Price -5%', priceMod: -5, pgiMod: 0 },
      { label: 'Base Case', priceMod: 0, pgiMod: 0 },
      { label: 'Price +5%', priceMod: 5, pgiMod: 0 },
      { label: 'Price +10%', priceMod: 10, pgiMod: 0 },
      { label: 'Rent -10%', priceMod: 0, pgiMod: -10 },
      { label: 'Rent +10%', priceMod: 0, pgiMod: 10 },
      { label: 'Rent +20%', priceMod: 0, pgiMod: 20 },
    ];
    return defs.map(d => ({
      ...d,
      result: computeScenario(scenarioBase, d.priceMod, d.pgiMod),
    }));
  }, [scenarioBase, purchasePrice, pgi]);

  // ─── Handlers ──────────────────────────────────────────────────

  /**
   * Gates the debounced autosave so it fires for USER edits only.
   *
   * `data` changes for three reasons that are not edits: the component mounts,
   * the async `initialData` sync lands, and the two derived-default effects fill
   * blanks from the property and the pro forma. The autosave effect keys on
   * `[data]`, so without this gate a mere page view rewrites the saved row - and
   * because several defaults are derived (`exit_cap_rate` falls back to
   * `suggestExitCapRate(comps)`), it can persist a computed value over a saved
   * null with nobody touching anything. Measured 2026-08-01: opening Bear Valley
   * with zero interaction moved its `updated_at`.
   *
   * The explicit Save button calls `handleSave` directly and is unaffected.
   */
  const userEditedRef = useRef(false);
  const markUserEdit = useCallback(() => { userEditedRef.current = true; }, []);

  /**
   * Writes the contractual schedule into `noi_overrides`. Explicit, never automatic:
   * it changes the priced NOI on every surface, so it has to be a deliberate act and
   * has to register as a user edit for the autosave gate.
   */
  const applyLeaseSchedule = useCallback(() => {
    markUserEdit();
    setData(prev => ({ ...prev, noi_overrides: leaseSchedule.noiSchedule as number[] }));
  }, [leaseSchedule.noiSchedule, markUserEdit]);

  /** Sets one rollover assumption. Creates the object on first edit, which is what
   *  switches the engine from "refuse to guess" to "model the rollover". */
  const setRolloverField = useCallback((field: string, value: string) => {
    markUserEdit();
    setData(prev => ({
      ...prev,
      rollover_assumptions: {
        // Defaults on first touch: a 65% renewal, 6 months down and 3 months free are
        // ordinary retail assumptions, and they are visible and editable rather than buried.
        renewalProbability: 65, downtimeMonths: 6, freeRentMonths: 3, marketRentPerSf: 0,
        ...(prev.rollover_assumptions ?? {}),
        [field]: value === '' ? undefined : Number(value),
      },
    }));
  }, [markUserEdit]);

  const disableRollover = useCallback(() => {
    markUserEdit();
    setData(prev => ({ ...prev, rollover_assumptions: null }));
  }, [markUserEdit]);

  // ─── Phase 3C: model versions ──────────────────────────────────
  const [versions, setVersions] = useState<Array<{ id: string; label: string; note: string | null; metrics: Record<string, unknown> | null; created_at: string }>>([]);
  const [versionLabel, setVersionLabel] = useState('');
  const [versionBusy, setVersionBusy] = useState(false);
  const [diffPair, setDiffPair] = useState<{ a: string; b: string }>({ a: '', b: '' });
  const [diffResult, setDiffResult] = useState<ReturnType<typeof diffVersions> | null>(null);

  const loadVersions = useCallback(async () => {
    try {
      const r = await api.listAnalysisVersions(property.id);
      setVersions((r as { versions: typeof versions }).versions ?? []);
    } catch { /* a missing version list must never break the analyzer */ }
  }, [property.id]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  /**
   * Snapshots the CURRENT inputs and the metrics as they read right now. Both are stored
   * verbatim and never recomputed - the point of a version is to record what was put in
   * front of a client, not what a later engine would produce from the same inputs.
   */
  const saveVersion = useCallback(async () => {
    if (!versionLabel.trim()) return;
    setVersionBusy(true);
    try {
      await api.createAnalysisVersion(property.id, {
        label: versionLabel.trim(),
        snapshot: data as unknown as Record<string, unknown>,
        metrics: {
          irr: irrValue, equity_multiple: equityMultiple, avg_cash_on_cash: avgCashOnCash,
          dscr, cap_rate: capRate, purchase_price: purchasePrice,
          verdict: score.verdict, passed: score.passed, total: score.total,
        },
      });
      setVersionLabel('');
      await loadVersions();
    } finally {
      setVersionBusy(false);
    }
  }, [versionLabel, property.id, data, irrValue, equityMultiple, avgCashOnCash, dscr, capRate, purchasePrice, score, loadVersions]);

  const runDiff = useCallback(async () => {
    if (!diffPair.a || !diffPair.b || diffPair.a === diffPair.b) return;
    setVersionBusy(true);
    try {
      const [ra, rb] = await Promise.all([
        api.getAnalysisVersion(property.id, diffPair.a),
        api.getAnalysisVersion(property.id, diffPair.b),
      ]);
      const va = (ra as { version: VersionSnapshot }).version;
      const vb = (rb as { version: VersionSnapshot }).version;
      setDiffResult(diffVersions(va, vb));
    } finally {
      setVersionBusy(false);
    }
  }, [diffPair, property.id]);

  const clearLeaseSchedule = useCallback(() => {
    markUserEdit();
    setData(prev => ({ ...prev, noi_overrides: null }));
  }, [markUserEdit]);

  /**
   * Open items are an ARRAY, so they cannot go through `handleChange` - that helper coerces a
   * string value to a number for every field outside its exception list, which would turn the
   * list into NaN. One item per line; blank lines are dropped so a trailing newline while
   * typing does not read as an outstanding item and flip the verdict to CONDITIONAL.
   */
  const handleOpenItemsChange = useCallback((text: string) => {
    markUserEdit();
    setData(prev => ({ ...prev, open_items: text.split('\n').map(l => l.trim()).filter(Boolean) }));
  }, []);

  const handleChange = useCallback((field: string, value: string | number | boolean) => {
    markUserEdit();
    // Record the edit as the user's own, which is what makes applyPreset refuse
    // to overwrite it when the archetype is re-picked.
    setFieldSources(prev => (sourceOf(prev[field]) === 'user' ? prev : { ...prev, [field]: 'user' }));
    setData(prev => {
      const updates: Partial<DealAnalysis> = {
        // ⚠️ `close_date` MUST be in this exception list. Without it `parseNumberInput` strips the
        // hyphens and "2026-11-25" becomes 2026.1125 - a date silently turned into a number, which
        // is exactly the class of thing that reaches a client document unnoticed.
        // ⭐ `financing_fee_note` is caught by the `includes('note')` clause already, and that is
        // luck rather than design - it is named explicitly so a rename cannot silently turn
        // "secondary" into 0 and move a financed fee onto the wrong note.
        [field]: typeof value === 'string' && field !== 'investment_strategy' && field !== 'lease_type' && !field.includes('note') && field !== 'notes' && field !== 'close_date' && field !== 'financing_fee_note'
          ? parseNumberInput(value)
          : value,
      };
      // Clear auto flag when user manually edits an auto-filled expense field
      if (field === 'property_taxes') updates.auto_property_taxes = false;
      if (field === 'insurance') updates.auto_insurance = false;
      if (field === 'utilities') updates.auto_utilities = false;
      return { ...prev, ...updates };
    });
  }, []);

  /**
   * The single entry point for the three axes, whether they came from the
   * parsed brief or from a hand-picked control. Picking an archetype applies
   * its preset through applyPreset, which refuses to touch any field whose
   * recorded source is 'user' — so hand-edited numbers survive a re-pick.
   */
  const handleAxisChange = useCallback((next: Partial<BriefAxes>, origin: 'brief' | 'user') => {
    // Both origins are user-initiated: they either typed the brief or picked a
    // control. Either way the resulting change should persist.
    markUserEdit();
    setData(prev => {
      let merged: Partial<DealAnalysis> = { ...prev, ...next };
      let sources = { ...fieldSources };

      Object.keys(next).forEach(k => { sources[k] = origin; });

      if (next.archetype) {
        const preset = ARCHETYPE_PRESETS[next.archetype];
        const applied = applyPreset(merged, preset.defaults, sources, 'archetype_preset');
        merged = applied.data;
        sources = applied.sources;
        if (!next.investor_profile_id) {
          const match = profiles.find(p => p.profile_type === preset.defaultProfile);
          if (match) { merged.investor_profile_id = match.id; sources.investor_profile_id = 'archetype_preset'; }
        }
      }

      if (next.capital_structure) {
        merged.sizing_mode = next.capital_structure;
        if (next.capital_structure === 'all_cash') {
          merged.loan_amount = 0;
          merged.ltv_percent = 0;
        }
      }

      setFieldSources(sources);
      return merged;
    });
  }, [fieldSources, profiles]);

  const SOURCE_LABEL: Record<string, string> = {
    archetype_preset: 'Set by the archetype preset',
    investor_profile: "From the investor's profile",
    brief: 'Read from your brief',
    property_record: 'From the property record',
    user: 'Your entry',
  };

  function SourceDot({ field }: { field: string }) {
    const src = sourceOf(fieldSources[field]);
    if (!src) return null;
    const isUser = src === 'user';
    return (
      <span
        title={SOURCE_LABEL[src] ?? src}
        className={`inline-block w-1.5 h-1.5 rounded-full ml-1.5 align-middle ${isUser ? 'bg-gray-400' : 'bg-amber-400'}`}
      />
    );
  }

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave({
        ...data,
        va_total_cost: vaTotalCost,
        total_project_cost: totalProjectCost,
        irr: irrValue,
        equity_multiple: equityMultiple,
        avg_cash_on_cash: avgCashOnCash,
        seller_carryback_monthly_payment: sellerMonthlyPmt,
        seller_annual_debt_service: sellerAnnualDS,
        // Do NOT "simplify" the line below into an unconditional spread of all four figures - see
        // `persistableAnalysisFigures` above for why: one invalid value there (a NaN or >100%
        // cap rate, a negative expense total) would 400 this ENTIRE autosave and silently drop
        // every field above, including the ones that always validate. Each figure is omitted on
        // its own, never clamped, when it would not pass the backend's own check.
        ...persistableAnalysisFigures({ egi, totalExpenses, noi, capRate }),
        // Provenance has to round-trip, or every reload reports the whole
        // analysis as hand-entered and presets start overwriting real edits.
        field_sources: fieldSources,
      });
    }
  }, [onSave, data, vaTotalCost, totalProjectCost, irrValue, equityMultiple, avgCashOnCash, sellerMonthlyPmt, sellerAnnualDS, egi, totalExpenses, noi, capRate, fieldSources]);

  // Auto-save debounce. Gated on a real user edit - see userEditedRef above.
  // Without the gate this fires on mount and on every derived-default write, so
  // simply opening a property rewrites its saved analysis.
  useEffect(() => {
    if (!onSave) return;
    if (!userEditedRef.current) return;
    const timer = setTimeout(() => {
      handleSave();
    }, 2000);
    return () => clearTimeout(timer);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Generate Key Considerations ───────────────────────────────
  const handleGenerateKeyConsiderations = useCallback(async () => {
    setKeyConsiderationsLoading(true);
    setKeyConsiderationsError(null);
    setKeyConsiderationsReview(null);
    try {
      const add = property.additional_data as Record<string, any> | undefined;
      const result = await api.dealAdvisorKeyConsiderations({
        address: property.address || '',
        city: property.city || '',
        state: property.state || '',
        property_type: property.property_type || 'industrial',
        building_size: property.building_size || 0,
        year_built: property.year_built,
        lot_size: property.lot_size,
        purchase_price: purchasePrice,
        noi,
        cap_rate: capRate,
        price_per_sqft: pricePerSqft,
        irr: irrValue,
        dscr,
        equity_multiple: equityMultiple,
        avg_cash_on_cash: avgCashOnCash,
        before_tax_cash_flow: beforeTaxCF,
        gross_income: pgi,
        vacancy_rate: n(data.vacancy_rate),
        egi,
        total_expenses: totalExpenses,
        expense_reimbursements: nnnReimbursements,
        lease_type: leaseType,
        occupancy_rate: property.occupancy_rate,
        tenants: add?.tenants || [],
        expenses: add?.expenses || [],
        // ⛔ `reconciliation.warnings`, not `reconciliation_warnings` - the latter is a key
        // nothing has ever written, so the narrative has never seen a single warning.
        reconciliation_warnings: (add?.reconciliation?.warnings ?? []).map((w: any) => w?.message).filter(Boolean),
        pro_forma: add?.pro_forma_financials || null,
        investment_strategy: strategy,
        holding_period: n(data.holding_period) || 5,
        exit_cap_rate: n(data.exit_cap_rate) || 6,
        market_highlights: add?.market_highlights || [],
        property_highlights: add?.property_highlights || [],
      });
      setKeyConsiderations(result.narrative);
      setKeyConsiderationsReview(result.review ?? null);
    } catch (err: any) {
      setKeyConsiderationsError(err.message || 'Failed to generate analysis');
    } finally {
      setKeyConsiderationsLoading(false);
    }
  }, [property, purchasePrice, noi, capRate, pricePerSqft, irrValue, dscr, equityMultiple, avgCashOnCash, beforeTaxCF, pgi, data.vacancy_rate, egi, totalExpenses, nnnReimbursements, leaseType, strategy, data.holding_period, data.exit_cap_rate]);

  const handleBuildScenario = useCallback(async () => {
    setScenarioLoading(true);
    setScenarioError(null);
    setScenario(null);
    try {
      const r = await api.buildScenario({
        risk_text: riskText,
        city: property.city || undefined,
        property_type: property.property_type || undefined,
        holding_period: n(data.holding_period) || 5,
      });
      setScenario(r.scenario);
      setScenarioFacts(r.facts || []);
    } catch (err: any) {
      // A refusal is the software stopping, never Joseph - the message names what it could not run.
      setScenarioError(err?.message || 'Could not build the scenario');
    } finally {
      setScenarioLoading(false);
    }
  }, [riskText, property.city, property.property_type, data.holding_period]);

  // ─── Apply Optimization Lever ───────────────────────────────────

  const applyOptimization = useCallback((lever: string) => {
    if (!optimization) return;
    switch (lever) {
      case 'price':
        handleChange('purchase_price', optimization.maxPurchasePrice);
        break;
      case 'noi': {
        const vacRate = n(data.vacancy_rate) / 100;
        const targetPGI = (optimization.requiredNOI + totalExpenses) / (1 - vacRate);
        handleChange('potential_gross_income', Math.round(targetPGI));
        break;
      }
      case 'rent':
        if (sqft > 0) handleChange('potential_gross_income', Math.round(optimization.requiredRentPSF * sqft));
        break;
      case 'capex': {
        // The other levers route through handleChange, which marks the edit; this
        // branch writes data directly, so it has to mark it too.
        markUserEdit();
        const currentTotal = vaTotalCost || 1;
        const scale = optimization.capexCeiling / currentTotal;
        setData(prev => ({
          ...prev,
          va_capex: Math.round(n(prev.va_capex) * scale),
          va_ti_leasing: Math.round(n(prev.va_ti_leasing) * scale),
          va_carry_costs: Math.round(n(prev.va_carry_costs) * scale),
          va_contingency: Math.round(n(prev.va_contingency) * scale),
        }));
        break;
      }
      case 'exitcap':
        handleChange('exit_cap_rate', optimization.targetExitCap);
        break;
    }
  }, [optimization, handleChange, data.vacancy_rate, totalExpenses, sqft, vaTotalCost, n]);

  // Confirmation state for apply/fix actions
  const [confirmAction, setConfirmAction] = useState<{ lever: string; label: string } | null>(null);

  // ─── Tab Config ────────────────────────────────────────────────

  const tabs: { id: TabType; label: string }[] = [
    { id: 'summary', label: 'Deal Summary' },
    { id: 'strategy', label: 'Strategy' },
    { id: 'valueadd', label: 'Value-Add' },
    { id: 'proforma', label: 'Proforma' },
    { id: 'exit', label: 'Exit Analysis' },
    { id: 'decision', label: 'Decision' },
    { id: 'optimization', label: 'Optimization' },
    { id: 'advisor', label: 'AI Advisor' },
  ];

  // ─── Format helpers ────────────────────────────────────────────

  const fmtMetric = (val: number, format: 'percent' | 'ratio' | 'currency') => {
    if (format === 'percent') return formatPercent(val);
    if (format === 'ratio') return formatRatio(val);
    return formatCurrency(val);
  };

  // ─── Render ────────────────────────────────────────────────────

  return (
    <Card variant="elevated">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <CardTitle>Investment Analysis</CardTitle>
              <p className="text-sm text-gray-500">Investment decision engine &amp; pro forma</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Verdict badge */}
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
              verdict === 'GO' ? 'bg-green-100 text-green-800' :
              isAmberVerdict(verdict) ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {verdict}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                // Review finding 2: checked SYNCHRONOUSLY, before any `await` below, and set
                // immediately after - this ref is what actually stops a second click fired
                // while `api.checkAnalysis` (a language-model call, seconds long) is still in
                // flight. `exportChecking` (on the Button below) only drives what the button
                // looks like; this is what makes a second invocation a no-op.
                if (exportCheckInFlightRef.current) return;
                // Round 2 finding 7: the ref above only covers the REQUEST itself - it is cleared
                // in `finally` below as soon as `api.checkAnalysis` settles, which is BEFORE the
                // panel showing that result has been resolved by the user. A second click landing
                // in that window (the panel open, waiting on Dismiss/Continue) sailed past the ref
                // and fired a second - billed - model call. `analysisCheck` is non-null for
                // exactly that window, so it needs no ref of its own: the panel has to already be
                // on screen for anyone to click through it again, and by then this state has long
                // since committed.
                if (analysisCheck) return;
                exportCheckInFlightRef.current = true;
                setExportChecking(true);
                // Refresh starred insights before generating PDF
                let insights = aiInsights;
                try {
                  const res = await api.dealAdvisorStarredMessages(property.id);
                  insights = res.messages || [];
                  setAiInsights(insights);
                } catch {}
                // The PDF call stays INLINE inside this closure, exactly as it was before T6,
                // rather than being lifted into a separately-named options object: two source
                // guards elsewhere (pdfBranding.manchester.test.ts, scheduleAnchors.test.ts) read
                // this exact call site's source text to confirm the export still runs on the
                // screen's own resolved scenario base - a real invariant, not incidental text.
                // Lifting the call's argument into a variable moved that text out of the window
                // those guards read and broke both; keeping it inline here keeps them honest.
                // CRITICAL 2 (final whole-branch review, 2026-09-19): `checkFailureNote`, when
                // given, is prepended to the export's own outcome note - see the catch block
                // below. Without it, `setExportNote` at the end of this function would OVERWRITE
                // whatever the catch block set beforehand the moment the export finished, and the
                // one fact that mattered (that the check itself never ran) would disappear.
                const runExport = async (checkFailureNote?: string) => {
                  const outcome = await generateInvestmentAnalysisPDF({
                    property,
                    data,
                    comps,
                    // Score the export against the same bar the screen used.
                    thresholds,
                    thresholdLabel,
                    branding: user ? {
                      company_name: user.company_name || undefined,
                      company_logo_url: user.company_logo_url || undefined,
                      company_phone: user.company_phone || undefined,
                      company_email: user.company_email || undefined,
                      company_address: user.company_address || undefined,
                    } : undefined,
                    photoBase64,
                    aiInsights: insights,
                    keyConsiderations: keyConsiderations || undefined,
                    openItems: data.open_items ?? [],
                    scenario: scenario ? {
                      title: scenario.title,
                      assumptions: scenario.assumptions,
                      rationale: scenario.rationale,
                      notEstablished: scenario.notEstablished,
                    } : undefined,
                    // The export has to report what this screen reports. Passing
                    // the scored rows, the exit NOI and the contractual NOI
                    // schedule is what keeps the printed IRR and the on-screen
                    // IRR from disagreeing in front of a client.
                    scoreRows: score.rows,
                    verdict: score.verdict,
                    passed: score.passed,
                    total: score.total,
                    profileName: thresholdLabel,
                    structureLine,
                    caveat,
                    sizing,
                    descriptor,
                    noiOverrides: data.noi_overrides ?? undefined,
                    capitalCostsByYear: appliedCapitalCosts,
                    exitNOI,
                    exitNoiSource: exitNOIResolved.source,
                    // ⭐⭐ KK2: the document prices its sensitivities and scenario on THIS base - the
                    // one the cards and the scenario builder on this screen read.
                    scenarioBase: scenarioBaseResolved,
                  });
                  const outcomeNote = describeOutcome(outcome);
                  setExportNote(checkFailureNote ? `${checkFailureNote} ${outcomeNote}` : outcomeNote);
                };
                // T6 - the analysis checker. Read-only report before the PDF actually generates;
                // it never blocks and a broken check must not stop him sending a PDF, so a failed
                // check logs and exports anyway rather than surfacing an error here.
                //
                // CRITICAL 1 (final whole-branch review, 2026-09-19): sends the figures CURRENTLY
                // on screen, not just whatever was last saved - `data.purchase_price`/
                // `data.potential_gross_income` are the live values this export is about to
                // print. Autosave is a 2-second debounce; edit a figure and export inside that
                // window and the saved-row fallback used to name the OM, quote a page and
                // confidently report on a number that was not the one leaving the building.
                try {
                  const result = await api.checkAnalysis(property.id, {
                    purchase_price: data.purchase_price ?? null,
                    gross_income: data.potential_gross_income ?? null,
                  });
                  pendingExportRef.current = runExport;
                  setAnalysisCheck(result);
                } catch (err) {
                  // CRITICAL 2 (final whole-branch review, 2026-09-19): the one fact that
                  // mattered - that no check happened - used to be the fact that disappeared: this
                  // logged and exported with no panel at all. `runExport`'s own note still reports
                  // where the PDF went; this preamble makes sure the screen also says plainly that
                  // the check itself never ran, rather than exporting as if nothing happened.
                  console.error('[AnalysisCheck] check failed, exporting anyway:', err);
                  await runExport('The analysis check could not run, so this export was not checked against the source document.');
                } finally {
                  // Review finding 2: the in-flight window ends here whether the check
                  // succeeded (the panel is now showing) or failed (the fallback export above
                  // just ran) - either way this button's own async work is done.
                  exportCheckInFlightRef.current = false;
                  setExportChecking(false);
                }
              }}
              isLoading={exportChecking}
              disabled={exportChecking}
            >
              {!exportChecking && (
                <svg className="w-4 h-4 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              {exportChecking ? 'Checking...' : 'Export PDF'}
            </Button>
            {onSave && (
              <Button size="sm" onClick={handleSave} isLoading={saving} disabled={saving}>
                Save
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              <svg className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Button>
          </div>
        </div>
        {exportNote && (
          <p className="px-6 pb-2 text-xs text-gray-600" data-testid="export-note">{exportNote}</p>
        )}
        {/* T6 - shown before the PDF actually generates. Rendered here, OUTSIDE `{expanded &&
            <CardContent>}` below, so it stays visible even when the card is collapsed - the
            Export PDF button that triggers it lives in this header regardless of `expanded`. */}
        {analysisCheck && (
          <AnalysisCheckPanel
            check={analysisCheck}
            onProceed={() => {
              setAnalysisCheck(null);
              pendingExportRef.current?.();
            }}
            onDismiss={() => {
              // Review finding 3: closes the panel and returns to the analysis WITHOUT
              // exporting - no call to `pendingExportRef.current`, which is the entire point.
              setAnalysisCheck(null);
              pendingExportRef.current = null;
            }}
          />
        )}
      </CardHeader>

      {expanded && (
        <CardContent>
          {/* ⭐⭐⭐ EXTRACTION WARNINGS, ABOVE EVERYTHING. Joseph's ruling, 2026-08-31.
              A ten-property portfolio OM produced one property and said so only in a prose notes
              field nothing reads, so a broker who wanted a different building had no way to know.
              These are SCOPE facts about what was modelled - they belong before the first number,
              not beside it. ⛔ Read from `reconciliation.warnings`, which is where the guard
              actually writes; the old `reconciliation_warnings` key was never written by anything. */}
          {extractionWarnings.length > 0 && (
            <div className="mb-5 space-y-2">
              {extractionWarnings.map((w, i) => (
                <div
                  key={i}
                  className={`rounded-lg border p-3 ${
                    w.severity === 'error' ? 'bg-red-50 border-red-300'
                      : w.severity === 'warning' ? 'bg-amber-50 border-amber-300'
                      : 'bg-gray-50 border-gray-300'
                  }`}
                >
                  <p className={`text-xs font-semibold uppercase tracking-wide ${
                    w.severity === 'error' ? 'text-red-800'
                      : w.severity === 'warning' ? 'text-amber-800' : 'text-gray-700'
                  }`}>
                    {w.field === 'portfolio' ? 'This document describes more than one property' : `Extraction - ${w.field}`}
                  </p>
                  <p className={`text-xs mt-1 ${
                    w.severity === 'error' ? 'text-red-900'
                      : w.severity === 'warning' ? 'text-amber-900' : 'text-gray-800'
                  }`}>{w.message}</p>
                </div>
              ))}
            </div>
          )}

          {/*
            ⭐⭐⭐ THE QUICK SCREEN, under the warnings and above everything else. His ask: "a way to
            easily put in key information that can basically tell us if deal is good or not and we
            can always adjust after that."

            ⛔ It edits the ANALYZER'S OWN FIELDS through the same `handleChange`, and every figure
            it prints is computed above and passed down. No second NOI, no second cap rate - two
            models of one deal is how 13368 Main Street came to show 7.46% against a stated 5.75%.
          */}
          <QuickScreen
            values={{
              purchase_price: data.purchase_price,
              potential_gross_income: data.potential_gross_income,
              vacancy_rate: data.vacancy_rate,
              property_taxes: data.property_taxes,
              insurance: data.insurance,
              repairs_maintenance: data.repairs_maintenance,
              management_fee_percent: data.management_fee_percent,
            }}
            computed={{
              pgi,
              vacancyAmount,
              otherIncome: n(data.other_income),
              reimbursements: nnnReimbursements,
              totalExpenses,
              // ⛔ The SAME NOI the cap rate is struck from, or the panel would foot to a
              // different answer than the number beside it.
              noi: noiRebase.inPlaceNoi,
              capRate,
              purchasePrice,
            }}
            onChange={handleChange}
            renderSourceDot={(f) => <SourceDot field={f} />}
          />

          {/* Three-axis deal description — drives sizing, presets and thresholds */}
          <DealBriefStrip
            value={{
              archetype: data.archetype ?? null,
              holding_period: data.holding_period ?? null,
              exit_cap_rate: data.exit_cap_rate ?? null,
              capital_structure: data.capital_structure ?? null,
              down_payment_target: data.down_payment_target ?? null,
              investor_profile_id: data.investor_profile_id ?? null,
              is_1031: data.is_1031 ?? false,
            }}
            profiles={profiles}
            onChange={handleAxisChange}
          />

          {/* Tabs */}
          <div className="flex border-b mb-6 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Persistent metrics strip — visible on every tab */}
          <div className="flex items-center gap-4 bg-gray-50 border rounded-lg px-4 py-2 mb-4 text-sm overflow-x-auto">
            {[
              { label: capCaption.capRate, value: formatPercent(capRate), pass: thresholds.cap_rate === null || capRate >= thresholds.cap_rate },
              { label: 'Avg CoC', value: formatPercent(avgCashOnCash), pass: avgCashOnCash >= thresholds.cash_on_cash },
              { label: 'IRR', value: formatPercent(irrValue), pass: irrValue >= thresholds.irr },
              { label: 'Eq. Mult', value: formatRatio(equityMultiple), pass: equityMultiple >= thresholds.equity_multiple },
              { label: 'DSCR', value: formatRatio(dscr), pass: dscr >= thresholds.dscr },
            ].map(m => (
              <div key={m.label} className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-gray-500 text-xs">{m.label}</span>
                <span className={`font-bold ${m.pass ? 'text-green-600' : 'text-red-600'}`}>{m.value}</span>
              </div>
            ))}
            <div className="ml-auto flex-shrink-0">
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                verdict === 'GO' ? 'bg-green-100 text-green-800' :
                isAmberVerdict(verdict) ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>{verdict}</span>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════
              TAB 1: Deal Summary (read-only overview)
             ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'summary' && (
            <div className="space-y-6">
              {/* Verdict against the active investor profile */}
              <VerdictCard
                verdict={score.verdict}
                passed={score.passed}
                total={score.total}
                profileName={thresholdLabel}
                structureLine={structureLine}
                caveat={caveat}
              />
              {data.capital_structure !== 'all_cash' && (
                <LoanSizingCard requested={loanAmount} sizing={sizing} />
              )}
              <DecisionScorecard rows={score.rows} profileName={thresholdLabel} />
              {/* NB1 (the 122nd): the market benchmark BESIDE the scorecard, never part of it - its thresholds are untouched.
                  It reads the same in-place NOI the going-in cap is struck on, and the bank loan's own debt service. */}
              {benchmarkTenant && (
                <BenchmarkPanel tenantName={benchmarkTenant.tenant_name} leaseEnd={benchmarkTenant.lease_end} noi={noiRebase.inPlaceNoi}
                  price={purchasePrice} leaseType={benchmarkTenant.lease_type} yearBuilt={property.year_built ?? null}
                  landSf={Number(property.lot_size) || null} loanAmount={loanAmount} annualDebtService={bankAnnualDS} />
              )}

              {/* Investor fit (Phase 4A) */}
              {buyBoxFit && (
                <div className={`mt-4 rounded-xl p-5 border ${
                  buyBoxFit.verdict === 'strong' ? 'bg-green-50 border-green-300'
                  : buyBoxFit.verdict === 'partial' ? 'bg-amber-50 border-amber-300'
                  : 'bg-red-50 border-red-300'
                }`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <h4 className="text-sm font-semibold uppercase text-gray-800">
                      {activeProfile?.name} buy box
                    </h4>
                    <span className="text-sm font-semibold">
                      {buyBoxFit.fits.length} of {buyBoxFit.total} criteria met
                      {' - '}
                      {buyBoxFit.verdict === 'strong' ? 'strong fit'
                        : buyBoxFit.verdict === 'partial' ? 'partial fit' : 'poor fit'}
                    </span>
                  </div>

                  {/* Misses first: what stops this deal is the useful half. */}
                  {buyBoxFit.misses.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {buyBoxFit.misses.map(m => (
                        <li key={m.label} className="text-xs text-red-800 flex flex-wrap gap-x-2">
                          <span className="font-medium">{m.label}</span>
                          <span>{m.reason}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {buyBoxFit.fits.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {buyBoxFit.fits.map(f => (
                        <li key={f.label} className="text-xs text-gray-600 flex flex-wrap gap-x-2">
                          <span className="font-medium text-gray-700">{f.label}</span>
                          <span>{f.reason}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <p className="text-xs text-gray-500 mt-3">
                    Scored against the same figures as the scorecard above, plus cheque size,
                    asset type and market from {activeProfile?.name}'s profile.
                  </p>
                </div>
              )}

              {/* Property Info */}
              <div className="bg-gray-900 text-white rounded-xl p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold">{property.address || 'Untitled Property'}</h3>
                    <p className="text-gray-400 text-sm">{[property.city, property.state, property.zip_code].filter(Boolean).join(', ')}</p>
                    <p className="text-gray-400 text-sm mt-1">{property.property_type?.replace('_', ' ')} {property.building_size ? `| ${property.building_size.toLocaleString()} SF` : ''}</p>
                  </div>
                  <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${
                    verdict === 'GO' ? 'bg-green-500 text-white' :
                    isAmberVerdict(verdict) ? 'bg-yellow-500 text-gray-900' :
                    'bg-red-500 text-white'
                  }`}>
                    {verdict}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-gray-400 text-xs uppercase">Purchase Price</p>
                    <p className="text-xl font-bold">{formatCurrency(purchasePrice)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase">Total Project Cost</p>
                    <p className="text-xl font-bold">{formatCurrency(totalProjectCost)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase">Year 1 NOI</p>
                    {/* ⭐ Year 1, as labelled - the lease-up's $0, the carry's $0, the schedule's year 1. In-place is the cap's. */}
                    <p className="text-xl font-bold">{formatCurrency(yearOneNOI)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs uppercase">{capCaption.goingInCap}</p>
                    <p className="text-xl font-bold">{formatPercent(capRate)}</p>
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{capCaption.capRate}{marketedCapRate !== null && ` (${capCaption.basis})`}</p>
                  <p className="text-2xl font-bold text-primary-600">{formatPercent(capRate)}</p>
                  {/* ⭐ The marketed cap is PRINTED, never silently dropped - a reader who came
                      from the OM must be able to see where the difference went. */}
                  {marketedCapRate !== null && (
                    <p className="text-[11px] text-amber-700 mt-1 leading-tight">
                      OM markets {formatPercent(marketedCapRate)}
                      <br />as of {noiRebase.statedAsOf}
                    </p>
                  )}
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">Avg CoC</p>
                  <p className={`text-2xl font-bold ${avgCashOnCash >= 8 ? 'text-green-600' : 'text-gray-900'}`}>
                    {formatPercent(avgCashOnCash)}
                  </p>
                  {vaTotalCost > 0 && <p className="text-[10px] text-gray-400 mt-1">Incl. value-add CapEx</p>}
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">IRR</p>
                  <p className={`text-2xl font-bold ${irrValue >= thresholds.irr ? 'text-green-600' : 'text-red-600'}`}>
                    {formatPercent(irrValue)}
                  </p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">Equity Multiple</p>
                  <p className="text-2xl font-bold text-gray-900">{formatRatio(equityMultiple)}</p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">DSCR</p>
                  <p className={`text-2xl font-bold ${dscr >= 1.25 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatRatio(dscr)}
                  </p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">Debt Yield</p>
                  <p className={`text-2xl font-bold ${debtYield >= n(data.min_debt_yield) || !data.min_debt_yield ? 'text-gray-900' : 'text-red-600'}`}>
                    {formatPercent(debtYield)}
                  </p>
                </div>
                <div className="bg-white border rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">Unlevered IRR</p>
                  <p className="text-2xl font-bold text-gray-900">{formatPercent(unleveredIrr)}</p>
                  <p className="text-[10px] text-gray-400 mt-1">No debt anywhere</p>
                </div>
              </div>

              {/* Optimization Callout */}
              {optimization && (
                <div className={`rounded-xl p-4 border text-sm ${verdict === 'GO' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  {verdict === 'GO' ? (
                    <p className="text-green-800 font-medium">
                      <span className="mr-1.5">&#10003;</span>
                      This deal meets all {thresholdLabel} thresholds
                    </p>
                  ) : (
                    <div className="space-y-1">
                      <p className="text-amber-900 font-medium">
                        To hit your {formatPercent(thresholds.irr)} IRR target:
                      </p>
                      {optimization.maxPurchasePrice > 0 ? (
                        <p className="text-amber-800">
                          Max purchase price: <span className="font-semibold">{formatCurrency(optimization.maxPurchasePrice)}</span>
                          <span className="text-amber-600 ml-1">(currently {formatCurrency(purchasePrice)})</span>
                        </p>
                      ) : (
                        <p className="text-red-700">Price reduction alone won't achieve target IRR — significant income growth needed.</p>
                      )}
                      {optimization.requiredNOI > 0 ? (
                        <p className="text-amber-800">
                          {optimization.maxPurchasePrice > 0 ? <>&mdash; or &mdash; </> : ''}Required NOI: <span className="font-semibold">{formatCurrency(optimization.requiredNOI)}</span>
                          <span className="text-amber-600 ml-1">(currently {formatCurrency(noi)})</span>
                        </p>
                      ) : (
                        <p className="text-red-700">Income growth alone won't achieve target IRR at this price and debt structure.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Quick financial summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                  <h4 className="text-sm font-semibold text-green-800 uppercase mb-3">
                    Income
                    {leaseType !== 'gross' && (
                      <span className="ml-2 text-xs font-normal text-green-600">
                        ({leaseType === 'nnn' ? 'NNN' : 'Modified Gross'})
                      </span>
                    )}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Gross Income</span><span className="font-medium">{formatCurrency(pgi)}</span></div>
                    <div className="flex justify-between text-red-600"><span>Less: Vacancy</span><span>-{formatCurrency(vacancyAmount)}</span></div>
                    <div className="flex justify-between"><span>Other Income</span><span className="font-medium">{formatCurrency(n(data.other_income))}</span></div>
                    {nnnReimbursements > 0 && (
                      <div className="flex justify-between text-green-700"><span>Expense Reimbursements</span><span className="font-medium">+{formatCurrency(nnnReimbursements)}</span></div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-green-300 font-bold"><span>EGI</span><span>{formatCurrency(egi)}</span></div>
                  </div>
                </div>
                <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                  <h4 className="text-sm font-semibold text-red-800 uppercase mb-3">Expenses & Debt</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>{expensesAreCarry ? 'Carry Costs (not operating expenses)' : 'Operating Expenses'}</span><span className="font-medium">{formatCurrency(totalExpenses)}</span></div>
                    <div className="flex justify-between"><span>Bank Debt Service</span><span className="font-medium">{formatCurrency(bankAnnualDS)}</span></div>
                    {sellerAnnualDS > 0 && (
                      <div className="flex justify-between"><span>Seller Carryback DS</span><span className="font-medium">{formatCurrency(sellerAnnualDS)}</span></div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-red-300 font-bold">
                      <span>Before-Tax Cash Flow</span>
                      <span className={beforeTaxCF >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(beforeTaxCF)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Three cases side by side — shows the distribution instead of
                  asking the reader to imagine it from one case plus sliders. */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Downside / Base / Upside</h4>
                <div className="grid grid-cols-3 gap-3">
                  {([
                    ['Downside', scenarios.downside, 'bg-red-50 border-red-200'],
                    ['Base', scenarios.base, 'bg-gray-50 border-gray-300'],
                    ['Upside', scenarios.upside, 'bg-green-50 border-green-200'],
                  ] as const).map(([label, s, cls]) => (
                    <div key={label} className={`rounded-xl border p-4 ${cls}`}>
                      <p className="text-xs uppercase tracking-wide text-gray-600 mb-2">
                        {label} <span className="text-gray-400">({s.exitCapUsed.toFixed(2)}% exit)</span>
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span>IRR</span><span className="font-semibold">{formatPercent(s.irr)}</span></div>
                        <div className="flex justify-between"><span>EM</span><span className="font-semibold">{formatRatio(s.equityMultiple)}</span></div>
                        <div className="flex justify-between"><span>Avg CoC</span><span className="font-semibold">{formatPercent(s.avgCoC)}</span></div>
                        <div className="flex justify-between"><span>DSCR</span><span className="font-semibold">{formatRatio(s.dscr)}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sensitivity Analysis */}
              {sensitivityScenarios.length > 0 && (
                <div className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => setSensitivityExpanded(!sensitivityExpanded)}
                    className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Sensitivity Analysis</h4>
                    <svg className={`w-4 h-4 text-gray-500 transition-transform ${sensitivityExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {sensitivityExpanded && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-t">
                            <th className="text-left px-4 py-2 font-semibold text-gray-600">Scenario</th>
                            <th className="text-right px-4 py-2 font-semibold text-gray-600">IRR</th>
                            <th className="text-right px-4 py-2 font-semibold text-gray-600">Avg CoC</th>
                            <th className="text-right px-4 py-2 font-semibold text-gray-600">DSCR</th>
                            <th className="text-right px-4 py-2 font-semibold text-gray-600">Eq. Mult</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sensitivityScenarios.map((s, i) => {
                            const isBase = s.label === 'Base Case';
                            const isSeparator = i === 5; // gap before rent scenarios
                            return (
                              <tr
                                key={s.label}
                                className={`border-t ${isBase ? 'bg-primary-50 font-semibold' : ''} ${isSeparator ? 'border-t-2 border-gray-300' : ''}`}
                              >
                                <td className="px-4 py-2 text-gray-800">
                                  {s.label}
                                  {isBase && <span className="ml-1 text-xs text-primary-500">&larr;</span>}
                                </td>
                                <td className={`px-4 py-2 text-right ${s.result.irr >= thresholds.irr ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatPercent(s.result.irr)}
                                </td>
                                <td className={`px-4 py-2 text-right ${s.result.avgCoC >= thresholds.cash_on_cash ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatPercent(s.result.avgCoC)}
                                </td>
                                <td className={`px-4 py-2 text-right ${s.result.dscr >= thresholds.dscr ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatRatio(s.result.dscr)}
                                </td>
                                <td className={`px-4 py-2 text-right ${s.result.equityMultiple >= thresholds.equity_multiple ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatRatio(s.result.equityMultiple)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Current vs Pro Forma (from OM extraction) */}
              {(() => {
                const add = property.additional_data as Record<string, any> | undefined;
                const pf = add?.pro_forma_financials;
                if (!pf || (!pf.noi && !pf.cap_rate && !pf.gross_income)) return null;

                const rows = [
                  { label: 'NOI', current: property.noi, proforma: pf.noi, fmt: formatCurrency },
                  { label: 'CAP Rate', current: property.cap_rate, proforma: pf.cap_rate, fmt: formatPercent },
                  { label: 'Gross Income', current: property.gross_income, proforma: pf.gross_income, fmt: formatCurrency },
                  { label: 'Occupancy', current: property.occupancy_rate, proforma: pf.occupancy_rate, fmt: formatPercent },
                ];

                return (
                  <div className="border rounded-xl overflow-hidden">
                    <div className="px-5 py-3 bg-blue-50 border-b border-blue-200">
                      <h4 className="text-sm font-semibold text-blue-800 uppercase tracking-wide">Current vs Pro Forma (from OM)</h4>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="text-left px-4 py-2 font-semibold text-gray-600">Metric</th>
                          <th className="text-right px-4 py-2 font-semibold text-gray-600">Current (In-Place)</th>
                          <th className="text-right px-4 py-2 font-semibold text-gray-600">Pro Forma (Stabilized)</th>
                          <th className="text-right px-4 py-2 font-semibold text-gray-600">Upside</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => {
                          const c = r.current ?? 0;
                          const p = r.proforma ?? 0;
                          const delta = p - c;
                          return (
                            <tr key={r.label} className="border-b last:border-0">
                              <td className="px-4 py-2 font-medium text-gray-800">{r.label}</td>
                              <td className="px-4 py-2 text-right text-gray-600">{c ? r.fmt(c) : '—'}</td>
                              <td className="px-4 py-2 text-right font-semibold text-gray-900">{p ? r.fmt(p) : '—'}</td>
                              <td className={`px-4 py-2 text-right font-medium ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                {c && p ? (r.fmt === formatCurrency ? `+${formatCurrency(delta)}` : `+${delta.toFixed(2)}%`) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {pf.notes && (
                      <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 border-t">{pf.notes}</div>
                    )}
                  </div>
                );
              })()}

              {/* Tenant Roster (from OM extraction) */}
              {(() => {
                const add = property.additional_data as Record<string, any> | undefined;
                const tenants = add?.tenants as Array<{
                  tenant_name?: string; leased_sf?: number; monthly_base_rent?: number;
                  rent_per_sf?: number; lease_end?: string; tenant_credit?: string;
                  renewal_options?: string;
                }> | undefined;
                if (!tenants || tenants.length === 0) return null;

                const occupied = tenants.filter(t => !t.tenant_name?.toLowerCase().includes('vacant'));
                const vacant = tenants.filter(t => t.tenant_name?.toLowerCase().includes('vacant'));

                return (
                  <div className="border rounded-xl overflow-hidden">
                    <div className="px-5 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-indigo-800 uppercase tracking-wide">Tenant Roster</h4>
                      <span className="text-xs text-indigo-600">{occupied.length} occupied, {vacant.length} vacant</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="text-left px-4 py-2 font-semibold text-gray-600">Tenant</th>
                            <th className="text-right px-4 py-2 font-semibold text-gray-600">SF</th>
                            <th className="text-right px-4 py-2 font-semibold text-gray-600">Rent</th>
                            <th className="text-center px-4 py-2 font-semibold text-gray-600">Lease End</th>
                            <th className="text-center px-4 py-2 font-semibold text-gray-600">Credit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tenants.map((t, i) => {
                            const isVacant = t.tenant_name?.toLowerCase().includes('vacant');
                            return (
                              <tr key={i} className={`border-b last:border-0 ${isVacant ? 'bg-red-50 text-red-700' : ''}`}>
                                <td className="px-4 py-2 font-medium">{t.tenant_name || 'Unknown'}</td>
                                <td className="px-4 py-2 text-right">{t.leased_sf?.toLocaleString() || '—'}</td>
                                <td className="px-4 py-2 text-right">
                                  {t.monthly_base_rent ? `$${t.monthly_base_rent.toLocaleString()}/mo` : t.rent_per_sf ? `$${t.rent_per_sf.toFixed(2)}/SF` : '—'}
                                </td>
                                <td className="px-4 py-2 text-center">{t.lease_end || '—'}</td>
                                <td className="px-4 py-2 text-center">
                                  {t.tenant_credit && (
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      t.tenant_credit.toLowerCase() === 'national' ? 'bg-green-100 text-green-800' :
                                      t.tenant_credit.toLowerCase() === 'regional' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>{t.tenant_credit}</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Key Considerations (AI-generated narrative) */}
              <div className="border rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-purple-50 border-b border-purple-200 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-purple-800 uppercase tracking-wide">Key Considerations</h4>
                  <Button
                    size="sm"
                    variant={keyConsiderations ? 'outline' : 'primary'}
                    onClick={handleGenerateKeyConsiderations}
                    disabled={keyConsiderationsLoading}
                    isLoading={keyConsiderationsLoading}
                  >
                    {keyConsiderations ? 'Regenerate' : 'Generate AI Analysis'}
                  </Button>
                </div>
                <div className="p-5">
                  {keyConsiderationsLoading && (
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Analyzing deal data and generating narrative...
                    </div>
                  )}
                  {keyConsiderationsError && (
                    <p className="text-sm text-red-600">{keyConsiderationsError}</p>
                  )}
                  {!keyConsiderations && !keyConsiderationsLoading && !keyConsiderationsError && (
                    <p className="text-sm text-gray-400 italic">
                      Click "Generate AI Analysis" to draft the narrative covering tenant risk, the financials, data discrepancies and market context. It reports findings and what the numbers return at different bases; it does not recommend an action.
                    </p>
                  )}
                  {keyConsiderationsReview && !keyConsiderationsLoading &&
                    (keyConsiderationsReview.advocacy.length > 0 ||
                     keyConsiderationsReview.attribution.length > 0) && (
                    /* The guard REPORTS rather than deletes. A wrong figure is dangerous and the
                       fact guard drops those on brochures - but an advocacy phrase or a stray "per
                       the OM" sits inside a sentence carrying a real finding, and the house style's
                       own closing rule is that softening the findings is the one edit that has gone
                       too far. So it is surfaced here and Joseph rewrites the line. */
                    <div
                      data-testid="house-style-review"
                      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
                    >
                      <p className="font-semibold text-amber-900 mb-1">Read before sending</p>
                      {keyConsiderationsReview.attribution.length > 0 && (
                        <p className="text-amber-900 mb-1">
                          <span className="font-medium">Attributed to the OM:</span>{' '}
                          {keyConsiderationsReview.attribution.join(', ')}
                          {' '}- this draft was given the analyzer record, not the Offering Memorandum.
                        </p>
                      )}
                      {keyConsiderationsReview.advocacy.length > 0 && (
                        <p className="text-amber-900">
                          <span className="font-medium">Advocacy the house style drops:</span>{' '}
                          {keyConsiderationsReview.advocacy.join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                  {keyConsiderations && !keyConsiderationsLoading && (
                    <div className="prose prose-sm max-w-none text-gray-700">
                      {keyConsiderations.split('\n').map((line, i) => {
                        if (line.startsWith('**') && line.endsWith('**')) {
                          return <h5 key={i} className="text-sm font-bold text-gray-900 mt-4 mb-1">{line.replace(/\*\*/g, '')}</h5>;
                        }
                        if (line.trim() === '') return <br key={i} />;
                        // Handle inline bold markers
                        const parts = line.split(/(\*\*.*?\*\*)/g);
                        return (
                          <p key={i} className="mb-2 leading-relaxed">
                            {parts.map((part, j) =>
                              part.startsWith('**') && part.endsWith('**')
                                ? <strong key={j}>{part.replace(/\*\*/g, '')}</strong>
                                : part
                            )}
                          </p>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Items to verify - this list IS what separates GO from CONDITIONAL. */}
              <div className="border rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-amber-50 border-b border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Items to Verify</h4>
                </div>
                <div className="p-5">
                  <p className="text-xs text-gray-500 mb-2">
                    One per line. Material items outstanding - the things that could change the structure
                    rather than the arithmetic. <span className="font-medium">Leave empty and a deal that
                    clears every metric reads GO; list anything here and it reads CONDITIONAL.</span>
                  </p>
                  <textarea
                    data-testid="open-items"
                    value={(data.open_items ?? []).join('\n')}
                    onChange={(e) => handleOpenItemsChange(e.target.value)}
                    placeholder={'Financing obtainable at the modeled terms\nGuarantor financial statements\nEstoppels from both tenants'}
                    rows={3}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {(data.open_items ?? []).length === 0
                      ? 'Nothing outstanding.'
                      : `${(data.open_items ?? []).length} item${(data.open_items ?? []).length === 1 ? '' : 's'} outstanding - these print under the scorecard.`}
                  </p>
                </div>
              </div>

              {/* Scenario from plain language */}
              <div className="border rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Scenario</h4>
                  <Button
                    size="sm"
                    variant={scenario ? 'outline' : 'primary'}
                    onClick={handleBuildScenario}
                    disabled={scenarioLoading || riskText.trim().length < 8}
                    isLoading={scenarioLoading}
                  >
                    {scenario ? 'Rebuild' : 'Build Scenario'}
                  </Button>
                </div>
                <div className="p-5">
                  <p className="text-xs text-gray-500 mb-2">
                    Describe the risk in your own words. The model picks the assumptions and
                    <span className="font-medium"> prints them</span>; the analyzer computes every number.
                    It will say when something is not in the record rather than guess it.
                  </p>
                  <textarea
                    data-testid="risk-text"
                    value={riskText}
                    onChange={(e) => setRiskText(e.target.value)}
                    placeholder="Risky because the lease is expiring in year 4 and other buildings in the area have been on the market a long time"
                    rows={3}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base"
                  />

                  {scenarioError && (
                    <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg p-3">
                      {scenarioError}
                    </p>
                  )}

                  {scenario && !scenarioLoading && (
                    <div className="mt-4 space-y-3">
                      <p className="text-sm font-semibold text-gray-900">{scenario.title}</p>
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Assumptions, as modelled</p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {scenario.assumptions.map((a, i) => (
                            <li key={i} className="flex gap-2">
                              <span aria-hidden="true">-</span>
                              <span>
                                {a.label}
                                <span className="text-gray-500"> ({a.lever.replace(/_/g, ' ')} {a.value}{a.year ? `, year ${a.year}` : ''})</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {/* The engine's answer, base beside modelled. Every figure here comes from
                          computeScenario on the same base the sensitivity tables use. */}
                      {scenarioOutcome && (
                        <div className="rounded-lg border border-slate-300 overflow-hidden" data-testid="scenario-outcome">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100 text-gray-600">
                              <tr>
                                <th className="text-left px-3 py-1.5 font-medium">Modelled</th>
                                <th className="text-right px-3 py-1.5 font-medium">Base</th>
                                <th className="text-right px-3 py-1.5 font-medium">Scenario</th>
                              </tr>
                            </thead>
                            <tbody>
                              {([
                                ['IRR', formatPercent(scenarioOutcome.base.irr), formatPercent(scenarioOutcome.modelled.irr)],
                                ['Equity Multiple', `${scenarioOutcome.base.equityMultiple.toFixed(2)}x`, `${scenarioOutcome.modelled.equityMultiple.toFixed(2)}x`],
                                ['Avg Cash-on-Cash', formatPercent(scenarioOutcome.base.avgCoC), formatPercent(scenarioOutcome.modelled.avgCoC)],
                                ['Year 1 NOI', formatCurrency(scenarioOutcome.base.noi), formatCurrency(scenarioOutcome.modelled.noi)],
                              ] as Array<[string, string, string]>).map(([label, b, m]) => (
                                <tr key={label} className="border-t">
                                  <td className="px-3 py-1.5">{label}</td>
                                  <td className="px-3 py-1.5 text-right text-gray-600">{b}</td>
                                  <td className="px-3 py-1.5 text-right font-semibold">{m}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {scenarioOutcome.ignored.length > 0 && (
                            <p className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border-t border-amber-200">
                              Not modelled: {scenarioOutcome.ignored.map(i => i.label).join('; ')} - the engine has no lever for it.
                            </p>
                          )}
                        </div>
                      )}
                      {scenario.rationale && <p className="text-sm text-gray-700">{scenario.rationale}</p>}
                      {scenario.notEstablished.length > 0 && (
                        <div className="rounded-lg border border-gray-300 bg-gray-50 p-3">
                          <p className="text-xs font-semibold text-gray-800 mb-1">Not established by the record</p>
                          <p className="text-xs text-gray-600">{scenario.notEstablished.join('; ')}</p>
                        </div>
                      )}
                      {scenarioFacts.length > 0 && (
                        <details className="text-xs text-gray-600">
                          <summary className="cursor-pointer font-medium text-gray-700">What the model was given</summary>
                          <ul className="mt-2 space-y-1">
                            {scenarioFacts.map((f, i) => (
                              <li key={i}>
                                {f.label}: {f.value ?? <span className="italic">not in the record</span>}
                                <span className="text-gray-400"> ({f.source})</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Analysis Notes</label>
                <textarea
                  value={data.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Notes about assumptions, risks, or other considerations..."
                  rows={3}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB 2: Strategy
             ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'strategy' && (
            <div className="space-y-6">
              <div className="bg-primary-50 rounded-xl p-5 border border-primary-200">
                <h3 className="text-sm font-semibold text-primary-800 uppercase tracking-wide mb-4">Investment Strategy</h3>
                <Select
                  label="Strategy"
                  value={data.investment_strategy || 'value_add'}
                  onChange={(e) => handleChange('investment_strategy', e.target.value)}
                  options={[
                    { value: 'core', label: 'Core' },
                    { value: 'core_plus', label: 'Core-Plus' },
                    { value: 'value_add', label: 'Value-Add' },
                    { value: 'opportunistic', label: 'Opportunistic' },
                  ]}
                />

                {suggestedExitCap && exitCapEvidence.supporting.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-primary-700">
                      Based on {exitCapEvidence.supporting.length}{' '}
                      {usingPool
                        ? `closed sales within ${poolExitCap?.radiusMiles} miles, last ${poolExitCap?.windowMonths} months`
                        : 'linked comps'}, median cap rate is {formatPercent(suggestedExitCap)} — {
                        suggestedExitCap >= 6.5 ? 'aligns with Core strategy' :
                        suggestedExitCap >= 5.5 ? 'aligns with Value-Add strategy' :
                        'suggests Opportunistic strategy'
                      }
                    </p>
                    {/* Show the comps behind the number, so it can be defended in a
                        meeting rather than taken on faith. */}
                    <ul className="mt-2 space-y-1">
                      {exitCapEvidence.supporting.map((c, i) => (
                        <li key={i} className="text-xs text-gray-600 flex flex-wrap gap-x-2">
                          <span className="font-medium">{c.address}</span>
                          {c.city && <span>{c.city}</span>}
                          {c.capRate != null && <span>{formatPercent(c.capRate)} cap</span>}
                          {c.pricePerSqft != null && <span>${Math.round(c.pricePerSqft)}/SF</span>}
                          {c.date && <span>{c.date}</span>}
                          {c.distanceMiles != null && <span>{c.distanceMiles.toFixed(1)} mi</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Strategy Thresholds */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b">
                  <h4 className="text-sm font-semibold text-gray-700">
                    {thresholdLabel} — Required Thresholds
                  </h4>
                </div>
                <div className="divide-y">
                  {[
                    { label: 'Min Cap Rate', value: thresholds.cap_rate, fmt: 'percent' as const },
                    { label: 'Min Cash-on-Cash', value: thresholds.cash_on_cash, fmt: 'percent' as const },
                    { label: 'Min IRR', value: thresholds.irr, fmt: 'percent' as const },
                    { label: 'Min Equity Multiple', value: thresholds.equity_multiple, fmt: 'ratio' as const },
                    { label: 'Min DSCR', value: thresholds.dscr, fmt: 'ratio' as const },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between px-5 py-3 text-sm">
                      <span className="text-gray-600">{row.label}</span>
                      <span className="font-semibold">
                        {row.value === null ? 'N/A' : row.fmt === 'percent' ? formatPercent(row.value) : formatRatio(row.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comp benchmark */}
              {psfBenchmark && (
                <div className="bg-gray-50 rounded-xl p-5 border">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Comp Price/SF Benchmark</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div><p className="text-xs text-gray-500">Min</p><p className="font-bold">${psfBenchmark.min.toFixed(0)}/SF</p></div>
                    <div><p className="text-xs text-gray-500">Avg</p><p className="font-bold text-primary-600">${psfBenchmark.avg.toFixed(0)}/SF</p></div>
                    <div><p className="text-xs text-gray-500">Max</p><p className="font-bold">${psfBenchmark.max.toFixed(0)}/SF</p></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">Subject: ${pricePerSqft.toFixed(0)}/SF</p>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB 3: Value-Add
             ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'valueadd' && (
            <div className="space-y-6">
              {/* Value-Add Drivers */}
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide mb-4">Value-Add Drivers</h3>
                <div className="space-y-4">
                  {VA_ITEMS.map(item => (
                    <div key={item.key} className="space-y-1">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!data[item.key]}
                          onChange={(e) => handleChange(item.key, e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-gray-700">{item.label}</span>
                      </label>
                      {data[item.key] && (
                        <input
                          type="text"
                          value={(data[item.noteKey] as string) || ''}
                          onChange={(e) => handleChange(item.noteKey, e.target.value)}
                          placeholder={`Notes for ${item.label}...`}
                          className="w-full text-sm rounded-md border-gray-300 pl-7 focus:border-primary-500 focus:ring-primary-500"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* As-Is vs Stabilized */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b">
                  <h4 className="text-sm font-semibold text-gray-700">As-Is vs Stabilized Comparison</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-gray-600 font-medium">Metric</th>
                        <th className="px-4 py-2 text-right text-gray-600 font-medium">As-Is</th>
                        <th className="px-4 py-2 text-right text-gray-600 font-medium">Stabilized</th>
                        <th className="px-4 py-2 text-right text-gray-600 font-medium">Delta</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr>
                        <td className="px-4 py-2">Rent PSF</td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" step="0.01" value={data.as_is_rent_psf || ''} onChange={(e) => handleChange('as_is_rent_psf', e.target.value)}
                            className="w-24 text-right text-sm border-gray-300 rounded" />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" step="0.01" value={data.stabilized_rent_psf || ''} onChange={(e) => handleChange('stabilized_rent_psf', e.target.value)}
                            className="w-24 text-right text-sm border-gray-300 rounded" />
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-green-600">
                          +${((n(data.stabilized_rent_psf) - n(data.as_is_rent_psf))).toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Occupancy %</td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" step="0.1" value={data.as_is_occupancy || ''} onChange={(e) => handleChange('as_is_occupancy', e.target.value)}
                            className="w-24 text-right text-sm border-gray-300 rounded" />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" step="0.1" value={data.stabilized_occupancy || ''} onChange={(e) => handleChange('stabilized_occupancy', e.target.value)}
                            className="w-24 text-right text-sm border-gray-300 rounded" />
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-green-600">
                          +{((n(data.stabilized_occupancy) - n(data.as_is_occupancy))).toFixed(1)}%
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Other Income</td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" value={data.as_is_other_income || ''} onChange={(e) => handleChange('as_is_other_income', e.target.value)}
                            className="w-24 text-right text-sm border-gray-300 rounded" />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" value={data.stabilized_other_income || ''} onChange={(e) => handleChange('stabilized_other_income', e.target.value)}
                            className="w-24 text-right text-sm border-gray-300 rounded" />
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-green-600">
                          +{formatCurrency(n(data.stabilized_other_income) - n(data.as_is_other_income))}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">Expense Ratio %</td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" step="0.1" value={data.as_is_expense_ratio || ''} onChange={(e) => handleChange('as_is_expense_ratio', e.target.value)}
                            className="w-24 text-right text-sm border-gray-300 rounded" />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" step="0.1" value={data.stabilized_expense_ratio || ''} onChange={(e) => handleChange('stabilized_expense_ratio', e.target.value)}
                            className="w-24 text-right text-sm border-gray-300 rounded" />
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-green-600">
                          {((n(data.stabilized_expense_ratio) - n(data.as_is_expense_ratio))).toFixed(1)}%
                        </td>
                      </tr>
                    </tbody>
                    <tfoot className="bg-gray-50 font-semibold">
                      <tr>
                        <td className="px-4 py-2">NOI</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(asIsNOI)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(stabilizedNOI)}</td>
                        <td className="px-4 py-2 text-right text-green-600">+{formatCurrency(noiLift)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Value-Add Costs */}
              <div className="bg-orange-50 rounded-xl p-5 border border-orange-200">
                <h3 className="text-sm font-semibold text-orange-800 uppercase tracking-wide mb-4">Value-Add Costs</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="CapEx" type="number" leftAddon="$" value={data.va_capex || ''} onChange={(e) => handleChange('va_capex', e.target.value)} />
                  <Input label="TI / Leasing" type="number" leftAddon="$" value={data.va_ti_leasing || ''} onChange={(e) => handleChange('va_ti_leasing', e.target.value)} />
                  <Input label="Carry Costs" type="number" leftAddon="$" value={data.va_carry_costs || ''} onChange={(e) => handleChange('va_carry_costs', e.target.value)} />
                  <Input label="Contingency" type="number" leftAddon="$" value={data.va_contingency || ''} onChange={(e) => handleChange('va_contingency', e.target.value)} />
                  {/*
                    ⭐⭐ THE ONLY PART OF THE LEASE-UP THAT PRICES ANYTHING. The driver checkbox
                    above is an attribution label read by no calculation; this feeds
                    generateProjections and therefore the scorecard, the PDF and the scenarios.
                  */}
                  <Input label="Lease-Up (months to income)" type="number" rightAddon="mo"
                    value={data.lease_up_months || ''}
                    onChange={(e) => handleChange('lease_up_months', e.target.value)} />
                </div>
                <div className="flex justify-between pt-4 mt-4 border-t border-orange-300 text-sm font-bold">
                  <span className="text-orange-800">Total Value-Add Costs</span>
                  <span className="text-orange-700 text-lg">{formatCurrency(vaTotalCost)}</span>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB 4: Proforma
             ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'proforma' && (
            <div className="space-y-6">
              {/* Settings */}
              <div className="bg-gray-50 rounded-xl p-5 border">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Proforma Settings</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input label="Income Growth" type="number" step="0.1" rightAddon="%" value={data.income_growth_rate || ''} onChange={(e) => handleChange('income_growth_rate', e.target.value)} />
                  <Input label="Expense Growth" type="number" step="0.1" rightAddon="%" value={data.expense_growth_rate || ''} onChange={(e) => handleChange('expense_growth_rate', e.target.value)} />
                  <Select
                    label="Holding Period"
                    value={String(data.holding_period || 5)}
                    onChange={(e) => handleChange('holding_period', e.target.value)}
                    options={[
                      { value: '5', label: '5 Years' },
                      { value: '7', label: '7 Years' },
                      { value: '10', label: '10 Years' },
                    ]}
                  />
                  <Input label="Exit Cap Rate" type="number" step="0.05" rightAddon="%" value={data.exit_cap_rate || ''} onChange={(e) => handleChange('exit_cap_rate', e.target.value)} />
                  {/* Hold year 1 runs from here, and a mid-year rent step blends against it. */}
                  <Input
                    label="Expected Close Date"
                    type="date"
                    value={data.close_date || ''}
                    onChange={(e) => handleChange('close_date', e.target.value)}
                  />
                </div>
              </div>

              {/* Contractual rent schedule (Phase 3A) */}
              {leaseSchedule.tenantCount > 0 && (
                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-800 uppercase">Contractual Rent Schedule</h4>
                      <p className="text-xs text-emerald-700 mt-1">
                        Built from {leaseSchedule.tenantCount} lease{leaseSchedule.tenantCount === 1 ? '' : 's'} on their real
                        effective dates, blended for partial years. <span className="font-medium">Year 1 starts {leaseSchedule.startDate}</span>
                        {isDefaultedCloseDate(data.close_date)
                          ? ` - a ${DEFAULT_CLOSE_DAYS}-day default, since no close date is set. Set one below to make the blend exact.`
                          : ' - your expected close.'}
                      </p>
                      {/* ⭐ The anchor is STATED, never silent. A reader who disagrees with the
                          rent in force today can see it and correct the rent roll. */}
                      <p className="text-xs text-emerald-700 mt-1">
                        {typeof leaseSchedule.anchorRate === 'number'
                          ? <>Contractual growth is anchored on the rent in force on{' '}
                              <span className="font-medium">{leaseSchedule.anchorDate}</span>
                              {' - '}<span className="font-medium">{formatCurrency(leaseSchedule.anchorRate)}</span>
                              {' '}a year, which is the rent the in-place NOI is struck on.</>
                          : <>The rent in force today could not be read from the leases, so no NOI schedule is offered.</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {scheduleIsApplied
                        ? <span className="text-xs bg-emerald-600 text-white px-2.5 py-1 rounded font-medium">Applied</span>
                        : leaseSchedule.noiSchedule.some(v => typeof v === 'number')
                          ? <button onClick={applyLeaseSchedule} className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded hover:bg-emerald-700 font-medium">Apply to projections</button>
                          : null}
                      {data.noi_overrides && (
                        <button onClick={clearLeaseSchedule} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 underline">Clear</button>
                      )}
                    </div>
                  </div>

                  <p className="text-xs font-semibold text-emerald-800 uppercase mt-4">
                    Proforma - what you collect
                  </p>
                  <p className="text-xs text-emerald-700">
                    Hold years from the close. A step landing mid-year is blended, because that is
                    the rent actually collected in that year.
                  </p>
                  <table className="w-full text-sm mt-2">
                    <thead className="text-gray-600">
                      <tr>
                        <th className="text-left py-1">Year</th>
                        <th className="text-right py-1">Contractual rent</th>
                        <th className="text-right py-1">NOI</th>
                        <th className="text-left py-1 pl-4">Why</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-emerald-200">
                      {leaseSchedule.detail.map((d, i) => (
                        <tr key={d.year}>
                          <td className="py-1.5 font-medium">Y{d.year}</td>
                          <td className="py-1.5 text-right">{d.rent === null ? '--' : formatCurrency(d.rent)}</td>
                          <td className="py-1.5 text-right font-semibold">
                            {leaseSchedule.noiSchedule[i] === null ? 'modelled' : formatCurrency(leaseSchedule.noiSchedule[i] as number)}
                          </td>
                          <td className="py-1.5 pl-4 text-xs text-gray-600">{d.notes.join('; ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* ⭐⭐ The contract as written, beside the blend. Two questions, two tables. */}
                  {leaseSchedule.leaseYears.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-emerald-200">
                      <p className="text-xs font-semibold text-emerald-800 uppercase">
                        Lease-year schedule - what the lease says
                      </p>
                      <p className="text-xs text-emerald-700">
                        The rent in force from each effective date, as the lease reads. This is the
                        table to check the OM against; the proforma above is what those figures
                        produce once a mid-year step is blended.
                      </p>
                      <table className="w-full text-sm mt-2">
                        <thead className="text-gray-600">
                          <tr>
                            <th className="text-left py-1">Effective</th>
                            <th className="text-right py-1">Annual rent</th>
                            <th className="text-left py-1 pl-4">Step</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-emerald-200">
                          {leaseSchedule.leaseYears.map((r, i) => (
                            <tr key={r.from}>
                              <td className="py-1.5 font-medium whitespace-nowrap">
                                {r.from}
                                {r.inferred && (
                                  <span className="ml-1.5 text-[10px] uppercase text-amber-700 font-semibold">derived</span>
                                )}
                              </td>
                              <td className="py-1.5 text-right">{formatCurrency(r.annualRent)}</td>
                              <td className="py-1.5 pl-4 text-xs text-gray-600">
                                {i === 0 ? 'In force at close' : r.drivers.join(', ')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {leaseSchedule.leaseYears.some(r => r.inferred) && (
                        <p className="text-xs text-amber-700 mt-2">
                          Rows marked <span className="font-semibold uppercase">derived</span> carry a
                          date the document did not state - it is the lease-commencement anniversary,
                          not a quoted figure. Confirm against the lease.
                        </p>
                      )}
                    </div>
                  )}

                  {leaseSchedule.capitalCosts.some(c => c > 0) && (
                    <p className="text-xs text-gray-700 mt-3">
                      Leasing capital (TI + LC):{' '}
                      {leaseSchedule.capitalCosts.map((c, i) => c > 0 ? `Y${i + 1} ${formatCurrency(c)}` : null)
                        .filter(Boolean).join('  |  ')}
                      <span className="text-gray-500"> - capital, not netted against NOI</span>
                    </p>
                  )}

                  {leaseSchedule.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-700 mt-2">{w}</p>
                  ))}

                  {/* Rollover assumptions (Phase 3B) */}
                  <div className="mt-4 pt-4 border-t border-emerald-200">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h5 className="text-xs font-semibold text-emerald-800 uppercase">
                        Rollover assumptions {data.rollover_assumptions ? '' : '(not modelled)'}
                      </h5>
                      {data.rollover_assumptions
                        ? <button onClick={disableRollover} className="text-xs text-gray-500 hover:text-gray-700 underline">Stop modelling rollover</button>
                        : <button onClick={() => setRolloverField('renewalProbability', '65')} className="text-xs bg-white border border-emerald-400 text-emerald-800 px-2.5 py-1 rounded hover:bg-emerald-100 font-medium">Model rollover</button>}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {data.rollover_assumptions
                        ? 'Rent after a lease expires is an expected value, not a forecast. Downtime and free rent apply only if the tenant leaves.'
                        : 'Without these, years at or after a lease expiry carry no contractual figure and fall back to the growth model.'}
                    </p>

                    {data.rollover_assumptions && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <Input label="Renewal probability" type="number" rightAddon="%"
                          value={data.rollover_assumptions.renewalProbability ?? ''}
                          onChange={(e) => setRolloverField('renewalProbability', e.target.value)} />
                        <Input label="Downtime" type="number" rightAddon="mo"
                          value={data.rollover_assumptions.downtimeMonths ?? ''}
                          onChange={(e) => setRolloverField('downtimeMonths', e.target.value)} />
                        <Input label="Market rent" type="number" step="0.25" rightAddon="$/SF"
                          value={data.rollover_assumptions.marketRentPerSf ?? ''}
                          onChange={(e) => setRolloverField('marketRentPerSf', e.target.value)} />
                        <Input label="Free rent" type="number" rightAddon="mo"
                          value={data.rollover_assumptions.freeRentMonths ?? ''}
                          onChange={(e) => setRolloverField('freeRentMonths', e.target.value)} />
                        <Input label="TI, new" type="number" rightAddon="$/SF"
                          value={data.rollover_assumptions.tiNewPerSf ?? ''}
                          onChange={(e) => setRolloverField('tiNewPerSf', e.target.value)} />
                        <Input label="TI, renewal" type="number" rightAddon="$/SF"
                          value={data.rollover_assumptions.tiRenewalPerSf ?? ''}
                          onChange={(e) => setRolloverField('tiRenewalPerSf', e.target.value)} />
                        <Input label="LC, new" type="number" rightAddon="%"
                          value={data.rollover_assumptions.lcNewPercent ?? ''}
                          onChange={(e) => setRolloverField('lcNewPercent', e.target.value)} />
                        <Input label="LC, renewal" type="number" rightAddon="%"
                          value={data.rollover_assumptions.lcRenewalPercent ?? ''}
                          onChange={(e) => setRolloverField('lcRenewalPercent', e.target.value)} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Model versions (Phase 3C) */}
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-800 uppercase">Model Versions</h4>
                <p className="text-xs text-slate-600 mt-1">
                  Save the deal as it stands now, so "at ask" and "at counter" are two versions of
                  one model instead of two PDFs. Metrics are stored as they read today and are
                  never recomputed.
                </p>

                <div className="flex items-end gap-2 mt-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      label="Version label"
                      value={versionLabel}
                      placeholder="At counter $4.6M"
                      onChange={(e) => setVersionLabel(e.target.value)}
                    />
                  </div>
                  <button
                    onClick={saveVersion}
                    disabled={versionBusy || !versionLabel.trim()}
                    className="text-xs bg-slate-800 text-white px-3 py-2 rounded hover:bg-slate-900 font-medium disabled:opacity-40"
                  >
                    Save version
                  </button>
                </div>

                {versions.length > 0 && (
                  <ul className="mt-4 space-y-1">
                    {versions.map(v => (
                      <li key={v.id} className="text-xs text-gray-700 flex flex-wrap gap-x-3">
                        <span className="font-medium">{v.label}</span>
                        <span className="text-gray-500">{new Date(v.created_at).toLocaleDateString()}</span>
                        {typeof v.metrics?.irr === 'number' && <span>IRR {formatPercent(v.metrics.irr as number)}</span>}
                        {typeof v.metrics?.purchase_price === 'number' && <span>{formatCurrency(v.metrics.purchase_price as number)}</span>}
                        {typeof v.metrics?.verdict === 'string' && <span className="font-medium">{v.metrics.verdict as string}</span>}
                      </li>
                    ))}
                  </ul>
                )}

                {versions.length >= 2 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-end gap-2 flex-wrap">
                      <Select
                        label="Compare"
                        value={diffPair.a}
                        onChange={(e) => setDiffPair(p => ({ ...p, a: e.target.value }))}
                        options={[{ value: '', label: 'Select...' }, ...versions.map(v => ({ value: v.id, label: v.label }))]}
                      />
                      <Select
                        label="With"
                        value={diffPair.b}
                        onChange={(e) => setDiffPair(p => ({ ...p, b: e.target.value }))}
                        options={[{ value: '', label: 'Select...' }, ...versions.map(v => ({ value: v.id, label: v.label }))]}
                      />
                      <button
                        onClick={runDiff}
                        disabled={versionBusy || !diffPair.a || !diffPair.b || diffPair.a === diffPair.b}
                        className="text-xs bg-white border border-slate-400 text-slate-800 px-3 py-2 rounded hover:bg-slate-100 font-medium disabled:opacity-40"
                      >
                        Compare
                      </button>
                    </div>

                    {diffResult && (
                      <div className="mt-3 text-xs">
                        <p className="font-medium text-slate-800">{diffResult.from} vs {diffResult.to}</p>
                        {diffResult.isIdentical && <p className="text-gray-600 mt-1">Nothing changed between these two.</p>}

                        {diffResult.verdictChange && (
                          <p className="mt-1 font-semibold text-slate-900">
                            Verdict {diffResult.verdictChange.from} to {diffResult.verdictChange.to}
                          </p>
                        )}

                        {diffResult.metricChanges.length > 0 && (
                          <ul className="mt-2 space-y-0.5">
                            {diffResult.metricChanges.map(m => (
                              <li key={m.metric} className={m.delta > 0 ? 'text-green-700' : 'text-red-700'}>
                                {m.metric.replace(/_/g, ' ')}: {m.from.toFixed(2)} to {m.to.toFixed(2)}
                                {' '}({m.delta > 0 ? '+' : ''}{m.delta.toFixed(2)})
                              </li>
                            ))}
                          </ul>
                        )}

                        {diffResult.inputChanges.length > 0 && (
                          <>
                            <p className="mt-2 text-gray-600">Inputs that changed:</p>
                            <ul className="mt-1 space-y-0.5 text-gray-700">
                              {diffResult.inputChanges.map(c => (
                                <li key={c.field}>
                                  {c.field.replace(/_/g, ' ')}: {String(c.from ?? 'not set')} to {String(c.to ?? 'not set')}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Lease Structure */}
              <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
                <h4 className="text-sm font-semibold text-indigo-800 uppercase mb-3">Lease Structure</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Select
                    label="Lease Type"
                    value={data.lease_type || 'gross'}
                    onChange={(e) => handleChange('lease_type', e.target.value)}
                    options={[
                      { value: 'gross', label: 'Gross Lease' },
                      { value: 'nnn', label: 'NNN (Triple Net)' },
                      { value: 'modified_gross', label: 'Modified Gross' },
                    ]}
                  />
                  {leaseType === 'nnn' && (
                    <Input label="NNN Recovery %" type="number" step="1" rightAddon="%" value={data.nnn_reimbursement_rate ?? 100} onChange={(e) => handleChange('nnn_reimbursement_rate', e.target.value)} />
                  )}
                  {leaseType === 'modified_gross' && (
                    <Input label="Annual Reimbursement" type="number" leftAddon="$" value={data.modified_gross_reimbursement || ''} onChange={(e) => handleChange('modified_gross_reimbursement', e.target.value)} />
                  )}
                </div>
                {nnnReimbursements > 0 && (
                  <div className="flex justify-between text-sm pt-3 mt-3 border-t border-indigo-200 font-medium text-indigo-700">
                    <span>Expense Reimbursement Income</span>
                    <span>+{formatCurrency(nnnReimbursements)}/yr</span>
                  </div>
                )}
              </div>

              {/* Income & Expense Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                  <h4 className="text-sm font-semibold text-green-800 uppercase mb-3">Income</h4>
                  <div className="space-y-3">
                    <div>
                      <Input label="Potential Gross Income" type="number" leftAddon="$" value={data.potential_gross_income || ''} onChange={(e) => handleChange('potential_gross_income', e.target.value)} />
                      {/* ⭐⭐⭐ THE SEED SAYS WHAT IT DID. An income line derived from a stated NOI
                          is a modelling decision, not an extraction, and a reader who disagrees
                          has to be able to see it and overwrite it. Silent would be a guess. */}
                      {statedNoiSeed && (
                        <div className="mt-2 rounded-lg bg-sky-50 border border-sky-300 p-3">
                          <p className="text-xs text-sky-900">{statedNoiSeed.because}</p>
                          <p className="text-xs text-sky-800 mt-1">
                            Overwrite either side and this deal prices on what you type instead.
                          </p>
                        </div>
                      )}
                      {/* ⭐⭐ F1 - the OM's headline and its own rent roll disagree. Stated, never
                          silently resolved: only a human reading the OM can tell real other
                          income from a stale or forward headline. */}
                      {noiRebase.message && (
                        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-300 p-3">
                          <p className="text-xs text-amber-900">{noiRebase.message}</p>
                        </div>
                      )}
                      {inPlaceIncome.message && (
                        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-300 p-3">
                          <p className="text-xs text-amber-900">{inPlaceIncome.message}</p>
                          {typeof inPlaceIncome.basis === 'number' && inPlaceIncome.source === 'rent_roll'
                            && Math.abs(inPlaceIncome.basis - pgi) >= 1 && (
                            <button
                              type="button"
                              onClick={() => handleChange('potential_gross_income', String(Math.round(inPlaceIncome.basis as number)))}
                              className="mt-2 text-xs bg-amber-600 text-white px-2.5 py-1 rounded hover:bg-amber-700 font-medium"
                            >
                              Use {formatCurrency(inPlaceIncome.basis)} from the rent roll
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label htmlFor="vacancy-rate" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Vacancy %<SourceDot field="vacancy_rate" />
                      </label>
                      <Input id="vacancy-rate" type="number" rightAddon="%" value={data.vacancy_rate || ''} onChange={(e) => handleChange('vacancy_rate', e.target.value)} />
                    </div>
                    <Input label="Other Income" type="number" leftAddon="$" value={data.other_income || ''} onChange={(e) => handleChange('other_income', e.target.value)} />
                    {/*
                      ⭐⭐ RECOVERIES REIMBURSE EXPENSES, SO THEY GROW WITH THEM. This line lives
                      inside the year-1 in-place NOI and was held FLAT across the hold while
                      expenses grew beside it - understating NOI by construction on any deal with a
                      recovery line. Blank keeps it flat, which is how every saved analysis was
                      priced. Measured on Stagecoach: $2,231 by year 5.
                    */}
                    <div>
                      <label htmlFor="other-income-growth" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Other Income Growth %<SourceDot field="other_income_growth_rate" />
                      </label>
                      <Input id="other-income-growth" type="number" rightAddon="%" placeholder="flat"
                        value={data.other_income_growth_rate ?? ''}
                        onChange={(e) => handleChange('other_income_growth_rate', e.target.value)} />
                      <p className="mt-1 text-xs text-gray-500">
                        Blank holds it flat. Expense recoveries usually track the expense growth rate.
                      </p>
                    </div>
                    {nnnReimbursements > 0 && (
                      <div className="flex justify-between text-sm text-indigo-700">
                        <span>Expense Reimbursements</span><span className="font-medium">+{formatCurrency(nnnReimbursements)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t border-green-300 font-bold">
                      <span>EGI</span><span>{formatCurrency(egi)}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                  <h4 className="text-sm font-semibold text-red-800 uppercase mb-3">Expenses</h4>
                  <div className="space-y-3">
                    <div className="relative">
                      <label htmlFor="property-taxes" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Property Taxes<SourceDot field="property_taxes" />
                      </label>
                      <Input id="property-taxes" type="number" leftAddon="$" value={data.property_taxes || ''} onChange={(e) => handleChange('property_taxes', e.target.value)} />
                      {data.auto_property_taxes && <span className="absolute top-0 right-0 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-medium">auto</span>}
                    </div>
                    {/*
                      ⭐⭐ TAXES GROW AT THEIR OWN RATE. Prop 13 caps assessed-value growth at 2%
                      while insurance, CAM and utilities run with inflation. Blank keeps the single
                      expense growth rate, which is what every saved analysis was priced under.
                    */}
                    <div>
                      <label htmlFor="tax-growth" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Tax Growth %<SourceDot field="property_tax_growth_rate" />
                      </label>
                      <Input id="tax-growth" type="number" rightAddon="%"
                        placeholder={`Prop 13 cap ${PROP_13_ANNUAL_CAP_PERCENT}%`}
                        value={data.property_tax_growth_rate ?? ''}
                        onChange={(e) => handleChange('property_tax_growth_rate', e.target.value)} />
                      <p className="mt-1 text-xs text-gray-500">
                        Blank grows taxes with the other expenses. California caps assessed value at{' '}
                        {PROP_13_ANNUAL_CAP_PERCENT}% a year.
                      </p>
                    </div>
                    <div className="relative">
                      <label htmlFor="insurance" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Insurance<SourceDot field="insurance" />
                      </label>
                      <Input id="insurance" type="number" leftAddon="$" value={data.insurance || ''} onChange={(e) => handleChange('insurance', e.target.value)} />
                      {data.auto_insurance && <span className="absolute top-0 right-0 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-medium">auto</span>}
                    </div>
                    <div className="relative">
                      <label htmlFor="utilities" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Utilities<SourceDot field="utilities" />
                      </label>
                      <Input id="utilities" type="number" leftAddon="$" value={data.utilities || ''} onChange={(e) => handleChange('utilities', e.target.value)} />
                      {data.auto_utilities && <span className="absolute top-0 right-0 text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-medium">auto</span>}
                    </div>
                    <div>
                      <label htmlFor="mgmt-fee-percent" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Mgmt Fee %<SourceDot field="management_fee_percent" />
                      </label>
                      <Input id="mgmt-fee-percent" type="number" rightAddon="%" value={data.management_fee_percent || ''} onChange={(e) => handleChange('management_fee_percent', e.target.value)} />
                    </div>
                    <Input label="Repairs" type="number" leftAddon="$" value={data.repairs_maintenance || ''} onChange={(e) => handleChange('repairs_maintenance', e.target.value)} />
                    <Input label="Reserves/CapEx" type="number" leftAddon="$" value={data.reserves_capex || ''} onChange={(e) => handleChange('reserves_capex', e.target.value)} />
                    <Input label="Other" type="number" leftAddon="$" value={data.other_expenses || ''} onChange={(e) => handleChange('other_expenses', e.target.value)} />
                    <div className="flex justify-between text-sm pt-2 border-t border-red-300 font-bold">
                      <span>Total Expenses</span><span>{formatCurrency(totalExpenses)}</span>
                    </div>
                    {/*
                      ⭐⭐⭐ WHERE THE MONEY GOES, not how much it is. On an asset that earns nothing
                      these same figures are a CARRY: they reduce cash flow and never NOI. Charged
                      against zero income instead, NOI goes negative, the exit goes negative with
                      it, and the IRR prints a sentinel. That is the Victorville defect, and land
                      is that condition permanently.
                    */}
                    <label htmlFor="expenses-are-carry"
                      className="col-span-2 flex items-start gap-2 pt-2 text-sm text-gray-700 cursor-pointer">
                      <input id="expenses-are-carry" type="checkbox" className="mt-0.5"
                        checked={data.expenses_are_carry === true}
                        onChange={(e) => handleChange('expenses_are_carry', e.target.checked)} />
                      <span>
                        These are <strong>carry costs</strong>, not operating expenses
                        <span className="block text-xs text-gray-500">
                          For land and other assets that earn nothing. Reduces cash flow, never NOI.
                        </span>
                      </span>
                    </label>
                    {/*
                      ⛔ SAY IT, DO NOT SILENTLY ZERO IT. The recovery rate stays stored and comes
                      straight back when the flag is turned off; what changes is that it is not
                      applied. A number that vanishes without explanation is how a screen comes to
                      disagree with the field beside it.
                    */}
                    {recoveries.suppressedByCarry && (
                      <div className="col-span-2 text-sm text-amber-900 bg-amber-50 rounded-lg px-3 py-2">
                        <strong>Expense recoveries are not being applied.</strong> A carry has no
                        tenant to reimburse it, so the {leaseType === 'nnn' ? 'NNN recovery rate' : 'modified-gross reimbursement'}{' '}
                        is ignored while these are carry costs. The setting is kept and applies again
                        if you untick the box above.
                      </div>
                    )}
                    {expensesAreCarry && projections.length > 0 && (
                      <div className="col-span-2 text-sm text-emerald-900 bg-emerald-50 rounded-lg px-3 py-2">
                        Carried as capital: {formatCurrency(projections[0].carryCosts)} in year 1 rising to{' '}
                        {formatCurrency(projections[projections.length - 1].carryCosts)} by year {projections.length},{' '}
                        {formatCurrency(projections.reduce((a, p) => a + p.carryCosts, 0))} over the hold.
                        NOI stays at {formatCurrency(projections[0].noi)}.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Primary Loan */}
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-800 uppercase mb-3">Primary Loan</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Input label="Purchase Price" type="number" leftAddon="$" value={data.purchase_price || ''} onChange={(e) => handleChange('purchase_price', e.target.value)} />
                  <Input label="LTV %" type="number" rightAddon="%" value={data.ltv_percent || ''} onChange={(e) => handleChange('ltv_percent', e.target.value)} />
                  <Input label="Interest Rate" type="number" step="0.125" rightAddon="%" value={data.interest_rate || ''} onChange={(e) => handleChange('interest_rate', e.target.value)} />
                  <Input label="Amortization" type="number" rightAddon="yrs" value={data.amortization_years || ''} onChange={(e) => handleChange('amortization_years', e.target.value)} />
                  <Input label="Closing Costs" type="number" rightAddon="%" value={data.closing_costs_percent || ''} onChange={(e) => handleChange('closing_costs_percent', e.target.value)} />
                </div>
              </div>

              {/* Seller Carryback */}
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                <h4 className="text-sm font-semibold text-amber-800 uppercase mb-3">Seller Carryback</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Input label="Carryback Amount" type="number" leftAddon="$" value={data.seller_carryback_amount || ''} onChange={(e) => handleChange('seller_carryback_amount', e.target.value)} />
                  <Input label="Interest Rate" type="number" step="0.125" rightAddon="%" value={data.seller_carryback_rate || ''} onChange={(e) => handleChange('seller_carryback_rate', e.target.value)} />
                  <Input label="Term" type="number" rightAddon="yrs" value={data.seller_carryback_term || ''} onChange={(e) => handleChange('seller_carryback_term', e.target.value)} />
                  <div>
                    <label htmlFor="carryback-amortization" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Amortization
                    </label>
                    <select id="carryback-amortization"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={data.seller_carryback_interest_only ? 'interest_only' : 'amortizing'}
                      onChange={(e) => handleChange('seller_carryback_interest_only', e.target.value === 'interest_only')}>
                      <option value="amortizing">Amortizing over the term</option>
                      <option value="interest_only">Interest-only, balance due at exit</option>
                    </select>
                  </div>
                </div>
                {/*
                  ⭐⭐ THE MECHANIC IN WORDS. An interest-only note pays interest and nothing else,
                  so the principal is still there at the sale - which is the whole of what the exit
                  has to retire. It is the natural shape for LAND, which has no income to amortize
                  principal out of.
                */}
                {carrybackInterestOnly && effectiveCarrybackAmt > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-200 text-sm text-amber-900">
                    Interest-only: {formatCurrency(sellerAnnualDS)} of interest every year and{' '}
                    {formatCurrency(effectiveCarrybackAmt)} still outstanding at the sale. The term
                    is the balloon date, not an amortization schedule.
                  </div>
                )}
                {sellerCarrybackAmt > 0 && (
                  <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-amber-200 text-sm">
                    <div><span className="text-gray-600">Monthly Payment:</span> <span className="font-medium">{formatCurrency(sellerMonthlyPmt)}</span></div>
                    <div><span className="text-gray-600">Annual DS:</span> <span className="font-medium">{formatCurrency(sellerAnnualDS)}</span></div>
                  </div>
                )}
              </div>

              {/* Financing Fees - a cost that attaches to the FINANCING, not the purchase or the property */}
              <div className="bg-violet-50 rounded-xl p-5 border border-violet-200">
                <h4 className="text-sm font-semibold text-violet-800 uppercase mb-1">Financing Fees</h4>
                <p className="text-xs text-violet-700 mb-3">
                  A cost that attaches to the FINANCING - an origination fee, an SBA guaranty fee, a loan
                  assumption fee. Closing costs attach to the purchase; operating expenses to the property.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input label="Financing Fee" type="number" leftAddon="$"
                    value={data.financing_fee_amount ?? ''}
                    onChange={(e) => handleChange('financing_fee_amount', e.target.value)} />
                  <div>
                    <label htmlFor="financing-fee-financed" className="block text-sm font-medium text-gray-700 mb-1.5">
                      How it is paid
                    </label>
                    <select id="financing-fee-financed"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={data.financing_fee_financed ? 'financed' : 'cash'}
                      onChange={(e) => handleChange('financing_fee_financed', e.target.value === 'financed')}>
                      <option value="cash">Paid in cash at closing</option>
                      <option value="financed">Financed into the loan</option>
                    </select>
                  </div>
                  {data.financing_fee_financed && sellerCarrybackAmt > 0 && (
                    <div>
                      <label htmlFor="financing-fee-note" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Rides into
                      </label>
                      <select id="financing-fee-note"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        value={data.financing_fee_note ?? 'primary'}
                        onChange={(e) => handleChange('financing_fee_note', e.target.value)}>
                        <option value="primary">The primary loan</option>
                        <option value="secondary">The second note</option>
                      </select>
                    </div>
                  )}
                  <Input label="Loan Servicing Fee" type="number" step="0.0001" rightAddon="%/yr"
                    value={data.loan_servicing_fee_rate ?? ''}
                    onChange={(e) => handleChange('loan_servicing_fee_rate', e.target.value)} />
                </div>
                {/*
                  ⭐⭐ THE MECHANIC IS STATED IN WORDS, not left to be inferred from a checkbox. The
                  same number has opposite effects on every return metric depending on this one
                  choice, so the screen says which one it applied.
                */}
                {financingFee.fee > 0 && (
                  <div className="mt-3 pt-3 border-t border-violet-200 text-sm text-violet-900">
                    {financingFee.cashCost > 0
                      ? `${formatCurrency(financingFee.fee)} is paid in cash at closing - it raises Total Cash Required and does not change the debt.`
                      : `${formatCurrency(financingFee.fee)} rides into the ${financingFee.secondaryUplift > 0 ? 'second note' : 'primary loan'} - it raises the DEBT and the cash does not move.`}
                  </div>
                )}
                {financingFee.servicingFeeRate !== null && financingFee.servicingFeeRate > 0 && projections.length > 0 && (
                  <div className="mt-2 text-sm text-violet-900">
                    Ongoing servicing fee: {formatCurrency(projections[0].servicingFee)} in year 1, falling to{' '}
                    {formatCurrency(projections[projections.length - 1].servicingFee)} by year {projections.length}.
                    It reduces cash flow and is deliberately excluded from DSCR.
                  </div>
                )}
              </div>

              {/* Financing Summary */}
              <div className="bg-gray-100 rounded-xl p-4 border">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                  <div><span className="text-gray-600">Bank Loan:</span> <span className="font-medium">{formatCurrency(loanAmount)}</span></div>
                  <div><span className="text-gray-600">Monthly Pmt:</span> <span className="font-medium">{formatCurrency(monthlyPmt)}</span></div>
                  {effectiveCarrybackAmt > 0 && (
                    <div><span className="text-gray-600">Seller Loan:</span> <span className="font-medium">{formatCurrency(effectiveCarrybackAmt)}</span></div>
                  )}
                  <div><span className="text-gray-600">Down Payment:</span> <span className="font-medium">{formatCurrency(downPayment)}</span></div>
                  {financingFee.cashCost > 0 && (
                    <div><span className="text-gray-600">Financing Fee:</span> <span className="font-medium">{formatCurrency(financingFee.cashCost)}</span></div>
                  )}
                  <div><span className="text-gray-600">Total DS:</span> <span className="font-medium">{formatCurrency(annualDS / 12)}/mo</span></div>
                  <div><span className="text-gray-600">Total DS:</span> <span className="font-medium">{formatCurrency(annualDS)}/yr</span></div>
                  <div><span className="text-gray-600">DSCR:</span> <span className={`font-medium ${dscr >= 1.25 ? 'text-green-600' : 'text-red-600'}`}>{formatRatio(dscr)}</span></div>
                </div>
              </div>

              {/* Projection Table */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b">
                  <h4 className="text-sm font-semibold text-gray-700">{holdingPeriod}-Year Proforma</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-600 font-medium">Year</th>
                        <th className="px-3 py-2 text-right text-gray-600 font-medium">Income</th>
                        <th className="px-3 py-2 text-right text-gray-600 font-medium">Expenses</th>
                        <th className="px-3 py-2 text-right text-gray-600 font-medium">NOI</th>
                        <th className="px-3 py-2 text-right text-gray-600 font-medium">Debt Svc</th>
                        {/*
                          ⭐⭐ THE COLUMN APPEARS ONLY WHEN THE DEAL PAYS ONE. It is deducted from
                          Cash Flow and is deliberately NOT in Debt Svc, because DSCR is struck on
                          debt service alone - so without a column of its own the deduction would
                          be invisible and Cash Flow would not equal NOI less Debt Svc. ⛔ A
                          permanent 8th column of zeros would also widen a table that has to work
                          at 250px.
                        */}
                        {showServicingFeeColumn && (
                          <th className="px-3 py-2 text-right text-gray-600 font-medium">Svc Fee</th>
                        )}
                        {/* ⭐ Same rule as Svc Fee: the column appears only on a deal that pays one. */}
                        {showCarryColumn && (
                          <th className="px-3 py-2 text-right text-gray-600 font-medium">Carry</th>
                        )}
                        <th className="px-3 py-2 text-right text-gray-600 font-medium">Cash Flow</th>
                        <th className="px-3 py-2 text-right text-gray-600 font-medium">CoC</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {projections.map((yr) => (
                        <tr key={yr.year} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{yr.year}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(yr.income)}</td>
                          <td className="px-3 py-2 text-right text-red-600">{formatCurrency(yr.expenses)}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(yr.noi)}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(yr.debtService)}</td>
                          {showServicingFeeColumn && (
                            <td className="px-3 py-2 text-right text-red-600">{formatCurrency(yr.servicingFee)}</td>
                          )}
                          {showCarryColumn && (
                            <td className="px-3 py-2 text-right text-red-600">{formatCurrency(yr.carryCosts)}</td>
                          )}
                          <td className={`px-3 py-2 text-right font-medium ${yr.cashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(yr.cashFlow)}</td>
                          <td className="px-3 py-2 text-right">{formatPercent(calculateCashOnCash(yr.cashFlow, totalCashInvested))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB 5: Exit Analysis
             ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'exit' && (
            <div className="space-y-6">
              {/* Exit inputs */}
              <div className="bg-gray-50 rounded-xl p-5 border">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Exit Assumptions</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Input label="Exit Cap Rate" type="number" step="0.05" rightAddon="%" value={data.exit_cap_rate || ''} onChange={(e) => handleChange('exit_cap_rate', e.target.value)} />
                  <Input label="Selling Costs" type="number" step="0.1" rightAddon="%" value={data.selling_costs_percent || ''} onChange={(e) => handleChange('selling_costs_percent', e.target.value)} />
                  <Input label="Exit Value (overrides the cap)" type="number" leftAddon="$"
                    value={data.exit_value ?? ''}
                    onChange={(e) => handleChange('exit_value', e.target.value)} />
                  <div className="flex items-end text-sm text-gray-600">
                    {suggestedExitCap && <p>Comp-suggested exit cap: {formatPercent(suggestedExitCap)}</p>}
                  </div>
                </div>
                {/*
                  ⭐⭐⭐ THE SCREEN SAYS WHICH BASIS PRICED THE SALE. An income property's exit is a
                  YIELD; land's cannot be, because a cap rate on zero NOI is undefined. Leaving the
                  basis to be inferred from which box happens to be filled is how a document comes
                  to state a number nobody can account for.
                  ⛔ ONE input for all three land methods - appreciation, residual and entitlement
                  each produce a VALUE, and which one applies is Joseph's to record, not the app's
                  to guess.
                */}
                {exitIsStatedValue ? (
                  <div className="mt-3 pt-3 border-t text-sm text-indigo-900 bg-indigo-50 -mx-5 -mb-5 px-5 py-3 rounded-b-xl">
                    Exit priced on a <strong>stated value of {formatCurrency(saleProceeds.salePrice)}</strong>,
                    not on NOI divided by a cap rate. The exit cap rate above is ignored while this is set.
                    {exitNOI <= 0 && ' This property produces no income in the exit year, so a cap rate could not price it at all.'}
                    {' '}Record which valuation method produced the figure in your analysis - the app does not assume one.
                  </div>
                ) : Math.abs(exitNOI) < 0.5 ? (
                  /* ⭐ JJ3: this sentence fires on the figure the waterfall actually priced, not on the in-place NOI. */
                  <div className="mt-3 pt-3 border-t text-sm text-amber-900 bg-amber-50 -mx-5 -mb-5 px-5 py-3 rounded-b-xl">
                    <strong>This property produces no income in {exitNOIYearLabel}, so the exit cannot be priced on a cap rate.</strong>{' '}
                    A cap rate on zero NOI is undefined and the sale prices at $0. Enter an Exit Value above -
                    from appreciation, a residual land value, or a per-entitled-unit figure.
                  </div>
                ) : exitNOI < 0 ? (
                  <div className="mt-3 pt-3 border-t text-sm text-red-900 bg-red-50 -mx-5 -mb-5 px-5 py-3 rounded-b-xl">
                    <strong>NOI is negative in {exitNOIYearLabel} ({formatCurrency(exitNOI)}), so a cap-rate exit is negative and does not price the asset.</strong>{' '}
                    Expenses exceed income in that year. If the building is vacant and these are carry costs, tick
                    "carry costs" on the Proforma tab so NOI stays at $0; otherwise enter an Exit Value above.
                  </div>
                ) : null}
              </div>

              {/* Exit waterfall */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b">
                  <h4 className="text-sm font-semibold text-gray-700">Sale Proceeds Waterfall (Year {holdingPeriod})</h4>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Exit NOI ({exitNOIYearLabel})</span>
                    <span className="font-medium">{formatCurrency(exitNOI)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Exit Cap Rate</span>
                    <span className="font-medium">{formatPercent(exitCapRate)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                    <span>Gross Sale Price</span>
                    <span>{formatCurrency(saleProceeds.salePrice)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Less: Selling Costs ({formatPercent(sellingCostsPercent)})</span>
                    <span>-{formatCurrency(saleProceeds.sellingCosts)}</span>
                  </div>
                  {/* ⭐⭐ EACH NOTE AT ITS OWN EXIT-YEAR BALANCE, and the two lines SUM to the net
                      below them. This block used to print the bank's balance beside the
                      carryback's ORIGINAL PRINCIPAL - a payoff line for a note the model never
                      retired - so the rows never added up to the figure underneath. */}
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Less: Bank Loan Payoff{exitSecondBalance > 0 ? ' (Year 5 balance)' : ''}</span>
                    <span>-{formatCurrency(exitBankBalance)}</span>
                  </div>
                  {exitSecondBalance > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Less: Second Note Payoff (Year 5 balance)</span>
                      <span>-{formatCurrency(exitSecondBalance)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 border-t-2 border-primary-300 font-bold text-lg">
                    <span>Net Sale Proceeds</span>
                    <span className={saleProceeds.netToSeller >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(saleProceeds.netToSeller)}
                    </span>
                  </div>
                </div>

                {/* The same sale across a range of exit caps. Same exitNOI, same loan
                    balance, same selling costs, same calculateSaleProceeds - only the cap
                    moves, so the highlighted column reproduces the waterfall above exactly. */}
                {exitCapLadder.length > 0 && (
                  <div className="border-t">
                    <div className="px-5 py-3 bg-gray-50 border-b">
                      <h4 className="text-sm font-semibold text-gray-700">Value at Different Exit Caps</h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Same {exitNOIYearLabel} NOI of {formatCurrency(exitNOI)}, priced at each cap.
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">Exit Cap</th>
                            {exitCapLadder.map((r) => (
                              <th
                                key={r.cap}
                                className={`px-4 py-2 text-center font-semibold ${
                                  r.isCurrent ? 'bg-primary-100 text-primary-700' : 'text-gray-600'
                                }`}
                              >
                                {r.cap.toFixed(2)}%
                                {r.isCurrent && <span className="block text-xs font-normal">Current</span>}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="px-4 py-2 font-medium text-gray-700">Gross Sale Price</td>
                            {exitCapLadder.map((r) => (
                              <td key={r.cap} className={`px-4 py-2 text-center ${r.isCurrent ? 'bg-primary-50 font-semibold' : 'text-gray-600'}`}>
                                {formatCurrency(r.salePrice)}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-b">
                            <td className="px-4 py-2 font-medium text-gray-700">Net Proceeds</td>
                            {exitCapLadder.map((r) => (
                              <td key={r.cap} className={`px-4 py-2 text-center ${r.isCurrent ? 'bg-primary-50 font-semibold' : ''} ${r.netToSeller >= 0 ? 'text-gray-600' : 'text-red-600'}`}>
                                {formatCurrency(r.netToSeller)}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="px-4 py-2 font-medium text-gray-700">vs. Purchase Price</td>
                            {exitCapLadder.map((r) => (
                              <td
                                key={r.cap}
                                className={`px-4 py-2 text-center ${r.isCurrent ? 'bg-primary-50 font-semibold' : ''} ${
                                  r.vsBasis >= 0 ? 'text-green-600' : 'text-red-600'
                                }`}
                              >
                                {r.vsBasis >= 0 ? '+' : ''}{r.vsBasis.toFixed(1)}%
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="px-5 py-2 text-xs text-gray-500 border-t">
                      A lower exit cap means a higher sale price. Net proceeds are after selling
                      costs and loan payoff, matching the waterfall above.
                    </p>
                  </div>
                )}
              </div>

              {/* Value Creation Summary */}
              {stabilizedNOI > 0 ? (
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-200 overflow-hidden">
                  <div className="px-5 py-3 bg-indigo-100/60 border-b border-indigo-200">
                    <h4 className="text-sm font-semibold text-indigo-800 uppercase tracking-wide">Value Creation Summary</h4>
                  </div>
                  <div className="p-5 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Purchase Price</span>
                      <span className="font-medium">{formatCurrency(purchasePrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">+ Closing Costs</span>
                      <span className="font-medium">{formatCurrency(closingCosts)}</span>
                    </div>
                    {vaTotalCost > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">+ Value-Add Costs</span>
                        <span className="font-medium">{formatCurrency(vaTotalCost)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-indigo-200 font-semibold">
                      <span>Total Project Cost</span>
                      <span>{formatCurrency(totalProjectCost)}</span>
                    </div>

                    <div className="pt-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Stabilized NOI</span>
                        <span className="font-medium">{formatCurrency(stabilizedNOI)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">&divide; Exit Cap Rate</span>
                        <span className="font-medium">{formatPercent(exitCapRate)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-indigo-200 font-semibold">
                        <span>Pro Forma Value</span>
                        <span>{formatCurrency(stabilizedValue)}</span>
                      </div>
                    </div>

                    <div className="pt-3 mt-1 border-t-2 border-indigo-300 space-y-2">
                      <div className="flex justify-between font-bold text-base">
                        <span>Equity Created</span>
                        <span className={equityCreated >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(equityCreated)}
                        </span>
                      </div>
                      <div className="flex justify-between font-bold text-base">
                        <span>Profit on Sale</span>
                        <span className={profitOnSale >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(profitOnSale)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Cost-to-Value</span>
                        <span className="font-medium">{formatPercent(costToValue)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-5 border text-center">
                  <p className="text-sm text-gray-500">Fill in stabilized assumptions on the Value-Add tab to see value creation analysis</p>
                </div>
              )}

              {/* Return Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-primary-50 rounded-xl p-4 text-center border border-primary-200">
                  <p className="text-xs text-primary-600 uppercase font-medium">IRR</p>
                  <p className={`text-3xl font-bold ${irrValue >= thresholds.irr ? 'text-green-600' : 'text-red-600'}`}>
                    {formatPercent(irrValue)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Target: {formatPercent(thresholds.irr)}</p>
                </div>
                <div className="bg-primary-50 rounded-xl p-4 text-center border border-primary-200">
                  <p className="text-xs text-primary-600 uppercase font-medium">Equity Multiple</p>
                  <p className={`text-3xl font-bold ${equityMultiple >= thresholds.equity_multiple ? 'text-green-600' : 'text-red-600'}`}>
                    {formatRatio(equityMultiple)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Target: {formatRatio(thresholds.equity_multiple)}</p>
                </div>
                <div className="bg-primary-50 rounded-xl p-4 text-center border border-primary-200">
                  <p className="text-xs text-primary-600 uppercase font-medium">Avg CoC</p>
                  <p className={`text-3xl font-bold ${avgCashOnCash >= thresholds.cash_on_cash ? 'text-green-600' : 'text-red-600'}`}>
                    {formatPercent(avgCashOnCash)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Target: {formatPercent(thresholds.cash_on_cash)}</p>
                  {vaTotalCost > 0 && <p className="text-[10px] text-gray-400 mt-1">Incl. value-add CapEx</p>}
                </div>
                <div className="bg-primary-50 rounded-xl p-4 text-center border border-primary-200">
                  <p className="text-xs text-primary-600 uppercase font-medium">Total Cash Invested</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalCashInvested)}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatCurrency(totalCashRequired)} + {formatCurrency(vaTotalCost)} VA</p>
                </div>
              </div>

              {/* NPV */}
              <div className="bg-gray-50 rounded-xl p-5 border">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Net Present Value</h4>
                <p className="text-2xl font-bold">{formatCurrency(calculateNPV(irrCashFlows, 10))}</p>
                <p className="text-xs text-gray-500">At 10% discount rate</p>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB 6: Decision Dashboard
             ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'decision' && (
            <div className="space-y-6">
              {/*
                ⭐⭐⭐ THE RECEIPT. It names what was checked, so when it is wrong you know where to
                look - and it can REFUSE. ⛔ A check that can only say yes is a decoration: it
                makes a reader read LESS carefully while catching nothing, which trades a reliable
                checker for an unreliable one. Every line here is a defect that actually shipped.
              */}
              <div className={`rounded-xl border-2 p-4 ${
                preflight.blocking.length > 0 ? 'border-red-500 bg-red-50'
                  : preflight.attention.length > 0 ? 'border-amber-400 bg-amber-50'
                  : 'border-green-500 bg-green-50'}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <p className={`text-sm font-bold ${
                    preflight.blocking.length > 0 ? 'text-red-800'
                      : preflight.attention.length > 0 ? 'text-amber-900' : 'text-green-800'}`}>
                    {preflight.blocking.length > 0
                      ? 'NOT READY TO SEND'
                      : `Checked - ${preflight.passed.length} of ${preflight.checks.length} assertions passed`}
                  </p>
                  <p className="text-xs text-gray-600">
                    {preflight.blocking.length > 0 && `${preflight.blocking.length} must be resolved. `}
                    {preflight.attention.length > 0 && `${preflight.attention.length} need you.`}
                    {preflight.blocking.length === 0 && preflight.attention.length === 0
                      && 'Nothing outstanding. These are assertions, not a judgement about the deal.'}
                  </p>
                </div>

                {preflight.passed.length > 0 && (
                  <p className="mt-2 text-xs text-gray-700">
                    {preflight.passed.map(c => c.label).join(' \u00b7 ')}
                  </p>
                )}

                {[...preflight.blocking, ...preflight.attention].map(c => (
                  <div key={c.id} className="mt-3 flex gap-2 items-start">
                    <span className={`mt-0.5 text-xs font-bold shrink-0 ${
                      c.status === 'blocking' ? 'text-red-700' : 'text-amber-800'}`}>
                      {c.status === 'blocking' ? 'BLOCKS' : 'CHECK'}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-900">{c.label}</p>
                      <p className="text-xs text-gray-700">{c.detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Verdict */}
              <div className={`rounded-xl p-6 text-center ${
                verdict === 'GO' ? 'bg-green-100 border-2 border-green-500' :
                isAmberVerdict(verdict) ? 'bg-yellow-100 border-2 border-yellow-500' :
                'bg-red-100 border-2 border-red-500'
              }`}>
                <p className={`text-5xl font-black ${
                  verdict === 'GO' ? 'text-green-700' :
                  isAmberVerdict(verdict) ? 'text-yellow-700' :
                  'text-red-700'
                }`}>{verdict}</p>
                <p className="text-sm mt-2 text-gray-600">
                  {verdict === 'GO' && 'All applicable metrics clear and no material items outstanding'}
                  {verdict === 'CONDITIONAL' && `All ${scoredTotal} applicable metrics clear - subject to verification of the open items listed`}
                  {verdict === 'CAUTION' && `${passCount} of ${scoredTotal} metrics pass - one misses the bar`}
                  {verdict === 'NO GO' && `Only ${passCount} of ${scoredTotal} metrics pass - does not meet requirements`}
                </p>
              </div>

              {/* Scorecard */}
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Scorecard — {thresholdLabel}
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-gray-600 font-medium">Metric</th>
                        <th className="px-4 py-2 text-right text-gray-600 font-medium">Actual</th>
                        <th className="px-4 py-2 text-right text-gray-600 font-medium">Required</th>
                        <th className="px-4 py-2 text-center text-gray-600 font-medium">Status</th>
                        <th className="px-4 py-2 text-center text-gray-600 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {score.rows.map((m) => {
                        // Keyed by the stable metric key, not the display label:
                        // scoreDeal labels the first metric "Going-In Cap" while this
                        // table used to say "Going-In Cap Rate", so a label-keyed map
                        // would silently drop its Fix button.
                        const priceDesc = optimization && optimization.maxPurchasePrice > 0
                          ? `Set price to ${formatCurrency(optimization.maxPurchasePrice)}?`
                          : 'Not achievable via price alone';
                        const noiDesc = optimization && optimization.requiredNOI > 0
                          ? 'Adjust PGI to hit NOI target?'
                          : 'Not achievable via income alone';
                        const fixLeverMap: Record<string, { lever: string; desc: string }> = {
                          cap_rate: { lever: 'price', desc: priceDesc },
                          cash_on_cash: { lever: 'noi', desc: noiDesc },
                          irr: { lever: 'price', desc: priceDesc },
                          equity_multiple: { lever: 'price', desc: priceDesc },
                          dscr: { lever: 'noi', desc: noiDesc },
                        };
                        const fix = fixLeverMap[m.key];
                        return (
                          <tr key={m.key}>
                            <td className="px-4 py-3 font-medium">{m.label}</td>
                            <td className="px-4 py-3 text-right font-semibold">{fmtMetric(m.actual, m.format)}</td>
                            <td className="px-4 py-3 text-right text-gray-500">
                              {fmtMetric(m.threshold, m.format)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {m.pass ? (
                                <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  PASS
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-red-700 font-medium">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                  FAIL
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {!m.pass && optimization && fix ? (
                                confirmAction?.lever === `fix-${m.key}` ? (
                                  <span className="inline-flex items-center gap-1">
                                    <button onClick={() => { applyOptimization(fix.lever); setConfirmAction(null); }} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">{fix.desc}</button>
                                    <button onClick={() => setConfirmAction(null)} className="text-xs text-gray-400 hover:text-gray-600 px-1">&times;</button>
                                  </span>
                                ) : (
                                  <button onClick={() => setConfirmAction({ lever: `fix-${m.key}`, label: m.label })} className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded hover:bg-red-200 font-medium">Fix</button>
                                )
                              ) : m.pass ? (
                                <span className="text-green-500 text-xs">--</span>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Additional context */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 border">
                  <p className="text-xs text-gray-500 uppercase font-medium">Price/SF vs Market</p>
                  <p className="text-xl font-bold">${pricePerSqft.toFixed(0)}/SF</p>
                  {psfBenchmark && (
                    <p className="text-xs text-gray-500">Comp avg: ${psfBenchmark.avg.toFixed(0)}/SF ({pricePerSqft <= psfBenchmark.avg ? 'Below' : 'Above'} market)</p>
                  )}
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border">
                  <p className="text-xs text-gray-500 uppercase font-medium">Total Equity Required</p>
                  <p className="text-xl font-bold">{formatCurrency(totalCashInvested)}</p>
                  <p className="text-xs text-gray-500">{formatCurrency(downPayment)} + {formatCurrency(closingCosts)} closing + {formatCurrency(vaTotalCost)} VA</p>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB 7: Optimization (Goal-Seek)
             ══════════════════════════════════════════════════════════════ */}
          {activeTab === 'optimization' && (
            <div className="space-y-6">
              <div className="bg-primary-50 rounded-xl p-5 border border-primary-200">
                <h3 className="text-sm font-semibold text-primary-800 uppercase tracking-wide mb-2">What Makes This a GO?</h3>
                <p className="text-sm text-primary-600">
                  Target IRR: {formatPercent(thresholds.irr)} ({thresholdLabel})
                </p>
              </div>

              {optimization ? (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-gray-600 font-medium">Lever</th>
                          <th className="px-4 py-3 text-right text-gray-600 font-medium">Current</th>
                          <th className="px-4 py-3 text-right text-gray-600 font-medium">Required</th>
                          <th className="px-4 py-3 text-right text-gray-600 font-medium">Gap</th>
                          <th className="px-4 py-3 text-center text-gray-600 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr>
                          <td className="px-4 py-3 font-medium">Max Purchase Price</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(purchasePrice)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-primary-600">{optimization.maxPurchasePrice > 0 ? formatCurrency(optimization.maxPurchasePrice) : <span className="text-red-500">N/A</span>}</td>
                          <td className={`px-4 py-3 text-right font-medium ${optimization.maxPurchasePrice === 0 ? 'text-red-500' : optimization.maxPurchasePrice >= purchasePrice ? 'text-green-600' : 'text-red-600'}`}>
                            {optimization.maxPurchasePrice === 0 ? 'Not achievable' : `${optimization.maxPurchasePrice >= purchasePrice ? '+' : ''}${formatCurrency(optimization.maxPurchasePrice - purchasePrice)}`}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {optimization.maxPurchasePrice > 0 ? (
                              confirmAction?.lever === 'price' ? (
                                <span className="inline-flex items-center gap-1">
                                  <button onClick={() => { applyOptimization('price'); setConfirmAction(null); }} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">Set to {formatCurrency(optimization.maxPurchasePrice)}?</button>
                                  <button onClick={() => setConfirmAction(null)} className="text-xs text-gray-400 hover:text-gray-600 px-1">&times;</button>
                                </span>
                              ) : (
                                <button onClick={() => setConfirmAction({ lever: 'price', label: 'price' })} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded hover:bg-primary-200 font-medium">Apply</button>
                              )
                            ) : <span className="text-xs text-gray-400">—</span>}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 font-medium">Required NOI</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(noi)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-primary-600">{optimization.requiredNOI > 0 ? formatCurrency(optimization.requiredNOI) : <span className="text-red-500">N/A</span>}</td>
                          <td className={`px-4 py-3 text-right font-medium ${optimization.requiredNOI === 0 ? 'text-red-500' : optimization.requiredNOI <= noi ? 'text-green-600' : 'text-red-600'}`}>
                            {optimization.requiredNOI === 0 ? 'Not achievable' : `${optimization.requiredNOI <= noi ? '' : '+'}${formatCurrency(optimization.requiredNOI - noi)}`}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {optimization.requiredNOI > 0 ? (
                              confirmAction?.lever === 'noi' ? (
                                <span className="inline-flex items-center gap-1">
                                  <button onClick={() => { applyOptimization('noi'); setConfirmAction(null); }} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">Adjust PGI?</button>
                                  <button onClick={() => setConfirmAction(null)} className="text-xs text-gray-400 hover:text-gray-600 px-1">&times;</button>
                                </span>
                              ) : (
                                <button onClick={() => setConfirmAction({ lever: 'noi', label: 'noi' })} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded hover:bg-primary-200 font-medium">Apply</button>
                              )
                            ) : <span className="text-xs text-gray-400">—</span>}
                          </td>
                        </tr>
                        {sqft > 0 && (
                          <tr>
                            <td className="px-4 py-3 font-medium">Required Rent PSF</td>
                            <td className="px-4 py-3 text-right">${(pgi / sqft).toFixed(2)}/SF</td>
                            <td className="px-4 py-3 text-right font-semibold text-primary-600">${optimization.requiredRentPSF.toFixed(2)}/SF</td>
                            <td className={`px-4 py-3 text-right font-medium ${optimization.requiredRentPSF <= (pgi / sqft) ? 'text-green-600' : 'text-red-600'}`}>
                              {optimization.requiredRentPSF <= (pgi / sqft) ? '-' : '+'}${Math.abs(optimization.requiredRentPSF - (pgi / sqft)).toFixed(2)}/SF
                            </td>
                            <td className="px-4 py-3 text-center">
                              {confirmAction?.lever === 'rent' ? (
                                <span className="inline-flex items-center gap-1">
                                  <button onClick={() => { applyOptimization('rent'); setConfirmAction(null); }} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">Set PGI?</button>
                                  <button onClick={() => setConfirmAction(null)} className="text-xs text-gray-400 hover:text-gray-600 px-1">&times;</button>
                                </span>
                              ) : (
                                <button onClick={() => setConfirmAction({ lever: 'rent', label: 'rent' })} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded hover:bg-primary-200 font-medium">Apply</button>
                              )}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td className="px-4 py-3 font-medium">CapEx Ceiling</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(vaTotalCost)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-primary-600">{optimization.capexCeiling > 0 ? formatCurrency(optimization.capexCeiling) : <span className="text-red-500">$0</span>}</td>
                          <td className={`px-4 py-3 text-right font-medium ${optimization.capexCeiling === 0 ? 'text-red-500' : optimization.capexCeiling >= vaTotalCost ? 'text-green-600' : 'text-red-600'}`}>
                            {optimization.capexCeiling === 0 ? 'No room for CapEx' : `${optimization.capexCeiling >= vaTotalCost ? '+' : ''}${formatCurrency(optimization.capexCeiling - vaTotalCost)}`}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {optimization.capexCeiling > 0 ? (
                              confirmAction?.lever === 'capex' ? (
                                <span className="inline-flex items-center gap-1">
                                  <button onClick={() => { applyOptimization('capex'); setConfirmAction(null); }} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">Scale VA costs?</button>
                                  <button onClick={() => setConfirmAction(null)} className="text-xs text-gray-400 hover:text-gray-600 px-1">&times;</button>
                                </span>
                              ) : (
                                <button onClick={() => setConfirmAction({ lever: 'capex', label: 'capex' })} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded hover:bg-primary-200 font-medium">Apply</button>
                              )
                            ) : <span className="text-xs text-gray-400">—</span>}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 font-medium">Target Exit Cap</td>
                          <td className="px-4 py-3 text-right">{formatPercent(exitCapRate)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-primary-600">{optimization.targetExitCap > 0 ? formatPercent(optimization.targetExitCap) : <span className="text-red-500">N/A</span>}</td>
                          <td className={`px-4 py-3 text-right font-medium ${optimization.targetExitCap === 0 ? 'text-red-500' : optimization.targetExitCap >= exitCapRate ? 'text-green-600' : 'text-red-600'}`}>
                            {optimization.targetExitCap === 0 ? 'Not achievable' : `${(optimization.targetExitCap - exitCapRate) >= 0 ? '+' : ''}${(optimization.targetExitCap - exitCapRate).toFixed(2)}%`}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {optimization.targetExitCap > 0 ? (
                              confirmAction?.lever === 'exitcap' ? (
                                <span className="inline-flex items-center gap-1">
                                  <button onClick={() => { applyOptimization('exitcap'); setConfirmAction(null); }} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">Set to {formatPercent(optimization.targetExitCap)}?</button>
                                  <button onClick={() => setConfirmAction(null)} className="text-xs text-gray-400 hover:text-gray-600 px-1">&times;</button>
                                </span>
                              ) : (
                                <button onClick={() => setConfirmAction({ lever: 'exitcap', label: 'exitcap' })} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded hover:bg-primary-200 font-medium">Apply</button>
                              )
                            ) : <span className="text-xs text-gray-400">—</span>}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-6 text-center border">
                  <p className="text-gray-500">Enter purchase price and income data to see optimization results</p>
                </div>
              )}

              {/* Interpretation */}
              {optimization && (
                <div className="bg-gray-50 rounded-xl p-5 border">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Interpretation</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {optimization.maxPurchasePrice === 0 && (
                      <li className="text-red-600">Target IRR of {formatPercent(thresholds.irr)} is <strong>not achievable</strong> through price reduction alone at current income levels. Significant income growth or value-add is required.</li>
                    )}
                    {optimization.maxPurchasePrice > 0 && optimization.maxPurchasePrice > purchasePrice && (
                      <li>You can pay up to <strong>{formatCurrency(optimization.maxPurchasePrice)}</strong> and still hit your target IRR — {formatCurrency(optimization.maxPurchasePrice - purchasePrice)} of headroom.</li>
                    )}
                    {optimization.maxPurchasePrice > 0 && optimization.maxPurchasePrice < purchasePrice && (
                      <li>The asking price is <strong>{formatCurrency(purchasePrice - optimization.maxPurchasePrice)}</strong> too high. You need to negotiate down to {formatCurrency(optimization.maxPurchasePrice)} to hit target IRR.</li>
                    )}
                    {optimization.requiredNOI > 0 && optimization.requiredNOI > noi && (
                      <li>NOI needs to increase by <strong>{formatCurrency(optimization.requiredNOI - noi)}</strong> ({formatPercent((optimization.requiredNOI - noi) / noi * 100)}) through rent increases or expense cuts.</li>
                    )}
                    {optimization.requiredNOI === 0 && (
                      <li className="text-red-600">Target IRR is <strong>not achievable</strong> through income growth alone at this purchase price and debt structure.</li>
                    )}
                    {optimization.capexCeiling > 0 && (
                      <li>Max CapEx budget before IRR drops below target: <strong>{formatCurrency(optimization.capexCeiling)}</strong></li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'advisor' && (
            <DealAdvisorChat
              propertyId={property.id}
              dealContext={{
                purchase_price: purchasePrice,
                potential_gross_income: pgi,
                vacancy_rate: n(data.vacancy_rate),
                effective_gross_income: egi,
                total_expenses: totalExpenses,
                noi,
                cap_rate: capRate,
                ltv_percent: n(data.ltv_percent),
                interest_rate: n(data.interest_rate),
                amortization_years: n(data.amortization_years),
                annual_debt_service: annualDS,
                dscr,
                before_tax_cash_flow: beforeTaxCF,
                irr: irrValue,
                avg_cash_on_cash: avgCashOnCash,
                equity_multiple: equityMultiple,
                investment_strategy: strategy,
                exit_cap_rate: exitCapRate,
                holding_period: holdingPeriod,
                income_growth_rate: n(data.income_growth_rate),
                expense_growth_rate: n(data.expense_growth_rate),
                closing_costs_percent: n(data.closing_costs_percent),
                selling_costs_percent: sellingCostsPercent,
                management_fee_percent: n(data.management_fee_percent),
                property_taxes: n(data.property_taxes),
                insurance: n(data.insurance),
                utilities: n(data.utilities),
                repairs_maintenance: n(data.repairs_maintenance),
                reserves_capex: n(data.reserves_capex),
                other_expenses: n(data.other_expenses),
                price_per_sqft: pricePerSqft,
                total_cash_required: totalCashRequired,
                va_total_cost: vaTotalCost,
              }}
              onApplySuggestion={(field, value) => handleChange(field, value)}
              thresholds={thresholds}
              strategy={strategy}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}
